import React, { useState, useEffect, useRef, useCallback, startTransition, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { TabContext } from '../components/TabbedLayout';
import {
  FiUser, FiX, FiChevronDown, FiChevronUp, FiPrinter, FiClock, FiShare2, FiCheck
} from "react-icons/fi";
import { TbCurrencyRupee } from "react-icons/tb";
import { MdDelete } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { GET_PATIENTS, BASE_URL, GET_OP_DETAILS, GET_SALES_LOAD_DATA, GET_INVOICE_ITEMS, POST_SALES_INVOICE, POST_PAYMENT, GET_INVOICE_DETAILS, GET_SALES_REPORT, VALIDATE_CARD, POST_SHORT_BOOKS_ITEMS, GET_EMR_DATA, GET_ITEM_MASTER, PUT_ITEM_MASTER, GET_PENDING_BILLS, applyPersistedBaseUrl } from '../utlilities/config';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';
import axios from 'axios';
import NormalBillPrint from '../components/Print';
import KotBillPrint from '../components/KotBill';
import AddDoctorModal from '../components/AddDoctorModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { webPrintA5Receipt } from '../utlilities/webPrint';
import { formatBillDateTime, getBillCopies, invokeBillPrint } from '../utlilities/billPrint';


// Helper for Number to Words (Indian Format)
const numToWords = (n) => {
  if (n === 0) return "Zero Rupees Only";

  const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (num) => {
    if (num < 20) return units[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "");
    if (num < 1000) return units[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " " + convert(num % 100) : "");
    if (num < 100000) return convert(Math.floor(num / 1000)) + " Thousand" + (num % 1000 ? " " + convert(num % 1000) : "");
    if (num < 10000000) return convert(Math.floor(num / 100000)) + " Lakh" + (num % 100000 ? " " + convert(num % 100000) : "");
    return convert(Math.floor(num / 10000000)) + " Crore" + (num % 10000000 ? " " + convert(num % 10000000) : "");
  };

  const integerPart = Math.floor(n);
  const words = convert(integerPart);
  return words + " Rupees Only";
};

const toSafeNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPrintInvoiceNumber = (invoiceNo) => {
  const rawValue = invoiceNo ?? '';
  const rawString = String(rawValue).trim();

  if (!rawString) return '';
  if (rawString.includes('/')) return rawString;

  const parsedInvoice = Number.parseInt(rawString, 10);
  const invoicePart = Number.isNaN(parsedInvoice)
    ? rawString
    : String(parsedInvoice).padStart(2, '0');

  const seriesName = String(localStorage.getItem('series_name') || '').trim();
  return seriesName ? `${invoicePart}/${seriesName}` : invoicePart;
};

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Batch Selection Modal Component
const BatchSelectionModal = ({ isOpen, onClose, medicine, batches, onSelectBatch, generalsettings, onReopen }) => {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const modalRef = useRef(null);
  const selectButtonRef = useRef(null);

  // Check if batch is expired
  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    } catch {
      return false;
    }
  };

  // Check if batch is expiring soon (within phExpiryDays)
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate || !generalsettings?.phExpiryDays) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);

      // If already expired, return false
      if (expiry < today) return false;

      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      const expiryDays = parseInt(generalsettings.phExpiryDays) || 0;

      return daysUntilExpiry <= expiryDays;
    } catch {
      return false;
    }
  };

  // Format expiry date to MM/YY format
  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    try {
      const date = new Date(expiryDate);
      if (isNaN(date.getTime())) return expiryDate; // Return original if invalid date

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2); // Last 2 digits of year
      return `${month}/${year}`;
    } catch {
      return expiryDate; // Return original if error
    }
  };

  useEffect(() => {
    if (isOpen && batches.length > 0) {
      // Find first non-expired batch
      const firstValidBatch = batches.findIndex(batch => !isExpired(batch.expiryDate));
      if (firstValidBatch !== -1) {
        setSelectedBatch(batches[firstValidBatch]);
        setSelectedIndex(firstValidBatch);
      } else {
        setSelectedBatch(batches[0]);
        setSelectedIndex(0);
      }
      // Focus the modal when it opens (including when reopened)
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (modalRef.current) {
            // Ensure any input fields are blurred first
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
              document.activeElement.blur();
            }
            // Focus the modal
            modalRef.current.focus();
          }
        }, 250);
      });
    }
  }, [isOpen, batches]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      e.preventDefault();
      const newIndex = selectedIndex - 1;
      // Skip expired batches
      let validIndex = newIndex;
      while (validIndex >= 0 && isExpired(batches[validIndex].expiryDate)) {
        validIndex--;
      }
      if (validIndex >= 0) {
        setSelectedIndex(validIndex);
        setSelectedBatch(batches[validIndex]);
      }
    } else if (e.key === 'ArrowDown' && selectedIndex < batches.length - 1) {
      e.preventDefault();
      const newIndex = selectedIndex + 1;
      // Skip expired batches
      let validIndex = newIndex;
      while (validIndex < batches.length && isExpired(batches[validIndex].expiryDate)) {
        validIndex++;
      }
      if (validIndex < batches.length) {
        setSelectedIndex(validIndex);
        setSelectedBatch(batches[validIndex]);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelectBatch();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const handleSelectBatch = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch', { autoClose: 1000 });
      return;
    }

    // Check if expired
    if (isExpired(selectedBatch.expiryDate)) {
      toast.error('Cannot select expired batch', { autoClose: 2000 });
      return;
    }

    // Check if expiring soon and show alert
    if (isExpiringSoon(selectedBatch.expiryDate)) {
      // Close modal first
      onClose();

      // Wait a bit for modal to close, then show alert
      await new Promise(resolve => setTimeout(resolve, 300));

      const expiryDays = parseInt(generalsettings?.phExpiryDays) || 0;
      const formattedExpiry = formatExpiryDate(selectedBatch.expiryDate);
      const result = await Swal.fire({
        title: 'Expiry Warning',
        html: `This batch is expiring soon.<br/>Expiry Date: <strong>${formattedExpiry}</strong><br/><br/>Do you want to continue?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
          cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
          popup: 'rounded-xl shadow-xl'
        }
      });

      if (!result.isConfirmed) {
        // User cancelled - reopen the modal
        if (onReopen) {
          onReopen();
        }
        return;
      }

      // User confirmed - proceed with selection
      // Wait a bit for alert to close, then select batch and focus will be handled
      await new Promise(resolve => setTimeout(resolve, 100));
      onSelectBatch(selectedBatch);
      return;
    }

    // Not expiring soon - proceed normally
    onSelectBatch(selectedBatch);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] max-w-3xl max-h-[80vh] overflow-hidden outline-none flex flex-col"
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Select Batch</h3>
            <p className="text-sm text-indigo-100 mt-1">{medicine?.label || medicine?.name || 'Medicine'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-indigo-700 rounded transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Batch Table */}
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b">Batch No</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b">Expiry</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase border-b">MRP</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase border-b">Available Qty</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch, index) => {
                const isSelected = selectedIndex === index;
                const expired = isExpired(batch.expiryDate);
                const expiringSoon = isExpiringSoon(batch.expiryDate);

                return (
                  <tr
                    key={index}
                    className={`transition-colors ${expired
                      ? 'bg-red-50 border-l-4 border-red-500 cursor-not-allowed opacity-60'
                      : expiringSoon
                        ? isSelected
                          ? 'bg-amber-50 border-l-4 border-amber-500 cursor-pointer'
                          : 'bg-amber-50/30 border-l-4 border-amber-300 cursor-pointer hover:bg-amber-50'
                        : isSelected
                          ? 'bg-indigo-50 border-l-4 border-indigo-500 cursor-pointer'
                          : 'hover:bg-gray-50 border-l-4 border-transparent cursor-pointer'
                      }`}
                    onClick={() => {
                      if (!expired) {
                        setSelectedIndex(index);
                        setSelectedBatch(batch);
                      }
                    }}
                  >
                    <td className="px-4 py-2 text-sm font-medium border-b">
                      <span className={expired ? 'text-red-700 line-through' : 'text-gray-900'}>
                        {batch.batchNo || 'N/A'}
                      </span>
                      {expired && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">(EXPIRED)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm border-b">
                      <span className={
                        expired
                          ? 'text-red-700 font-semibold'
                          : expiringSoon
                            ? 'text-amber-700 font-semibold'
                            : 'text-gray-700'
                      }>
                        {formatExpiryDate(batch.expiryDate)}
                      </span>
                      {expiringSoon && !expired && (
                        <span className="ml-2 text-xs text-amber-600 font-semibold">(Near Expiry)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right border-b">
                      <span className={expired ? 'text-red-700' : 'text-gray-900'}>
                        ₹{parseFloat(batch.mrp || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right border-b">
                      <span className={`px-2 py-1 rounded ${parseFloat(batch.availableQty || 0) === 0
                        ? 'bg-red-100 text-red-800'
                        : parseFloat(batch.availableQty || 0) < 10
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-green-100 text-green-800'
                        }`}>
                        {parseFloat(batch.availableQty || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Selected:</span>{' '}
            <span className="font-semibold text-gray-900">
              {selectedBatch?.batchNo || 'None'}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              className="px-5 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              ref={selectButtonRef}
              className={`px-5 py-2 font-medium rounded-lg transition-colors ${selectedBatch && isExpired(selectedBatch.expiryDate)
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              onClick={handleSelectBatch}
              disabled={!selectedBatch || (selectedBatch && isExpired(selectedBatch.expiryDate))}
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const ShareQuantityModal = ({ isOpen, onClose, product, qty, setQty, onSubmit }) => {
  const modalRef = useRef(null);
  const qtyInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && qtyInputRef.current) {
      // Focus the quantity input when modal opens
      setTimeout(() => {
        qtyInputRef.current?.focus();
        qtyInputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling
      onSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] max-w-md outline-none flex flex-col"
        ref={modalRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-3 flex items-center justify-between rounded-t-lg">
          <div>
            <h3 className="text-lg font-bold">Add to Short Books</h3>
            <p className="text-sm text-indigo-100 mt-1">{product?.Item_Name || product?.name || 'Product'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-indigo-700 rounded transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Quantity
            </label>
            <input
              type="text"
              ref={qtyInputRef}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
              placeholder="Enter quantity"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            className="px-5 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmit();
            }}
            type="button"
          >
            Add to Short Books
          </button>
        </div>
      </div>
    </div>
  );
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '34px', // reduce overall control height
    height: '34px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '34px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '34px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
        ? '#92c3fcff'
        : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const PharmacyBilling = () => {
  const navigate = useNavigate();
  const tabContext = useContext(TabContext);
  const activeTabId = tabContext?.activeTabId;
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [billedDate, setBilledDate] = useState('')
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [newinvoiceNumber, setNewInvoiceNumber] = useState('')
  const [mobileInput, setMobileInput] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [header, setHeader] = useState(null);
  const [doctorModal, setDoctorModal] = useState(false);
  const [prevBillDue, setPrevBillDue] = useState(0);
  const [patientPendingDue, setPatientPendingDue] = useState({ totalDue: 0, billCount: 0 });

  // Medicine specific states
  const [medicines, setMedicines] = useState([]);
  // Cache for selected medicines to persist even when search results are cleared
  const medicinesCacheRef = useRef({});

  const [products, setProducts] = useState([]);

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [payingAmount, setPayingAmount] = useState(0);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'upi', 'card'
  const [showBillPaymentModal, setShowBillPaymentModal] = useState(false);
  const [billPaymentSavedContext, setBillPaymentSavedContext] = useState(null);
  const [billPaymentModalAmount, setBillPaymentModalAmount] = useState(0);
  const [billPaymentModalMode, setBillPaymentModalMode] = useState('Cash');
  // Split mode: cash + bank legs of a single receipt transaction.
  const [billPaymentModalSplit, setBillPaymentModalSplit] = useState({ cash: 0, bank: 0 });
  const [roundOff, setRoundOff] = useState(false);
  const [halfQtyUsed, setHalfQtyUsed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [searchOpnum, setSearchOpnum] = useState('');
  const [edit, setEdit] = useState('');
  const [globalDiscount, setGlobalDiscount] = useState('');
  const [privilegeCardNumber, setPrivilegeCardNumber] = useState('');

  // Batch selection modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedMedicineForBatch, setSelectedMedicineForBatch] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareProductIndex, setShareProductIndex] = useState(null);
  const [shareQty, setShareQty] = useState('');

  // Medicine search input state for new entry row
  const [medicineSearch, setMedicineSearch] = useState('');
  const [showMedicineList, setShowMedicineList] = useState(false);
  const medicineSearchInputRef = useRef(null);
  const medicineSearchContainerRef = useRef(null);
  const medicineDropdownRef = useRef(null);
  const medicineRowRefs = useRef([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [selectedMedicineIndex, setSelectedMedicineIndex] = useState(-1); // -1 means no item focused initially

  // State for editing medicine in existing rows
  const [editingMedicineRowIndex, setEditingMedicineRowIndex] = useState(null);
  const [medicineSearchForEdit, setMedicineSearchForEdit] = useState('');
  const medicineNameCellRefs = useRef([]);
  const medicineSearchDebounceRef = useRef(null);
  const isSelectingMedicineRef = useRef(false); // Track if we're in the process of selecting a medicine
  const [prevBillAmount, setPrevBillAmount] = useState(0);

  // Helper to calculate dropdown position
  const calculateDropdownPosition = (element) => {
    if (!element) return { top: 0, left: 0, width: 0 };
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const estimatedHeight = 350; // Dropdown max height roughly

    // Check if space below is insufficient AND there is more space above
    if (spaceBelow < estimatedHeight && rect.top > spaceBelow) {
      return {
        bottom: viewportHeight - rect.top + 4, // Position above
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 600),
        isAbove: true
      };
    } else {
      return {
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 600),
        isAbove: false
      };
    }
  };

  // Patient info sidebar state
  const [showPatientSidebar, setShowPatientSidebar] = useState(false);
  const [patientHistory, setPatientHistory] = useState([]);
  const [expandedBillId, setExpandedBillId] = useState(null);
  const [billItems, setBillItems] = useState({}); // Store items for each bill: { invoiceNo: items[] }
  const [billCreatedDates, setBillCreatedDates] = useState({}); // { invoiceNo: CreatedDate }
  const [loadingBillItems, setLoadingBillItems] = useState({}); // Track loading state for each bill: { invoiceNo: boolean }
  const [loadingPatientBillLoad, setLoadingPatientBillLoad] = useState(null); // InvoiceNo while loading items into billing
  const [showEmrHistoryModal, setShowEmrHistoryModal] = useState(false);
  const [emrHistoryList, setEmrHistoryList] = useState([]);

  // Refs
  const mobileInputRef = useRef(null);
  const numberRef = useRef(null);
  const nameRef = useRef(null);
  const doctorSelectRef = useRef(null);
  const productRefs = useRef([]);
  const qtyRefs = useRef([]); // Add quantity input refs
  const rateRefs = useRef([]); // Add rate input refs
  const discountRefs = useRef([]); // Add discount input refs
  const opnumRef = useRef(null);
  const loadedOpnumRef = useRef(''); // Last OP number whose details were successfully loaded
  const submitbtnRef = useRef(null);
  const privilegeCardRef = useRef(null);
  const isSavingRef = useRef(false); // Track if save is in progress to prevent double submission
  const isButtonDisabledRef = useRef(false); // Track button disabled state for keyboard shortcut
  const showBillPaymentModalRef = useRef(false); // Blocks Alt+S while the mark-payment modal is up
  const handleSaveRef = useRef(null); // Store latest handleSave function
  const isSubmittingShareRef = useRef(false); // Prevent double submission for share functionality

  const [editNumber, setEditNumber] = useState('');
  const [editName, seteditName] = useState('');
  const [editAge, setEditAge] = useState('');
  const [editGender, setEditGender] = useState('');
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const hasUnsavedChangesRef = useRef(false); // Ref for synchronous access
  const initialBillStateRef = useRef(null); // Store initial state when bill is loaded for editing

  const [permissions, setPermissions] = useState({});

  const [sysInfo, setSysInfo] = useState({});
  const [clientInfo, setClientInfo] = useState({});
  const [userInfo, setUserInfo] = useState({})

  const [generalsettings, setGeneralSettings] = useState({});

  // Print component state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const normalBillPrintRef = useRef(null);


  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const handleLogout = useCallback(async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    if (isElectron()) {
      const loginModeStored = await ipcRenderer.invoke('store-get', 'login_mode');
      await ipcRenderer.invoke('store-set', 'clientInfo', {});
      await ipcRenderer.invoke('store-set', 'sysInfo', {});
      await ipcRenderer.invoke('store-set', 'userInfo', {});
      await ipcRenderer.invoke('store-set', 'generalsettings', {});
      await ipcRenderer.invoke('store-set', 'user_role', {});
      if (loginModeStored === 'cloud') {
        const origin = await ipcRenderer.invoke('store-get', 'api_base_url');
        if (origin && typeof origin === 'string') {
          await ipcRenderer.invoke('set-api-base-url', origin);
        }
      }
    }

    await applyPersistedBaseUrl();
    navigate('/');
  }, [navigate]);

  // Check subscription expiry on component mount
  // useEffect(() => {
  //   const checkSubscriptionExpiry = async () => {
  //     try {
  //       const response = await axios.get(`${BASE_URL}${GET_SUBSCRIPTION_EXPIRY_DATE}`, {
  //         headers: {
  //           'XApiKey': process.env.REACT_APP_API_KEY,
  //           'Authorization': `Bearer ${token}`
  //         }
  //       });

  //       if (response.status === 200 && response.data?.isSucess) {
  //         const expiryDateStr = response.data?.data?.subscriptionExpiryDate;

  //         if (expiryDateStr) {
  //           const expiryDate = new Date(expiryDateStr);
  //           const today = new Date();
  //           // Set time to midnight for accurate date comparison
  //           today.setHours(0, 0, 0, 0);
  //           expiryDate.setHours(0, 0, 0, 0);

  //           if (today > expiryDate) {
  //             Swal.fire({
  //               icon: 'warning',
  //               title: 'Trial Expired',
  //               text: 'Your key has expired. Please contact admin.',
  //               confirmButtonText: 'OK',
  //               allowOutsideClick: false,
  //               allowEscapeKey: false,
  //             }).then((result) => {
  //               if (result.isConfirmed) {
  //                 handleLogout();
  //               }
  //             });

  //             return; // Skip further execution if expired
  //           }

  //           const MS_PER_DAY = 24 * 60 * 60 * 1000;
  //           const daysLeft = Math.floor((expiryDate.getTime() - today.getTime()) / MS_PER_DAY);

  //           if (daysLeft >= 0 && daysLeft <= 5) {
  //             Swal.fire({
  //               icon: 'info',
  //               title: 'Subscription Expiry Alert',
  //               text: daysLeft === 0
  //                 ? 'Your subscription expires today.'
  //                 : `Your subscription expires in ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`,
  //               confirmButtonText: 'OK',
  //             });
  //           }
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error checking subscription expiry:', error);
  //       // Don't block the app if the API call fails
  //     }
  //   };

  //   checkSubscriptionExpiry();
  // }, [token, handleLogout]);

  useEffect(() => {
    const fetchData = async () => {
      let sysinfo;
      let generalSetting;
      let client_info;
      let userinfo;
      let userRolesData;


      if (isElectron()) {
        userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
        client_info = await ipcRenderer.invoke('store-get', 'clientInfo');
        userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      } else {
        // Cloud / browser login: session is persisted via setWebUserInfo in login.js
        initializeWebUserInfo();
        const webInfo = getWebUserInfo();
        userinfo = webInfo.userInfo;
        sysinfo = webInfo.sysInfo;
        client_info = webInfo.clientInfo;
        userRolesData = webInfo.user_role;
        generalSetting =
          webInfo.generalOpSettings ||
          webInfo.generalopsettings ||
          webInfo.generalSettings;
      }

      // Parse DeviceConfig from sysInfo if it exists
      let parsedDeviceConfig = {};
      const deviceConfigValue = sysinfo?.DeviceConfig || sysinfo?.deviceConfig || '';
      
      if (deviceConfigValue) {
        try {
          parsedDeviceConfig = typeof deviceConfigValue === 'string' 
            ? JSON.parse(deviceConfigValue) 
            : deviceConfigValue;
        } catch (e) {
          console.error('Error parsing DeviceConfig:', e);
          parsedDeviceConfig = {};
        }
      }

      // Add parsed values to sysInfo for easy access
      if (sysinfo) {
        sysinfo.phbilltype = parsedDeviceConfig.phbilltype || '';
        sysinfo.phbilltemplate = parsedDeviceConfig.phbilltemplate || '';
      }

      console.log(generalSetting);

      setSysInfo(sysinfo);
      setGeneralSettings(generalSetting);
      setClientInfo(client_info);
      setUserInfo(userinfo);
      setPermissions(processPermissions(userRolesData || []));

      if (generalSetting?.phAutoBillPrint) {
        setPrintAfterSave(true)
      } else {
        setPrintAfterSave(false)
      }
      //setPrintAfterSave
    };

    fetchData();
  }, []);

  useEffect(() => {
    getSalesLoadData();
  }, []);

  const processPermissions = (roles) => {
    const permissionsMap = {};
    roles.forEach((role) => {
      role.modules.forEach((module) => {
        permissionsMap[module.moduleName] = module.permissions;
      });
    });
    return permissionsMap;
  };

  const hasPermission = (moduleName, action) => {
    return permissions[moduleName]?.[action] || false;
  };

  // Focus opnum field on mount and when tab becomes active
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      opnumRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTabId]); // Re-run when activeTabId changes (tab switch)

  // Check for invoice number in localStorage to load from report
  useEffect(() => {
    const editInvoiceNo = localStorage.getItem('editInvoiceNo');
    if (editInvoiceNo) {
      setLoading(true);
      // Clear it from localStorage
      localStorage.removeItem('editInvoiceNo');
      localStorage.removeItem('editInvoiceSequence');
      // Load the invoice
      setTimeout(() => {
        getItemDatasparms(parseInt(editInvoiceNo));
      }, 1000);
    }
  }, []);

  const getSalesLoadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SALES_LOAD_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });



      if (response.status === 200) {
        const data = response.data.data;

        // Set invoice number from nextNum
        if (data.nextNum) {
          setInvoiceNumber(data.nextNum);
          setNewInvoiceNumber(data.nextNum);
        }


        const response1 = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
          },
          params: {
            Sequence: localStorage.getItem('finac_year'),
            InvoiceNo: (data.nextNum - 1).toString(),
          }
        });

        const prev_total_amount = response1.data.data.header.TotalAmount;
        setPrevBillAmount(prev_total_amount);

        // Set doctors from response
        if (data.doctors && Array.isArray(data.doctors)) {
          setDoctors(data.doctors);
        }
        setTimeout(() => {
          setLoading(false);
        }, 500);

      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error('Failed to fetch sales load data.', {
        autoClose: 1000,
      });
    }
  };


  const getInvoiceItems = async (filterText) => {
    if (!filterText || filterText.length < 1) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_ITEMS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          FilterText: filterText,
          ItemType: 'Pharmacy',
          FltrType: generalsettings.PhMedicineFilterType || 'all',
          DepartmentID: localStorage.getItem('department_id') || ''
        }
      });

      if (response.status === 200) {
        setLoading(false);
        const items = response.data.data || [];
        // Transform API response to medicine format and filter items
        const transformedMedicines = items
          .map((item) => {
            // Check if item has batches
            const itemBatches = item.itemBatch || [];
            if (!itemBatches || itemBatches.length === 0) {
              return null; // Skip items without batches
            }

            // Transform batches
            const batches = itemBatches.map((batch) => ({
              batchNo: batch.batchCode || '',
              expiryDate: batch.expiryDate || '',
              mrp: parseFloat(batch.salesPrice || 0), // Use salesPrice from batch as mrp
              rate: parseFloat(batch.salesPrice || 0), // Use salesPrice from batch as rate
              gstRate: parseFloat(item.taxCode || 0), // taxCode as GST rate
              availableQty: parseFloat(batch.onhand || 0),
              purchasePrice: parseFloat(batch.purchasePrice || 0),
              // Calculate GST amounts if needed
              sgst: 0, // Will be calculated when needed
              cgst: 0, // Will be calculated when needed
              totalWithGst: 0 // Will be calculated when needed
            }));

            // Check if at least one batch has stock (availableQty > 0)
            const hasStock = batches.some(batch => parseFloat(batch.availableQty || 0) > 0);
            if (!hasStock) {
              return null; // Skip items without any stock
            }

            return {
              id: item.item_No,
              name: item.item_Name || '',
              genericName: item.chemical || '',
              manufacturer: item.company || '',
              batches: batches,
              section: item.section || '',
              hsnCode: item.hsnCode || '',
              discount1: item.discount1 || 0,
              discount2: item.discount2 || 0,
              discount3: item.discount3 || 0,
              itemStock: item.itemStock || 0,
              shelf: item.shelf || ''
            };
          })
          .filter(med => med !== null); // Remove null items (items without batches or stock)

        // Update medicines state with transformed data
        setMedicines(transformedMedicines);
        // Also update cache
        transformedMedicines.forEach(med => {
          medicinesCacheRef.current[med.id] = med;
        });
      }
    } catch (error) {
      toast.error('Failed to fetch invoice items.', {
        autoClose: 1000,
      });
    }
  };

  // Focus medicine search input when products list is empty or after doctor selection
  useEffect(() => {
    if (products.length === 0 && selectedDoctor && medicineSearchInputRef.current) {
      setTimeout(() => {
        medicineSearchInputRef.current?.focus();
      }, 100);
    }
  }, [selectedDoctor, products.length]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!showMedicineList || !medicineSearchContainerRef.current) return;

    const updatePosition = () => {
      if (medicineSearchContainerRef.current) {
        setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showMedicineList]);

  const getPatientData = async (inputValue) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PATIENTS}`, {
        headers: {
          'PageNo': 1,
          'PageSize': 100,
          'FilterText': inputValue,
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setPatients(response.data.data.masterData);
      }
    } catch (error) {
      toast.error('Failed to filter patients.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatBilledDate = (date) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Format expiry date to MM-YY format (e.g., "10-24", "09-28")
  const formatExpiryDateMMYY = (expiryDate) => {
    if (!expiryDate) return '';
    try {
      const date = new Date(expiryDate);
      if (isNaN(date.getTime())) return expiryDate; // Return original if invalid date

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2); // Last 2 digits of year
      return `${month}-${year}`;
    } catch {
      return expiryDate; // Return original if error
    }
  };

  // Match BatchSelectionModal: block expired batches when auto-selecting single batch
  const isBatchExpired = (expiryDate) => {
    if (!expiryDate) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    } catch {
      return false;
    }
  };

  const isBatchExpiringSoonForBilling = (expiryDate) => {
    if (!expiryDate || !generalsettings?.phExpiryDays) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      if (expiry < today) return false;
      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      const expiryDays = parseInt(generalsettings.phExpiryDays) || 0;
      return daysUntilExpiry <= expiryDays;
    } catch {
      return false;
    }
  };

  const formatExpiryDateForWarning = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    try {
      const date = new Date(expiryDate);
      if (isNaN(date.getTime())) return String(expiryDate);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${year}`;
    } catch {
      return String(expiryDate);
    }
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
      // Close sidebar on ESC key
      if (e.key === 'Escape' && showPatientSidebar) {
        setShowPatientSidebar(false);
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [showPatientSidebar]);

  // Close payment options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPaymentOptions && !event.target.closest('.payment-options-container')) {
        setShowPaymentOptions(false);
      }
    };

    if (showPaymentOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPaymentOptions]);

  // Track changes when bill is in edit mode
  useEffect(() => {
    if (editInvoiceNumber && initialBillStateRef.current) {
      checkForChanges();
    }
  }, [products, editName, editNumber, selectedDoctor, payingAmount, globalDiscount, editInvoiceNumber]);

  useEffect(() => {
    const now = new Date();
    const formattedDateTime = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`;
    setCurrentDateTime(formattedDateTime);
  }, []);

  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  useEffect(() => {
    getHeaderData();

    const billigDate = new Date();
    const newdate = formatBilledDate(billigDate);
    setBilledDate(newdate);
  }, []);


  const getHeaderData = async () => {
    setLoading(true);
    try {
      // Invoice number and doctors are now set from getSalesLoadData()
      // Medicines are loaded via API when user searches
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch header data.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMobileChange = async (inputValue) => {
    if (!inputValue || inputValue === null || inputValue === undefined) {
      setMobileInput('');
      setEditNumber('');
      seteditName('');
      return;
    }

    const value = String(inputValue);
    setMobileInput(value);
    const isNumeric = /^\d+$/.test(value);

    if (isNumeric) {
      setEditNumber(value);
      seteditName('');
    } else {
      seteditName(value.toUpperCase());
      setEditNumber('');
    }

    if (value.length === 3 || value.length === 6 || value.length === 9 || value.length === 10) {
      await getPatientData(value);
    }
  };

  useEffect(() => {
    if (selectedPatient) {
      seteditName(selectedPatient.PatientName || '');
      setEditNumber(selectedPatient.MobileNo || '');
      setEditAge(selectedPatient.Age || '');
      setEditGender(selectedPatient.Gender || '');
      // Fetch patient history when patient is selected
      fetchPatientHistory(selectedPatient.PatientID);
      fetchPatientPendingDue(selectedPatient.PatientID);
    } else {
      setPatientHistory([]);
      setPatientPendingDue({ totalDue: 0, billCount: 0 });
    }
  }, [selectedPatient]);

  const fetchPatientPendingDue = async (patientId) => {
    if (!patientId || parseInt(patientId, 10) <= 0) {
      setPatientPendingDue({ totalDue: 0, billCount: 0 });
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${BASE_URL}${GET_PENDING_BILLS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'EntryDate': today,
          'opNumber': '',
        },
      });

      if (response.status !== 200) {
        setPatientPendingDue({ totalDue: 0, billCount: 0 });
        return;
      }

      const data = response.data?.data || [];
      const billIdSet = new Set();
      let totalDue = 0;
      let billCount = 0;

      data.forEach((departmentData) => {
        const departmentName = (departmentData.department || '').trim();
        const isPharmacyDepartment = departmentName &&
          (departmentName.toLowerCase().includes('pharmacy') ||
            departmentName.toLowerCase() === 'pharmacy');

        if (!isPharmacyDepartment) return;

        (departmentData.bills || []).forEach((bill) => {
          if (parseInt(bill.patientID, 10) !== parseInt(patientId, 10)) return;

          const uniqueId = `${bill.invoiceNo}-${bill.transType}-${bill.sequence || 0}`;
          if (billIdSet.has(uniqueId)) return;
          billIdSet.add(uniqueId);

          totalDue += parseFloat(bill.balanceDue || 0);
          billCount += 1;
        });
      });

      setPatientPendingDue({ totalDue, billCount });
    } catch (error) {
      console.error('Error fetching patient pending bills:', error);
      setPatientPendingDue({ totalDue: 0, billCount: 0 });
    }
  };

  // Fetch patient history (last visits, recent bills, pending bills)
  const fetchPatientHistory = async (patientId) => {
    setLoading(true);
    try {
      // Convert date strings to Date objects and format as YYYY-MM-DD
      const fromDate = new Date('2020-01-01');
      const toDate = new Date();

      // Format date as YYYY-MM-DD
      const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          'rpType': 'Patient',
          'fromDate': formatDate(fromDate),
          'toDate': formatDate(toDate),
          'searchKey': patientId,
          'patientId': 0,
          'itemNo': 0,
        },
      });
      if (response.status === 200) {
        setLoading(false);
        const data = (response.data.data) || [];
        setPatientHistory(data);
      } else {
        setLoading(false);
        toast.error('Failed to fetch sales report', { autoClose: 1000 });
      }
    } catch (error) {
      setLoading(false);
      toast.error('Failed to fetch sales report', { autoClose: 1000 });
    }
  };

  // Fetch bill items for display in accordion
  const fetchBillItems = async (invoiceNo, sequence) => {
    // If items already loaded, don't fetch again
    if (billItems[invoiceNo]) {
      return;
    }

    setLoadingBillItems(prev => ({ ...prev, [invoiceNo]: true }));
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          Sequence: sequence || localStorage.getItem('finac_year'),
          InvoiceNo: invoiceNo.toString(),
        }
      });

      if (response.status === 200) {
        const invoiceData = response.data.data;
        if (invoiceData && invoiceData.details && invoiceData.itemList) {
          setPrevBillDue(invoiceData.header.BalanceDue || 0);
          const { details, itemList } = invoiceData;

          // Create a map of itemList for quick lookup
          const itemListMap = {};
          if (itemList && Array.isArray(itemList)) {
            itemList.forEach(item => {
              itemListMap[item.Item_No] = item;
            });
          }

          // Transform details into display format
          const transformedItems = details.map((detail) => {
            const itemInfo = itemListMap[detail.ItemNo];

            return {
              SI_No: detail.SI_No || 0,
              ItemNo: detail.ItemNo,
              ItemName: detail.ItemName || (itemInfo ? itemInfo.Item_Name : ''),
              BatchCode: detail.BatchCode || '',
              Qty: parseFloat(detail.Qty || 0),
              SalesPrice: parseFloat(detail.SalesPrice || 0),
              MRP: parseFloat(detail.MRP || 0),
              Disc_Percentage: parseFloat(detail.Disc_Percentage || 0),
              Disc_Amount: parseFloat(detail.Disc_Amount || 0),
              TaxAmount: parseFloat(detail.TaxAmount || 0),
              NetTotal: parseFloat(detail.PostTaxTotal || 0),
              TaxCode: detail.TaxCode || 0,
              Shelf: itemInfo?.Shelf || itemInfo?.shelf || '',
              ExpiryDate: itemInfo?.ItemBatch ? (() => {
                try {
                  const batches = typeof itemInfo.ItemBatch === 'string'
                    ? JSON.parse(itemInfo.ItemBatch)
                    : itemInfo.ItemBatch;
                  const batch = batches.find(b => b.BatchCode === detail.BatchCode);
                  return batch?.ExpiryDate || '';
                } catch {
                  return '';
                }
              })() : ''
            };
          });

          setBillItems(prev => ({ ...prev, [invoiceNo]: transformedItems }));
          // The sales-report list only carries a date-only EntryDate; keep the
          // detail header's CreatedDate so the reprint can show date + time.
          setBillCreatedDates(prev => ({
            ...prev,
            [invoiceNo]: invoiceData.header?.CreatedDate || ''
          }));
        }
      }
    } catch (error) {
      toast.error('Failed to load bill items', { autoClose: 1000 });
    } finally {
      setLoadingBillItems(prev => ({ ...prev, [invoiceNo]: false }));
    }
  };

  // Handle bill accordion toggle
  const handleBillToggle = (invoiceNo, sequence) => {
    if (expandedBillId === invoiceNo) {
      // Collapse
      setExpandedBillId(null);
    } else {
      // Expand - fetch items if not already loaded
      setExpandedBillId(invoiceNo);
      if (!billItems[invoiceNo]) {
        fetchBillItems(invoiceNo, sequence);
      }
    }
  };

  // Handle print bill from patient history
  const handlePrintBillFromHistory = async (bill, items) => {
    if(userInfo?.Designation === 'Admin'){
      const numberToWords = (num) => {
        if (num === 0) return "Zero Rupees Only";

        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
          'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
          'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const convertHundreds = (n) => {
          if (n === 0) return '';
          let result = '';
          if (n >= 100) {
            result += ones[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
            if (n > 0) result += 'and ';
          }
          if (n >= 20) {
            result += tens[Math.floor(n / 10)] + ' ';
            n %= 10;
          }
          if (n > 0) {
            result += ones[n] + ' ';
          }
          return result.trim();
        };

        const numStr = parseFloat(num).toFixed(2);
        const parts = numStr.split('.');
        const rupees = parseInt(parts[0]);
        const paise = parseInt(parts[1] || 0);

        let words = '';

        if (rupees === 0) {
          words = 'Zero';
        } else if (rupees < 100) {
          // 1-99: Direct conversion
          words = convertHundreds(rupees);
        } else if (rupees < 1000) {
          // 100-999: Hundreds
          words = convertHundreds(rupees);
        } else if (rupees < 100000) {
          // 1000-99999: Thousands and below
          const thousands = Math.floor(rupees / 1000);
          const remainder = rupees % 1000;

          if (thousands > 0) {
            words = convertHundreds(thousands) + ' Thousand';
            if (remainder > 0) {
              words += ' ' + convertHundreds(remainder);
            }
          } else {
            words = convertHundreds(remainder);
          }
        } else if (rupees < 10000000) {
          // 100000-9999999: Lakhs, Thousands, and below
          const lakhs = Math.floor(rupees / 100000);
          const remainder = rupees % 100000;

          words = convertHundreds(lakhs) + ' Lakh';

          if (remainder > 0) {
            if (remainder >= 1000) {
              const thousands = Math.floor(remainder / 1000);
              const remainder2 = remainder % 1000;
              words += ' ' + convertHundreds(thousands) + ' Thousand';
              if (remainder2 > 0) {
                words += ' ' + convertHundreds(remainder2);
              }
            } else {
              words += ' ' + convertHundreds(remainder);
            }
          }
        } else {
          // 10000000+: Crores, Lakhs, Thousands, and below
          const crores = Math.floor(rupees / 10000000);
          const remainder = rupees % 10000000;

          words = convertHundreds(crores) + ' Crore';

          if (remainder > 0) {
            if (remainder >= 100000) {
              const lakhs = Math.floor(remainder / 100000);
              const remainder2 = remainder % 100000;
              words += ' ' + convertHundreds(lakhs) + ' Lakh';

              if (remainder2 > 0) {
                if (remainder2 >= 1000) {
                  const thousands = Math.floor(remainder2 / 1000);
                  const remainder3 = remainder2 % 1000;
                  words += ' ' + convertHundreds(thousands) + ' Thousand';
                  if (remainder3 > 0) {
                    words += ' ' + convertHundreds(remainder3);
                  }
                } else {
                  words += ' ' + convertHundreds(remainder2);
                }
              }
            } else if (remainder >= 1000) {
              const thousands = Math.floor(remainder / 1000);
              const remainder2 = remainder % 1000;
              words += ' ' + convertHundreds(thousands) + ' Thousand';
              if (remainder2 > 0) {
                words += ' ' + convertHundreds(remainder2);
              }
            } else {
              words += ' ' + convertHundreds(remainder);
            }
          }
        }

        words = words.trim() + ' Rupees';

        if (paise > 0) {
          words += ' and ' + convertHundreds(paise).trim() + ' Paise';
        }

        return words + ' Only';
      };

      const data = {
        settings: {
          cpi: 12,         // Condensed printing
          width: generalsettings?.phPrinterWidth || 80,       // Full width in condensed mode
          indent: 0,
          printerType: generalsettings?.phPrinterType || "TVS",// "EPSON" or "TVS"

          feedInLines: generalsettings?.phFeedinLines || 0,
          feedInForward: generalsettings?.phFeedInForward || 0,
          feedOutLines: generalsettings?.phFeedOutLines || 0,
          feedOutForward: generalsettings?.phFeedOutForward || 0,
          returnValidityDays: generalsettings?.phReturnValidityDays || 0,
          is_gstbill : generalsettings?.phIsGstBill || "No",
          show_itemwise_discount: generalsettings?.ShowItemwiseDiscount || "No",
        },

        header: {
          firmName:
            generalsettings?.phShowFirmname
              ? `${generalsettings?.FirstName || ""} ${generalsettings?.LastName || ""}`.trim()
              : "",

          phones:
            generalsettings?.phShowFirmnumber
              ? [generalsettings?.MobileNo1, generalsettings?.MobileNo2].filter(Boolean)
              : [],

          username:
            generalsettings?.phShowUsername && bill.UserName
              ? String(bill.UserName)
              : "",

          address:
            generalsettings?.phShowAddress
              ? [
                // generalsettings?.AddressLine1,
                // generalsettings?.AddressLine2,
                generalsettings?.Place
              ].filter(Boolean)
              : [],

          workingTime:
            generalsettings?.phShowWorkingHourse && generalsettings?.WorkingTime
              ? String(generalsettings.WorkingTime)
              : "",

          // FIXED — guaranteed string
          gst:
            generalsettings?.phShowGst && generalsettings?.GstNo
              ? String(generalsettings.GstNo)
              : "",

          // FIXED — MUST BE STRING
          invoiceNumber: formatPrintInvoiceNumber(bill.InvoiceNo),

          // Prefer the detail header's CreatedDate — it carries the bill time
          date: formatBillDateTime(
            billCreatedDates[bill.InvoiceNo] || bill.CreatedDate || bill.EntryDate
          ),

          patientName: bill?.PatientName || "",

          // FIXED — MUST BE STRING
          age: bill?.Age ? String(bill.Age) : "",

          gender: bill?.Gender || "",
          doctor: bill?.DoctorName || "",
          dlnum1: generalsettings?.dlnum1 || "",
          dlnum2: generalsettings?.dlnum2 || "",
          place: generalsettings?.Place || "",
        },

        products: items.map((p, i) => {
          // Format expiry date to MM/YY format
          let expFormatted = "";
          if (p?.ExpiryDate) {
            const expDate = String(p?.ExpiryDate);
            // If date is in format "YYYY-MM-DD", convert to "MM/YY"
            if (expDate.includes('-')) {
              const parts = expDate.split('-');
              if (parts.length >= 2) {
                // parts[0] = year, parts[1] = month
                const year = parts[0].slice(-2); // Get last 2 digits of year
                expFormatted = `${parts[1]}/${year}`;
              } else {
                expFormatted = expDate;
              }
            } else {
              expFormatted = expDate;
            }
          }

          return {
            sr: ((currentPage - 1) * 6) + (i + 1),
            name: String(p.ItemName || ""),
            batch: p?.BatchCode ? String(p.BatchCode).slice(-6) : "-",
            qty: Number(p.Qty ?? 0),
            rate: Number((p.SalesPrice + (p.Disc_Amount / p.Qty)) ?? 0),
            mrp: Number(p.MRP ?? p?.MRP ?? 0),
            gstRate: Number(p.TaxCode ?? p?.TaxCode ?? 0),
            gstAmount: Number(p.TaxAmount ?? 0),
            discount: Number(p.Disc_Amount ?? 0),
            total: Number((p.NetTotal + p.TaxAmount ?? 0) ?? 0),
            grandTotal: Number(p.TaxAmount + p.NetTotal ?? 0),
            exp: expFormatted
          };
        }),

        totals: {
          totalAmount: Number((bill.NetAmount + bill?.TaxAmount + bill?.DiscountAmount).toFixed(2) ?? 0),
          grandTotal: Number(bill.TotalAmount ?? 0),
          discount: Number(bill.DiscountAmount ?? 0),
          gst: Number(bill.TaxAmount ?? 0),
          paid: Number(bill.TotalAmount - bill.BalanceDue ?? 0),
          balance: Number(bill.BalanceDue ?? 0),
          balanceInWords: numberToWords(bill.BalanceDue ?? 0)
        }
      };

      const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';
      const copies = getBillCopies(generalsettings);

      if (isElectron()) {
        await invokeBillPrint(ipcRenderer, {
          printerName: sysInfo.pharmacyBillPriinter,
          json: data,
          phBillType,
          copies
        });
      } else {
        webPrintA5Receipt(data, { title: 'PHARMACY CASH BILL', copies });
      }
    } else {
      if(generalsettings?.billPrintonlyPaymentsave && prevBillDue > 0){
        toast.error('Please mark the payment before printing.', {
          autoClose: 2000,
        });
        return;
      } else {
  
        const numberToWords = (num) => {
          if (num === 0) return "Zero Rupees Only";
  
          const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
          const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
          const convertHundreds = (n) => {
            if (n === 0) return '';
            let result = '';
            if (n >= 100) {
              result += ones[Math.floor(n / 100)] + ' Hundred ';
              n %= 100;
              if (n > 0) result += 'and ';
            }
            if (n >= 20) {
              result += tens[Math.floor(n / 10)] + ' ';
              n %= 10;
            }
            if (n > 0) {
              result += ones[n] + ' ';
            }
            return result.trim();
          };
  
          const numStr = parseFloat(num).toFixed(2);
          const parts = numStr.split('.');
          const rupees = parseInt(parts[0]);
          const paise = parseInt(parts[1] || 0);
  
          let words = '';
  
          if (rupees === 0) {
            words = 'Zero';
          } else if (rupees < 100) {
            // 1-99: Direct conversion
            words = convertHundreds(rupees);
          } else if (rupees < 1000) {
            // 100-999: Hundreds
            words = convertHundreds(rupees);
          } else if (rupees < 100000) {
            // 1000-99999: Thousands and below
            const thousands = Math.floor(rupees / 1000);
            const remainder = rupees % 1000;
  
            if (thousands > 0) {
              words = convertHundreds(thousands) + ' Thousand';
              if (remainder > 0) {
                words += ' ' + convertHundreds(remainder);
              }
            } else {
              words = convertHundreds(remainder);
            }
          } else if (rupees < 10000000) {
            // 100000-9999999: Lakhs, Thousands, and below
            const lakhs = Math.floor(rupees / 100000);
            const remainder = rupees % 100000;
  
            words = convertHundreds(lakhs) + ' Lakh';
  
            if (remainder > 0) {
              if (remainder >= 1000) {
                const thousands = Math.floor(remainder / 1000);
                const remainder2 = remainder % 1000;
                words += ' ' + convertHundreds(thousands) + ' Thousand';
                if (remainder2 > 0) {
                  words += ' ' + convertHundreds(remainder2);
                }
              } else {
                words += ' ' + convertHundreds(remainder);
              }
            }
          } else {
            // 10000000+: Crores, Lakhs, Thousands, and below
            const crores = Math.floor(rupees / 10000000);
            const remainder = rupees % 10000000;
  
            words = convertHundreds(crores) + ' Crore';
  
            if (remainder > 0) {
              if (remainder >= 100000) {
                const lakhs = Math.floor(remainder / 100000);
                const remainder2 = remainder % 100000;
                words += ' ' + convertHundreds(lakhs) + ' Lakh';
  
                if (remainder2 > 0) {
                  if (remainder2 >= 1000) {
                    const thousands = Math.floor(remainder2 / 1000);
                    const remainder3 = remainder2 % 1000;
                    words += ' ' + convertHundreds(thousands) + ' Thousand';
                    if (remainder3 > 0) {
                      words += ' ' + convertHundreds(remainder3);
                    }
                  } else {
                    words += ' ' + convertHundreds(remainder2);
                  }
                }
              } else if (remainder >= 1000) {
                const thousands = Math.floor(remainder / 1000);
                const remainder2 = remainder % 1000;
                words += ' ' + convertHundreds(thousands) + ' Thousand';
                if (remainder2 > 0) {
                  words += ' ' + convertHundreds(remainder2);
                }
              } else {
                words += ' ' + convertHundreds(remainder);
              }
            }
          }
  
          words = words.trim() + ' Rupees';
  
          if (paise > 0) {
            words += ' and ' + convertHundreds(paise).trim() + ' Paise';
          }
  
          return words + ' Only';
        };
  
        const data = {
          settings: {
            cpi: 12,         // Condensed printing
            width: generalsettings?.phPrinterWidth || 80,       // Full width in condensed mode
            indent: 0,
            printerType: generalsettings?.phPrinterType || "TVS",// "EPSON" or "TVS"
  
            feedInLines: generalsettings?.phFeedinLines || 0,
            feedInForward: generalsettings?.phFeedInForward || 0,
            feedOutLines: generalsettings?.phFeedOutLines || 0,
            feedOutForward: generalsettings?.phFeedOutForward || 0,
            returnValidityDays: generalsettings?.phReturnValidityDays || 0,
            is_gstbill : generalsettings?.phIsGstBill || "No",
            show_itemwise_discount: generalsettings?.ShowItemwiseDiscount || "No",
          },
  
          header: {
            firmName:
              generalsettings?.phShowFirmname
                ? `${generalsettings?.FirstName || ""} ${generalsettings?.LastName || ""}`.trim()
                : "",
  
            phones:
              generalsettings?.phShowFirmnumber
                ? [generalsettings?.MobileNo1, generalsettings?.MobileNo2].filter(Boolean)
                : [],
  
            username:
              generalsettings?.phShowUsername && bill.UserName
                ? String(bill.UserName)
                : "",
  
            address:
              generalsettings?.phShowAddress
                ? [
                  // generalsettings?.AddressLine1,
                  // generalsettings?.AddressLine2,
                  generalsettings?.Place
                ].filter(Boolean)
                : [],
  
            workingTime:
              generalsettings?.phShowWorkingHourse && generalsettings?.WorkingTime
                ? String(generalsettings.WorkingTime)
                : "",
  
            // FIXED — guaranteed string
            gst:
              generalsettings?.phShowGst && generalsettings?.GstNo
                ? String(generalsettings.GstNo)
                : "",
  
            // FIXED — MUST BE STRING
            invoiceNumber: formatPrintInvoiceNumber(bill.InvoiceNo),
  
            // Prefer the detail header's CreatedDate — it carries the bill time
            date: formatBillDateTime(
              billCreatedDates[bill.InvoiceNo] || bill.CreatedDate || bill.EntryDate
            ),
  
            patientName: bill?.PatientName || "",
  
            // FIXED — MUST BE STRING
            age: bill?.Age ? String(bill.Age) : "",
  
            gender: bill?.Gender || "",
            doctor: bill?.DoctorName || "",
            dlnum1: generalsettings?.dlnum1 || "",
            dlnum2: generalsettings?.dlnum2 || "",
            place: generalsettings?.Place || "",
          },
  
          products: items.map((p, i) => {
            // Format expiry date to MM/YY format
            let expFormatted = "";
            if (p?.ExpiryDate) {
              const expDate = String(p?.ExpiryDate);
              // If date is in format "YYYY-MM-DD", convert to "MM/YY"
              if (expDate.includes('-')) {
                const parts = expDate.split('-');
                if (parts.length >= 2) {
                  // parts[0] = year, parts[1] = month
                  const year = parts[0].slice(-2); // Get last 2 digits of year
                  expFormatted = `${parts[1]}/${year}`;
                } else {
                  expFormatted = expDate;
                }
              } else {
                expFormatted = expDate;
              }
            }
  
            return {
              sr: ((currentPage - 1) * 6) + (i + 1),
              name: String(p.ItemName || ""),
              batch: p?.BatchCode ? String(p.BatchCode).slice(-6) : "-",
              qty: Number(p.Qty ?? 0),
              rate: Number((p.SalesPrice + (p.Disc_Amount / p.Qty)) ?? 0),
              mrp: Number(p.MRP ?? p?.MRP ?? 0),
              gstRate: Number(p.TaxCode ?? p?.TaxCode ?? 0),
              gstAmount: Number(p.TaxAmount ?? 0),
              discount: Number(p.Disc_Amount ?? 0),
              total: Number((p.NetTotal + p.TaxAmount ?? 0) ?? 0),
              grandTotal: Number(p.TaxAmount + p.NetTotal ?? 0),
              exp: expFormatted
            };
          }),
  
          totals: {
            totalAmount: Number((bill.NetAmount + bill?.TaxAmount + bill?.DiscountAmount).toFixed(2) ?? 0),
            grandTotal: Number(bill.TotalAmount ?? 0),
            discount: Number(bill.DiscountAmount ?? 0),
            gst: Number(bill.TaxAmount ?? 0),
            paid: Number(bill.TotalAmount - bill.BalanceDue ?? 0),
            balance: Number(bill.BalanceDue ?? 0),
            balanceInWords: numberToWords(bill.BalanceDue ?? 0)
          }
        };
  
        const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';
        const copies = getBillCopies(generalsettings);

        if (isElectron()) {
          await invokeBillPrint(ipcRenderer, {
            printerName: sysInfo.pharmacyBillPriinter,
            json: data,
            phBillType,
            copies
          });
        } else {
          webPrintA5Receipt(data, { title: 'PHARMACY CASH BILL', copies });
        }
      }

    }

  };

  const handleSuggestionClick = (selectedOption) => {
    if (selectedOption) {
      setSelectedPatient(selectedOption);
      setMobileInput(selectedOption.MobileNo || '');
      // Focus name input after selecting patient
      setTimeout(() => {
        nameRef.current?.focus();
      }, 100);
    } else {
      setSelectedPatient(null);
      setMobileInput('');
    }
  };

  const options = patients.map(patient => ({
    value: patient.PatientID,
    label: `${patient.PatientName} - ${patient.Age} years - ${patient.Place} - ${patient.MobileNo}`,
    ...patient
  }));

  const doctors_options = doctors.map(doctor => ({
    value: doctor.DoctorID,
    label: doctor.Designation
      ? `${doctor.DoctorName}, ${doctor.Designation}`
      : doctor.DoctorName,
    DoctorName: doctor.DoctorName,
    Designation: doctor.Designation,
    Department: doctor.Department
  }));

  const customOptionLabel = ({ PatientName, Age, Month, Days, MobileNo, PatientID }) => {
    // Convert to numbers and check if greater than zero
    const ageNum = parseInt(Age) || 0;
    const monthNum = parseInt(Month) || 0;
    const daysNum = parseInt(Days) || 0;

    return (
      <div className="flex items-center space-x-3 py-1">
        <div className="flex flex-col flex-1">
          {PatientName && (
            <div className="font-semibold text-xs text-black">
              {PatientName}
            </div>
          )}
          {MobileNo && (
            <div className="text-xs text-black mt-0.5">
              {MobileNo}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {ageNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Age} Y
            </div>
          )}
          {monthNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Month} M
            </div>
          )}
          {daysNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Days} D
            </div>
          )}
        </div>
      </div>
    );
  };


  const customDoctorLabel = ({ DoctorName, Designation }) => {
    return (
      <div className="flex items-center space-x-3 py-1">
        {DoctorName && (
          <div className="font-semibold text-xs text-black">
            {DoctorName}
          </div>
        )}
        {Designation && (
          <div className="font-semibold text-xs text-black">
            , {Designation}
          </div>
        )}
      </div>
    );
  };

  // Calculate default discount string from discount1, discount2, discount3
  // Uses discount1 as primary default (percentage)
  const getDefaultDiscountString = (medicine) => {
    if (!medicine) return '';

    // Use discount1 as the primary default discount
    if (medicine.discount1 && parseFloat(medicine.discount1) > 0) {
      return `${parseFloat(medicine.discount1).toFixed(2)}p`;
    }

    // Fallback to discount2 if discount1 is not available
    if (medicine.discount2 && parseFloat(medicine.discount2) > 0) {
      return `${parseFloat(medicine.discount2).toFixed(2)}p`;
    }

    // Fallback to discount3 if discount1 and discount2 are not available
    if (medicine.discount3 && parseFloat(medicine.discount3) > 0) {
      return `${parseFloat(medicine.discount3).toFixed(2)}p`;
    }

    return '';
  };

  // Normalize/validate discount input for an item.
  // Amount discount: 0 .. item total. Percentage (p/%): 0 .. 100.
  // Returns { value, rejected } — rejected means input was ignored (e.g. negative).
  const normalizeDiscountInput = (discountStr, maxAmount) => {
    if (discountStr == null || discountStr.toString().trim() === '') {
      return { value: '', rejected: false };
    }

    let normalized = discountStr.toString();
    if (normalized.endsWith('P')) {
      normalized = normalized.slice(0, -1) + 'p';
    }

    const trimmed = normalized.trim();
    const lower = trimmed.toLowerCase();
    const isPercentage = lower.endsWith('p') || lower.endsWith('%');
    const suffix = isPercentage ? (lower.endsWith('%') ? '%' : 'p') : '';
    const numericPart = isPercentage ? trimmed.slice(0, -1).trim() : trimmed;

    // Allow incomplete typing (e.g. "", ".", "p")
    if (numericPart === '' || numericPart === '.') {
      return { value: trimmed, rejected: false };
    }

    // Reject negatives
    if (numericPart.startsWith('-') || parseFloat(numericPart) < 0) {
      return { value: null, rejected: true, reason: 'negative' };
    }

    const num = parseFloat(numericPart);
    if (isNaN(num)) {
      return { value: trimmed, rejected: false };
    }

    if (isPercentage) {
      if (num > 100) {
        return { value: `100${suffix}`, rejected: false, clamped: true, reason: 'max_percent' };
      }
      return { value: trimmed, rejected: false };
    }

    const max = Math.max(0, parseFloat(maxAmount) || 0);
    if (num > max) {
      // Keep up to 2 decimal places for amount caps
      const capped = Number.isInteger(max) ? String(max) : max.toFixed(2);
      return { value: capped, rejected: false, clamped: true, reason: 'max_amount' };
    }

    return { value: trimmed, rejected: false };
  };

  // Calculate discount amount based on discount string
  const calculateDiscountAmount = (amount, discountStr) => {
    if (!discountStr) return 0;

    const discountTrimmed = discountStr.toString().trim();
    const discountLower = discountTrimmed.toLowerCase();

    // Check for percentage discount (case-insensitive: 'p', 'P', or '%')
    if (discountLower.endsWith('p') || discountLower.endsWith('%')) {
      // Percentage discount — clamp to 0..100
      let percentage = parseFloat(discountTrimmed.slice(0, -1));
      if (!isNaN(percentage)) {
        if (percentage < 0) percentage = 0;
        if (percentage > 100) percentage = 100;
        return (amount * percentage) / 100;
      }
    } else {
      // Fixed amount discount — clamp to 0..amount
      let fixedAmount = parseFloat(discountTrimmed);
      if (!isNaN(fixedAmount)) {
        if (fixedAmount < 0) fixedAmount = 0;
        return Math.min(fixedAmount, amount);
      }
    }

    return 0;
  };

  // Returns: { baseAmount, gstAmount, totalAmount }
  const calculateGstInclusive = (mrp, qty, discountStr, gstRate) => {

    // Total MRP (GST-exclusive base amount) = qty * mrp
    const totalMrp = qty * parseFloat(mrp || 0);

    // Apply discount on base MRP (before GST)
    const discountAmount = calculateDiscountAmount(totalMrp, discountStr);
    const baseAmount = totalMrp - discountAmount;

    let normalizedGstRate = gstRate;
    if (
      normalizedGstRate === '' ||
      normalizedGstRate == null ||
      (typeof normalizedGstRate === 'number' && Number.isNaN(normalizedGstRate))
    ) {
      normalizedGstRate = 0;
    }

    let taxRate = 0
    if(normalizedGstRate === 5){
      taxRate = 1.05;
    } else if(normalizedGstRate === 12){
      taxRate = 1.12;
    } else if(normalizedGstRate === 18){
      taxRate = 1.18;
    } else if(normalizedGstRate === 28){
      taxRate = 1.28;
    } else {
      taxRate = 0;
    }

    const taxDivisor = parseFloat(taxRate || 0);
    const NetTotal = taxDivisor > 0 ? baseAmount / taxDivisor : baseAmount;

    const gstAmount = baseAmount - NetTotal;


    // Total amount = base amount + GST
    const totalAmount = baseAmount + gstAmount;

    return {
      baseAmount,
      gstAmount,
      totalAmount: baseAmount, // This is what customer pays (base + GST)
      discountAmount,
      amount: totalMrp // Original amount before discount
    };
  };

  const handleQtyChange = (index, newQty) => {
    const requestedQty = parseFloat(newQty || 0);
    const currentProduct = products[index];

    if (!currentProduct || !currentProduct.batch || !currentProduct.medicine) {
      return;
    }

    // Try to find the medicine in medicines state or cache, but don't fail if not found
    let selectedMedicine = medicines.find(med => med.id === currentProduct.medicine.value);
    // If not found in medicines state, check cache
    if (!selectedMedicine && medicinesCacheRef.current[currentProduct.medicine.value]) {
      selectedMedicine = medicinesCacheRef.current[currentProduct.medicine.value];
    }

    const availableQty = parseFloat(currentProduct.batch.availableQty || 0);

    // Calculate quantities already used for this medicine across all rows (excluding current row)
    const updatedProducts = [...products];
    const sameMedicineProducts = updatedProducts.filter(
      (p, i) => i !== index && p.batch && p.medicine?.value === currentProduct.medicine.value
    );

    // Calculate available stock for current batch (accounting for quantities already in cart)
    // Subtract quantities already used in other rows for this specific batch
    const usedInOtherRowsForCurrentBatch = sameMedicineProducts
      .filter(p => p.batch.batchNo === currentProduct.batch.batchNo)
      .reduce((sum, p) => sum + parseFloat(p.qty || 0), 0);

    let currentBatchAvailableQty = Math.max(0, availableQty - usedInOtherRowsForCurrentBatch);

    // If we have medicine data with all batches, calculate total available across all batches
    let totalAvailableStock = currentBatchAvailableQty;
    if (selectedMedicine && selectedMedicine.batches && selectedMedicine.batches.length > 0) {
      // Calculate total stock across all batches, accounting for quantities already used
      totalAvailableStock = selectedMedicine.batches.reduce((sum, batch) => {
        // Calculate how much of this batch is already used in other rows
        const usedInOtherRows = sameMedicineProducts
          .filter(p => p.batch.batchNo === batch.batchNo)
          .reduce((s, p) => s + parseFloat(p.qty || 0), 0);

        const batchStock = parseFloat(batch.availableQty || 0);
        const available = Math.max(0, batchStock - usedInOtherRows);
        return sum + available;
      }, 0);
    } else {
      // If medicine data not available, just use current batch stock
      totalAvailableStock = currentBatchAvailableQty;
    }

    // Limit requested quantity to total available stock
    const finalQty = Math.max(0, Math.min(requestedQty, totalAvailableStock));

    // If requested quantity exceeds available stock, show warning
    if (requestedQty > totalAvailableStock) {
      toast.warning(`Insufficient stock. Only ${totalAvailableStock} units available. Quantity set to ${totalAvailableStock}.`, {
        autoClose: 2000,
      });
    }

    // If we have medicine data with multiple batches and quantity exceeds current batch, try to split
    if (selectedMedicine && selectedMedicine.batches && selectedMedicine.batches.length > 1 && finalQty > currentBatchAvailableQty) {
      let remainingQty = finalQty;

      // Update current row with available stock from current batch
      const currentQty = Math.min(remainingQty, currentBatchAvailableQty);
      const currentMrp = parseFloat(currentProduct.batch.mrp || 0);
      const currentCalculated = calculateGstInclusive(currentMrp, currentQty, currentProduct.discount, currentProduct.batch.gstRate);
      const currentBaseRate = currentMrp / (1 + (currentProduct.batch.gstRate / 100));

      updatedProducts[index] = {
        ...currentProduct,
        qty: currentQty,
        rate: currentBaseRate.toFixed(2),
        amount: currentCalculated.amount.toFixed(2),
        discountAmount: currentCalculated.discountAmount.toFixed(2),
        gstAmount: currentCalculated.gstAmount.toFixed(2),
        gstRate: currentProduct.batch.gstRate,
        totalAmount: currentCalculated.totalAmount.toFixed(2)
      };

      remainingQty -= currentQty;

      // Track insertion point (starts after current row)
      let insertIndex = index + 1;
      const usedBatchNos = sameMedicineProducts.map(p => p.batch.batchNo);

      // Add new rows for remaining batches (check all batches, skip already used ones)
      for (let i = 0; i < selectedMedicine.batches.length && remainingQty > 0; i++) {
        const batch = selectedMedicine.batches[i];

        // Skip if this batch is already used in another row or is the current batch
        if (usedBatchNos.includes(batch.batchNo) || batch.batchNo === currentProduct.batch.batchNo) {
          continue;
        }

        // Calculate available stock for this batch (accounting for quantities already used)
        const usedInOtherRows = sameMedicineProducts
          .filter(p => p.batch.batchNo === batch.batchNo)
          .reduce((sum, p) => sum + parseFloat(p.qty || 0), 0);

        const batchAvailableQty = Math.max(0, parseFloat(batch.availableQty || 0) - usedInOtherRows);
        if (batchAvailableQty <= 0) continue;

        const batchQty = Math.min(remainingQty, batchAvailableQty);

        if (batchQty > 0) {
          const batchMrp = parseFloat(batch.mrp || 0);
          // Use the same discount as the original product, or get default from medicine
          const batchDiscount = currentProduct.discount || getDefaultDiscountString(selectedMedicine);
          const batchCalculated = calculateGstInclusive(batchMrp, batchQty, batchDiscount, batch.gstRate);
          const batchBaseRate = batchMrp / (1 + (batch.gstRate / 100));

          const newProduct = {
            id: selectedMedicine.id,
            medicine: { value: selectedMedicine.id, label: selectedMedicine.name },
            batch: batch,
            rate: batchBaseRate.toFixed(2),
            amount: batchCalculated.amount.toFixed(2),
            qty: batchQty,
            discount: batchDiscount,
            defaultDiscount: currentProduct.defaultDiscount || getDefaultDiscountString(selectedMedicine),
            discountAmount: batchCalculated.discountAmount.toFixed(2),
            mrp: batch.mrp,
            gstAmount: batchCalculated.gstAmount.toFixed(2),
            gstRate: batch.gstRate,
            totalAmount: batchCalculated.totalAmount.toFixed(2),
            name: selectedMedicine.name,
            shelf: selectedMedicine.shelf || currentProduct.shelf || ''
          };

          // Insert new product at the tracked position
          updatedProducts.splice(insertIndex, 0, newProduct);
          remainingQty -= batchQty;
          insertIndex++; // Move insertion point forward

          // Mark this batch as used to avoid duplicates
          usedBatchNos.push(batch.batchNo);
        }
      }

      // If still remaining quantity, show warning
      if (remainingQty > 0) {
        toast.warning(`Insufficient stock. Only ${finalQty - remainingQty} units available.`, {
          autoClose: 2000,
        });
      }

      setProducts(updatedProducts);

      // Expand refs arrays after state update
      setTimeout(() => {
        const newLength = updatedProducts.length;
        if (productRefs.current.length < newLength) {
          productRefs.current = Array(newLength).fill(null);
        }
        if (qtyRefs.current.length < newLength) {
          qtyRefs.current = Array(newLength).fill(null);
        }
        if (rateRefs.current.length < newLength) {
          rateRefs.current = Array(newLength).fill(null);
        }
        if (discountRefs.current.length < newLength) {
          discountRefs.current = Array(newLength).fill(null);
        }
      }, 50);
    } else {
      // Normal case: quantity doesn't exceed current batch stock, but limit to available stock
      const qty = Math.max(0, Math.min(parseFloat(newQty || 0), currentBatchAvailableQty));

      // If requested qty exceeds available stock, show warning
      if (parseFloat(newQty || 0) > currentBatchAvailableQty) {
        toast.warning(`Insufficient stock. Only ${currentBatchAvailableQty} units available in this batch. Quantity set to ${currentBatchAvailableQty}.`, {
          autoClose: 2000,
        });
      }

      const updatedProducts = products.map((product, i) => {
        if (i === index && product.batch) {
          const mrp = parseFloat(product.batch.mrp || 0);
          const calculated = calculateGstInclusive(mrp, qty, product.discount, product.batch.gstRate);

          return {
            ...product,
            qty: qty,
            amount: calculated.amount.toFixed(2),
            discountAmount: calculated.discountAmount.toFixed(2),
            gstAmount: calculated.gstAmount.toFixed(2),
            gstRate: product.batch.gstRate,
            totalAmount: calculated.totalAmount.toFixed(2),
            rate: (mrp / (1 + (product.batch.gstRate / 100))).toFixed(2) // Base rate for display
          };
        }
        return product;
      });
      setProducts(updatedProducts);
    }
  };

  // Handle discount changes
  const handleDiscountChange = (index, newDiscount) => {
    const product = products[index];
    if (!product?.batch) return;

    const qty = parseFloat(product.qty || 0);
    const mrp = parseFloat(product.batch.mrp || 0);
    const itemTotal = qty * mrp;

    const { value: normalizedDiscount, rejected, clamped, reason } = normalizeDiscountInput(
      newDiscount,
      itemTotal
    );

    if (rejected) {
      if (reason === 'negative') {
        toast.error('Discount cannot be negative', { autoClose: 1500 });
      }
      return;
    }

    if (clamped) {
      if (reason === 'max_percent') {
        toast.warning('Discount cannot exceed 100%', { autoClose: 1500 });
      } else if (reason === 'max_amount') {
        toast.warning(`Discount cannot exceed item total (${itemTotal.toFixed(2)})`, { autoClose: 1500 });
      }
    }

    const updatedProducts = products.map((p, i) => {
      if (i === index && p.batch) {
        const rowQty = parseFloat(p.qty || 0);
        const rowMrp = parseFloat(p.batch.mrp || 0);
        const calculated = calculateGstInclusive(rowMrp, rowQty, normalizedDiscount, p.batch.gstRate);

        return {
          ...p,
          discount: normalizedDiscount,
          amount: calculated.amount.toFixed(2),
          discountAmount: calculated.discountAmount.toFixed(2),
          gstAmount: calculated.gstAmount.toFixed(2),
          gstRate: p.batch.gstRate,
          totalAmount: calculated.totalAmount.toFixed(2)
        };
      }
      return p;
    });
    setProducts(updatedProducts);
  };

  // Apply global discount to all items
  const applyGlobalDiscount = (discountValue) => {
    if (!discountValue || discountValue.trim() === '') {
      // If global discount is cleared, restore default discounts for each item from stored defaultDiscount field
      const updatedProducts = products.map((product) => {
        if (product.batch) {
          // Restore from stored defaultDiscount field, or use empty string if not available
          const defaultDiscountStr = product.defaultDiscount || '';
          const qty = parseFloat(product.qty || 0);
          const mrp = parseFloat(product.batch.mrp || 0);
          const calculated = calculateGstInclusive(mrp, qty, defaultDiscountStr, product.batch.gstRate);

          return {
            ...product,
            discount: defaultDiscountStr,
            amount: calculated.amount.toFixed(2),
            discountAmount: calculated.discountAmount.toFixed(2),
            gstAmount: calculated.gstAmount.toFixed(2),
            totalAmount: calculated.totalAmount.toFixed(2)
          };
        }
        return product;
      });
      setProducts(updatedProducts);
      return;
    }

    const discountTrimmed = discountValue.toString().trim();
    const discountLower = discountTrimmed.toLowerCase();

    // Check if it's a percentage discount (ends with 'p' or '%')
    const isPercentage = discountLower.endsWith('p') || discountLower.endsWith('%');

    // Get valid products (items with batch)
    const validProducts = products.filter(product => product.batch);
    const itemCount = validProducts.length;

    if (itemCount === 0) {
      return; // No items to apply discount to
    }

    // Apply global discount to all items
    const updatedProducts = products.map((product) => {
      if (product.batch) {
        const qty = parseFloat(product.qty || 0);
        const mrp = parseFloat(product.batch.mrp || 0);

        let discountToApply = discountValue;

        // If it's a fixed amount (not percentage), divide equally among all items
        if (!isPercentage) {
          const globalDiscountAmount = parseFloat(discountTrimmed);
          if (!isNaN(globalDiscountAmount) && itemCount > 0) {
            // Divide the total discount amount equally among all items
            const perItemDiscount = (globalDiscountAmount / itemCount).toFixed(2);
            discountToApply = perItemDiscount; // Apply as fixed amount per item
          }
        }

        const calculated = calculateGstInclusive(mrp, qty, discountToApply, product.batch.gstRate);

        return {
          ...product,
          discount: discountToApply,
          amount: calculated.amount.toFixed(2),
          discountAmount: calculated.discountAmount.toFixed(2),
          gstAmount: calculated.gstAmount.toFixed(2),
          totalAmount: calculated.totalAmount.toFixed(2)
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle global discount change
  const handleGlobalDiscountChange = (value) => {
    // Max fixed amount = sum of all item totals (qty * mrp)
    const billTotal = products.reduce((sum, product) => {
      if (!product?.batch) return sum;
      const qty = parseFloat(product.qty || 0);
      const mrp = parseFloat(product.batch.mrp || 0);
      return sum + (qty * mrp);
    }, 0);

    const { value: normalizedValue, rejected, clamped, reason } = normalizeDiscountInput(
      value,
      billTotal
    );

    if (rejected) {
      if (reason === 'negative') {
        toast.error('Discount cannot be negative', { autoClose: 1500 });
      }
      return;
    }

    if (clamped) {
      if (reason === 'max_percent') {
        toast.warning('Discount cannot exceed 100%', { autoClose: 1500 });
      } else if (reason === 'max_amount') {
        toast.warning(`Discount cannot exceed bill total (${billTotal.toFixed(2)})`, { autoClose: 1500 });
      }
    }

    setGlobalDiscount(normalizedValue);
    applyGlobalDiscount(normalizedValue);
  };

  // Handle half quantity - set all quantities to half (round up if fractional)
  const handleHalfQuantity = () => {
    if (products.length === 0) {
      toast.error('No products to set half quantity', { autoClose: 1000 });
      return;
    }
    const updatedProducts = products.map((product) => {
      if (!product.batch || !product.qty) {
        return product;
      }

      const currentQty = parseFloat(product.qty || 0);

      // Only process if quantity is greater than 1
      if (currentQty <= 1) {
        return product;
      }

      // Calculate half
      const halfQty = currentQty / 2;

      // If half is a whole number, use it; otherwise round up
      const newQty = Number.isInteger(halfQty) ? halfQty : Math.ceil(halfQty);

      // Update quantity using the existing handleQtyChange logic
      // We'll update the product directly here to avoid recursion
      const mrp = parseFloat(product.batch.mrp || 0);
      const calculated = calculateGstInclusive(mrp, newQty, product.discount, product.batch.gstRate);

      return {
        ...product,
        qty: newQty,
        amount: calculated.amount.toFixed(2),
        discountAmount: calculated.discountAmount.toFixed(2),
        gstAmount: calculated.gstAmount.toFixed(2),
        totalAmount: calculated.totalAmount.toFixed(2)
      };
    });

    setProducts(updatedProducts);
    setHalfQtyUsed(true);
    toast.success('All quantities set to half', { autoClose: 1000 });
  };

  // Handle quantity input key navigation
  const handleQtyKeyDown = (e, index) => {
    const currentQty = parseFloat(products[index]?.qty || 0);
    const hasValidQty = currentQty > 0;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's qty input
      if (index > 0 && qtyRefs.current[index - 1]) {
        qtyRefs.current[index - 1].focus();
        qtyRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's qty input
      if (index < products.length - 1 && qtyRefs.current[index + 1]) {
        qtyRefs.current[index + 1].focus();
        qtyRefs.current[index + 1].select();
      } else if (index === products.length - 1 && hasValidQty) {
        // If on last row with qty > 0, focus the medicine search input
        setTimeout(() => {
          if (medicineSearchInputRef.current) {
            medicineSearchInputRef.current.focus();
            setShowMedicineList(true);
            setSelectedMedicineIndex(-1);
            // Calculate position for dropdown
            if (medicineSearchContainerRef.current) {
              const rect = medicineSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 600)
              });
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();

      if (!hasValidQty) {
        return;
      }

      // Focus the medicine search input in the yellow row and show the list
      setTimeout(() => {
        if (medicineSearchInputRef.current) {
          medicineSearchInputRef.current.focus();
          setShowMedicineList(true);
          setSelectedMedicineIndex(-1);
          // Calculate position for dropdown
          if (medicineSearchContainerRef.current) {
            const rect = medicineSearchContainerRef.current.getBoundingClientRect();
            setDropdownPosition({
              top: rect.bottom + window.scrollY + 4,
              left: rect.left + window.scrollX,
              width: Math.max(rect.width, 600)
            });
          }
        }
      }, 150);
    }
  };

  const focusMedicineSearchInput = () => {
    setTimeout(() => {
      // Blur whatever currently has focus (e.g. discount input after Swal)
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
      if (medicineSearchInputRef.current) {
        medicineSearchInputRef.current.focus();
        setShowMedicineList(true);
        setSelectedMedicineIndex(-1);
        if (medicineSearchContainerRef.current) {
          const rect = medicineSearchContainerRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: Math.max(rect.width, 600)
          });
        }
      }
    }, 150);
  };

  const parseDiscountPercentValue = (discountStr) => {
    if (!discountStr) return null;
    const trimmed = discountStr.toString().trim();
    const lower = trimmed.toLowerCase();
    if (lower.endsWith('p') || lower.endsWith('%')) {
      const percentage = parseFloat(trimmed.slice(0, -1));
      return !isNaN(percentage) ? percentage : null;
    }
    return null;
  };

  const buildItemMasterPayload = (item, discount1) => ({
    ItemCode: item.Item_Code ?? item.ItemCode ?? '',
    Item_Name: item.Item_Name ?? item.ItemName ?? '',
    Company: item.Company ?? '',
    Chemical: item.Chemical ?? 0,
    Section: item.Section ?? '',
    Category: item.Category ?? '',
    Schedule: item.Schedule ?? '',
    Package: parseFloat(item.Package ?? item.package ?? 0) || 0,
    Packing_Size: '',
    Unit: item.Unit ?? item.unit ?? '',
    CostPerTest: 0,
    CostPerTestUnit: '',
    Mrp: parseFloat(item.MRP ?? item.Mrp ?? item.mrp ?? 0) || 0,
    Rate: parseFloat(item.Rate ?? item.rate ?? 0) || 0,
    Supplier: item.Supplier ?? 0,
    Shelf: item.Shelf ?? '',
    HsnCode: item.HSNCode ?? item.HsnCode ?? '',
    Barcode: item.Barcode ?? '',
    ItemType: 'Pharmacy',
    TaxCode: item.TaxCode ?? '',
    ReorderLevel: parseFloat(item.ReorderLevel ?? 0) || 0,
    ReorderLevelUnit: '',
    Remarks: item.Remarks ?? '',
    Discount1: discount1,
    Discount2: parseFloat(item.Discount2 ?? 0) || 0,
    Discount3: parseFloat(item.Discount3 ?? 0) || 0,
  });

  const updateMedicineDefaultDiscountCache = (itemId, discount1) => {
    const defaultDiscountStr = `${parseFloat(discount1).toFixed(2)}p`;
    const updateMedicine = (med) => {
      if (med.id !== itemId) return med;
      return { ...med, discount1 };
    };
    setMedicines((prev) => prev.map(updateMedicine));
    if (medicinesCacheRef.current[itemId]) {
      medicinesCacheRef.current[itemId] = {
        ...medicinesCacheRef.current[itemId],
        discount1,
      };
    }
    setProducts((prev) =>
      prev.map((p) =>
        p.id === itemId
          ? { ...p, defaultDiscount: defaultDiscountStr }
          : p
      )
    );
  };

  const updateItemMasterDefaultDiscount = async (product) => {
    const itemId = product?.id;
    if (!itemId) return false;

    const discountPercent = parseDiscountPercentValue(product.discount);
    if (discountPercent === null) {
      toast.error('Only percentage discounts (e.g. 10p) can be saved as the item default.', {
        autoClose: 2000,
      });
      return false;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'PageNo': 1,
          'PageSize': 1,
          'FilterText': itemId,
          'FilterColumn': '',
          'FilterValue': '',
        },
      });

      if (response.status !== 200) {
        toast.error('Could not load item data.', { autoClose: 1000 });
        return false;
      }

      const masterData = response.data?.data?.masterData || [];
      const item = masterData.find(
        (row) => String(row.Item_No ?? row.ItemNo ?? row.id) === String(itemId)
      ) || masterData[0];

      if (!item) {
        toast.error('Item not found in master.', { autoClose: 1000 });
        return false;
      }

      const productData = buildItemMasterPayload(item, discountPercent);
      const updateResponse = await axios.post(`${BASE_URL}${PUT_ITEM_MASTER}`, productData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'ItemNo': itemId,
        },
      });

      if (updateResponse.status === 200) {
        updateMedicineDefaultDiscountCache(itemId, discountPercent);
        toast.success('Default discount updated for this item.', { autoClose: 1000 });
        return true;
      }

      toast.error('Failed to update default discount.', { autoClose: 1000 });
      return false;
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while saving the default discount.', { autoClose: 1000 });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle discount input key navigation
  const handleDiscountKeyDown = async (e, index, product) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    if (!product?.id || !product?.batch) {
      focusMedicineSearchInput();
      return;
    }

    const askSaveDefaultDiscount =
      String(generalsettings?.GlobalDiscountonSales || 'No').toLowerCase() === 'yes';
    const discountPercent = parseDiscountPercentValue(product.discount);

    // Only prompt when setting is Yes and a percentage discount was entered
    if (askSaveDefaultDiscount && discountPercent !== null) {
      const itemLabel = product.name || product.medicine?.label || 'this item';
      const discountDisplay = product.discount || '0';
      const result = await Swal.fire({
        title: 'Set default discount?',
        html: `Save <strong>${discountDisplay}</strong> as the default discount for <strong>${itemLabel}</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        // Keep focus from bouncing back to the discount field after close
        returnFocus: false,
        allowEnterKey: true,
        customClass: {
          confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
          cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
          popup: 'rounded-xl shadow-xl'
        }
      });

      if (result.isConfirmed) {
        await updateItemMasterDefaultDiscount(product);
      }
    }

    // Always move to next medicine search (Yes, No, or when prompt is skipped)
    focusMedicineSearchInput();
  };

  // Handle batch selection
  const handleBatchSelection = (index, medicine, selectedBatch) => {
    const qty = 0;
    const mrp = parseFloat(selectedBatch.mrp || 0);
    // Get the medicine's default discount (not global discount)
    const medicineDefaultDiscount = getDefaultDiscountString(medicine);
    // Use global discount if set, otherwise use default discount from medicine
    const defaultDiscountStr = globalDiscount || medicineDefaultDiscount;
    const calculated = calculateGstInclusive(mrp, qty, defaultDiscountStr, selectedBatch.gstRate);
    const baseRate = mrp / (1 + (selectedBatch.gstRate / 100));

    // If index is equal to products.length, it means we're adding a new product
    if (index === products.length) {
      const newProduct = {
        id: medicine.id,
        medicine: { value: medicine.id, label: medicine.name },
        batch: selectedBatch,
        rate: baseRate.toFixed(2),
        amount: calculated.amount.toFixed(2),
        qty: qty,
        discount: defaultDiscountStr,
        defaultDiscount: medicineDefaultDiscount, // Store the original default discount from medicine
        discountAmount: calculated.discountAmount.toFixed(2),
        mrp: selectedBatch.mrp,
        gstAmount: calculated.gstAmount.toFixed(2),
        gstRate: selectedBatch.gstRate,
        totalAmount: calculated.totalAmount.toFixed(2),
        name: medicine.name,
        shelf: medicine.shelf || ''
      };

      // Check if there are any blank rows (rows without medicine) and replace the first one
      const blankRowIndex = products.findIndex(p => !p.medicine && !p.batch && !p.name);

      if (blankRowIndex !== -1) {
        // Replace the blank row with the new product
        const newProducts = [...products];
        newProducts[blankRowIndex] = newProduct;
        setProducts(newProducts);
        // Focus is handled by the caller (modal callback)
      } else {
        // No blank rows, add to the end
        setProducts([...products, newProduct]);

        // Expand refs arrays
        requestAnimationFrame(() => {
          const newLength = products.length + 1;
          if (qtyRefs.current.length < newLength) {
            qtyRefs.current = [...qtyRefs.current, null];
          }
          if (rateRefs.current.length < newLength) {
            rateRefs.current = [...rateRefs.current, null];
          }
          if (discountRefs.current.length < newLength) {
            discountRefs.current = [...discountRefs.current, null];
          }
          // Focus is handled by the caller (modal callback)
        });
      }

      // Clear search and hide dropdown
      setMedicineSearch('');
      setShowMedicineList(false);
    } else {
      // Update existing product
      const newProducts = products.map((product, i) => {
        if (i === index) {
          const mrp = parseFloat(selectedBatch.mrp || 0);
          // Get the medicine's default discount (not global discount)
          const medicineDefaultDiscount = getDefaultDiscountString(medicine);
          // Use global discount if set, otherwise use default discount from medicine
          const defaultDiscountStr = globalDiscount || medicineDefaultDiscount;
          const calculated = calculateGstInclusive(mrp, qty, defaultDiscountStr, selectedBatch.gstRate);
          const baseRate = mrp / (1 + (selectedBatch.gstRate / 100));

          return {
            id: medicine.id,
            medicine: { value: medicine.id, label: medicine.name },
            batch: selectedBatch,
            rate: baseRate.toFixed(2),
            amount: calculated.amount.toFixed(2),
            qty: qty,
            discount: defaultDiscountStr,
            defaultDiscount: medicineDefaultDiscount, // Store the original default discount from medicine
            discountAmount: calculated.discountAmount.toFixed(2),
            mrp: selectedBatch.mrp,
            gstAmount: calculated.gstAmount.toFixed(2),
            gstRate: selectedBatch.gstRate,
            totalAmount: calculated.totalAmount.toFixed(2),
            name: medicine.name,
            shelf: medicine.shelf || ''
          };
        }
        return product;
      });
      setProducts(newProducts);
    }
  };

  const handleDeleteRow = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
  };

  // Calculate match score for relevance ranking
  const calculateMatchScore = (med, searchLower) => {
    let score = 0;
    const nameLower = med.name.toLowerCase();
    const chemicalLower = med.genericName ? med.genericName.toLowerCase() : '';

    // Exact match in name (highest priority)
    if (nameLower === searchLower) {
      score += 1000;
    }
    // Name starts with search text (high priority)
    else if (nameLower.startsWith(searchLower)) {
      score += 500;
      // Bonus for shorter names (more specific matches)
      score += Math.max(0, 100 - nameLower.length);
    }
    // Name contains search text
    else if (nameLower.includes(searchLower)) {
      score += 200;
      // Bonus for earlier position in name
      const position = nameLower.indexOf(searchLower);
      score += Math.max(0, 50 - position);
    }

    // Chemical name matches (lower priority than name matches)
    if (chemicalLower) {
      if (chemicalLower === searchLower) {
        score += 100;
      } else if (chemicalLower.startsWith(searchLower)) {
        score += 50;
      } else if (chemicalLower.includes(searchLower)) {
        score += 20;
      }
    }

    return score;
  };

  // Filter and sort medicines by relevance
  const filteredMedicines = (() => {
    const searchText = editingMedicineRowIndex !== null ? medicineSearchForEdit : medicineSearch;
    if (!searchText || searchText.trim().length === 0) {
      return [];
    }

    const searchLower = searchText.toLowerCase();

    // Filter medicines that match
    const matched = medicines.filter((med) => {
      const nameMatch = med.name.toLowerCase().includes(searchLower);
      const chemicalMatch = med.genericName && med.genericName.toLowerCase().includes(searchLower);
      return nameMatch || chemicalMatch;
    });

    // Sort by match score (highest first)
    return matched.sort((a, b) => {
      const scoreA = calculateMatchScore(a, searchLower);
      const scoreB = calculateMatchScore(b, searchLower);
      return scoreB - scoreA; // Descending order
    });
  })();

  useEffect(() => {
    // Always set to -1 (no selection) - user must manually navigate
    setSelectedMedicineIndex(-1);
    // Reset row refs array
    medicineRowRefs.current = [];
  }, [filteredMedicines.length, medicineSearch, medicineSearchForEdit, editingMedicineRowIndex]);

  // Show medicine list when editing a row - don't auto-focus
  useEffect(() => {
    if (editingMedicineRowIndex !== null) {
      setShowMedicineList(true);
      setSelectedMedicineIndex(-1); // No item focused initially
    }
  }, [editingMedicineRowIndex]);

  // Handle ESC key globally when editing medicine
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && editingMedicineRowIndex !== null) {
        e.preventDefault();
        // Close without making any changes
        setShowMedicineList(false);
        setMedicineSearch('');
        setMedicineSearchForEdit('');
        setEditingMedicineRowIndex(null);
        setSelectedMedicineIndex(-1);
        setMedicines([]);
      }
    };

    if (editingMedicineRowIndex !== null) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [editingMedicineRowIndex]);

  // Handle click outside to close medicine dropdown when editing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editingMedicineRowIndex !== null) {
        // Check if click is outside the dropdown
        if (
          medicineDropdownRef.current &&
          !medicineDropdownRef.current.contains(e.target) &&
          medicineNameCellRefs.current[editingMedicineRowIndex] &&
          !medicineNameCellRefs.current[editingMedicineRowIndex].contains(e.target)
        ) {
          // Close without making any changes
          setShowMedicineList(false);
          setMedicineSearch('');
          setMedicineSearchForEdit('');
          setEditingMedicineRowIndex(null);
          setSelectedMedicineIndex(-1);
          setMedicines([]);
        }
      }
    };

    if (editingMedicineRowIndex !== null) {
      // Use mousedown to catch clicks before they bubble
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingMedicineRowIndex]);

  // Initialize with no selection when medicine list first shows
  useEffect(() => {
    if (showMedicineList && editingMedicineRowIndex === null) {
      const searchText = medicineSearch;
      // Only focus if user has typed something
      if (!searchText || searchText.trim().length === 0) {
        setSelectedMedicineIndex(-1);
      }
    }
  }, [showMedicineList]);

  // Scroll selected row into view
  useEffect(() => {
    if (showMedicineList && selectedMedicineIndex >= 0 && medicineRowRefs.current[selectedMedicineIndex] && medicineDropdownRef.current) {
      const selectedRow = medicineRowRefs.current[selectedMedicineIndex];
      const dropdownContainer = medicineDropdownRef.current;

      // Calculate if row is visible
      const rowTop = selectedRow.offsetTop;
      const rowBottom = rowTop + selectedRow.offsetHeight;
      const containerTop = dropdownContainer.scrollTop;
      const containerBottom = containerTop + dropdownContainer.clientHeight;

      // Scroll if row is not fully visible
      if (rowTop < containerTop) {
        dropdownContainer.scrollTop = rowTop - 10; // 10px padding
      } else if (rowBottom > containerBottom) {
        dropdownContainer.scrollTop = rowBottom - dropdownContainer.clientHeight + 10;
      }
    }
  }, [selectedMedicineIndex, showMedicineList]);

  // Handle keyboard navigation in medicine dropdown
  const handleMedicineSearchKeyDown = (e) => {
    if (!showMedicineList) {
      if (e.key === 'Enter' && filteredMedicines.length > 0) {
        setShowMedicineList(true);
        setSelectedMedicineIndex(-1); // Don't auto-focus
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (selectedMedicineIndex === -1) {
        // Start from first item if nothing is focused
        setSelectedMedicineIndex(0);
      } else {
        setSelectedMedicineIndex((prev) =>
          prev < filteredMedicines.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (selectedMedicineIndex === -1) {
        // Start from last item if nothing is focused
        setSelectedMedicineIndex(filteredMedicines.length - 1);
      } else {
        setSelectedMedicineIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedMedicineIndex >= 0 && selectedMedicineIndex < filteredMedicines.length) {
        // If an item is focused, select it
        const selectedMedicine = filteredMedicines[selectedMedicineIndex];
        if (selectedMedicine) {
          const targetIndex = editingMedicineRowIndex !== null ? editingMedicineRowIndex : products.length;
          handleSelectMedicineFromSearch(selectedMedicine, targetIndex);
        }
      } else {
        // If no item is focused, focus the submit button
        setShowMedicineList(false);
        setEditingMedicineRowIndex(null);
        setMedicineSearchForEdit('');
        setTimeout(() => {
          if (submitbtnRef.current) {
            submitbtnRef.current.focus();
          }
        }, 100);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Close without making any changes
      setShowMedicineList(false);
      setMedicineSearch('');
      setMedicineSearchForEdit('');
      setEditingMedicineRowIndex(null);
      setSelectedMedicineIndex(-1);
      // Clear medicines if we were editing (reset to empty)
      if (editingMedicineRowIndex !== null) {
        setMedicines([]);
      }
    }
  };

  // Handle medicine selection from search dropdown
  const handleSelectMedicineFromSearch = async (medicine, targetRowIndex = null) => {
    if (!medicine) return;

    // Mark that we're selecting to prevent blur handler interference
    isSelectingMedicineRef.current = true;

    // Immediately blur the medicine search input to prevent it from getting focus
    if (medicineSearchInputRef.current) {
      medicineSearchInputRef.current.blur();
    }

    // Clear search and hide dropdown immediately
    setMedicineSearch('');
    setShowMedicineList(false);
    setSelectedMedicineIndex(-1);
    setMedicineSearchForEdit('');
    setEditingMedicineRowIndex(null);

    // If targetRowIndex is provided, we're editing an existing row
    const isEditing = targetRowIndex !== null && targetRowIndex !== products.length;

    // If medicine has multiple batches, show batch selection modal
    if (medicine.batches.length > 1) {
      setSelectedMedicineForBatch(medicine);
      setCurrentProductIndex(isEditing ? targetRowIndex : products.length);
      setShowBatchModal(true);
    } else if (medicine.batches.length === 1) {
      // If only one batch, select it automatically (same expiry rules as batch modal)
      const selectedBatch = medicine.batches[0];
      const batchExpiry = selectedBatch.expiryDate ?? selectedBatch.ExpiryDate;

      if (isBatchExpired(batchExpiry)) {
        toast.error('Cannot select expired batch', { autoClose: 2000 });
        isSelectingMedicineRef.current = false;
        return;
      }

      if (isBatchExpiringSoonForBilling(batchExpiry)) {
        const formattedExpiry = formatExpiryDateForWarning(batchExpiry);
        const result = await Swal.fire({
          title: 'Expiry Warning',
          html: `This batch is expiring soon.<br/>Expiry Date: <strong>${formattedExpiry}</strong><br/><br/>Do you want to continue?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#4f46e5',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Yes, Continue',
          cancelButtonText: 'Cancel',
          customClass: {
            confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
            cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
            popup: 'rounded-xl shadow-xl'
          }
        });

        if (!result.isConfirmed) {
          isSelectingMedicineRef.current = false;
          return;
        }
      }

      const qty = isEditing ? parseFloat(products[targetRowIndex]?.qty || 0) : 0;
      const mrp = parseFloat(selectedBatch.mrp || 0);
      // Get the medicine's default discount (not global discount)
      const medicineDefaultDiscount = getDefaultDiscountString(medicine);
      // Use existing discount if editing, otherwise use global discount or default discount from medicine
      const discountStr = isEditing
        ? (products[targetRowIndex]?.discount || globalDiscount || medicineDefaultDiscount)
        : (globalDiscount || medicineDefaultDiscount);
      const calculated = calculateGstInclusive(mrp, qty, discountStr, selectedBatch.gstRate);
      const baseRate = mrp / (1 + (selectedBatch.gstRate / 100));

      const newProduct = {
        id: medicine.id,
        medicine: { value: medicine.id, label: medicine.name },
        batch: selectedBatch,
        rate: baseRate.toFixed(2),
        amount: calculated.amount.toFixed(2),
        qty: qty,
        discount: discountStr,
        defaultDiscount: medicineDefaultDiscount, // Store the original default discount from medicine
        discountAmount: calculated.discountAmount.toFixed(2),
        mrp: selectedBatch.mrp,
        gstAmount: calculated.gstAmount.toFixed(2),
        gstRate: selectedBatch.gstRate,
        totalAmount: calculated.totalAmount.toFixed(2),
        name: medicine.name,
        shelf: medicine.shelf || ''
      };

      if (isEditing) {
        // Update existing row
        const newProducts = [...products];
        newProducts[targetRowIndex] = newProduct;
        setProducts(newProducts);

        // Focus quantity field immediately - use double requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Ensure search input is still blurred
            if (medicineSearchInputRef.current) {
              medicineSearchInputRef.current.blur();
            }
            // Blur any other focused element
            if (document.activeElement && document.activeElement !== qtyRefs.current[targetRowIndex]) {
              document.activeElement.blur();
            }
            // Focus quantity field
            if (qtyRefs.current[targetRowIndex]) {
              qtyRefs.current[targetRowIndex].focus();
              qtyRefs.current[targetRowIndex].select();
            }
            // Reset selection flag after focus is set
            isSelectingMedicineRef.current = false;
          });
        });
      } else {
        // Check if there are any blank rows (rows without medicine) and replace the first one
        const blankRowIndex = products.findIndex(p => !p.medicine && !p.batch && !p.name);

        if (blankRowIndex !== -1) {
          // Replace the blank row with the new product
          const newProducts = [...products];
          newProducts[blankRowIndex] = newProduct;
          setProducts(newProducts);

          // Focus quantity field immediately - use double requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Ensure search input is still blurred
              if (medicineSearchInputRef.current) {
                medicineSearchInputRef.current.blur();
              }
              // Blur any other focused element
              if (document.activeElement && document.activeElement !== qtyRefs.current[blankRowIndex]) {
                document.activeElement.blur();
              }
              // Focus quantity field
              if (qtyRefs.current[blankRowIndex]) {
                qtyRefs.current[blankRowIndex].focus();
                qtyRefs.current[blankRowIndex].select();
              }
              // Reset selection flag after focus is set
              isSelectingMedicineRef.current = false;
            });
          });
        } else {
          // No blank rows, add to the end
          setProducts([...products, newProduct]);

          // Expand refs arrays and focus immediately
          requestAnimationFrame(() => {
            const newLength = products.length + 1;
            if (qtyRefs.current.length < newLength) {
              qtyRefs.current = [...qtyRefs.current, null];
            }
            if (rateRefs.current.length < newLength) {
              rateRefs.current = [...rateRefs.current, null];
            }
            if (discountRefs.current.length < newLength) {
              discountRefs.current = [...discountRefs.current, null];
            }

            // Use another requestAnimationFrame to ensure refs are set
            requestAnimationFrame(() => {
              // Ensure search input is still blurred
              if (medicineSearchInputRef.current) {
                medicineSearchInputRef.current.blur();
              }
              // Blur any other focused element
              const newIndex = products.length;
              if (document.activeElement && document.activeElement !== qtyRefs.current[newIndex]) {
                document.activeElement.blur();
              }
              // Focus quantity field directly
              if (qtyRefs.current[newIndex]) {
                qtyRefs.current[newIndex].focus();
                qtyRefs.current[newIndex].select();
              }
              // Reset selection flag after focus is set
              isSelectingMedicineRef.current = false;
            });
          });
        }
      }
    }
  };

  // Handle clicking on medicine name to edit
  const handleMedicineNameClick = (index, e) => {
    e.stopPropagation(); // Prevent row selection
    setEditingMedicineRowIndex(index);
    setMedicineSearchForEdit('');

    // Calculate dropdown position based on clicked cell
    setTimeout(() => {
      if (medicineNameCellRefs.current[index]) {
        setDropdownPosition(calculateDropdownPosition(medicineNameCellRefs.current[index]));
      }
    }, 50);
  };

  const calculateSubTotal = () => {
    // Subtotal is now the grand total (sum of all MRPs/totalAmounts)
    const total = products.reduce((sum, product) => sum + parseFloat(product.totalAmount || 0), 0);
    if (roundOff) {
      // Round to nearest integer
      return Math.round(total).toFixed(2);
    }
    return total.toFixed(2);
  };

  const calculateTotalGST = () => {
    // Calculate total GST included in all products
    return products.reduce((total, product) => total + parseFloat(product.gstAmount || 0), 0).toFixed(2);
  };

  const calculateTotalDiscount = () => {
    // Calculate total discount amount from all products
    return products.reduce((total, product) => total + parseFloat(product.discountAmount || 0), 0).toFixed(2);
  };

  const calculateTotalAmountBeforeDiscount = () => {
    // Calculate total amount before discount (sum of all amount fields)
    const total = products.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0);
    return total.toFixed(2);
  };

  const calculateGrandTotal = () => {
    // Grand total is the same as subtotal (sum of all MRPs/totalAmounts)
    return calculateSubTotal();
  };

  const calculateTotal = () => {
    // Alias for calculateGrandTotal for backward compatibility
    return calculateGrandTotal();
  };

  // Auto-fill paying amount with grand total when phpaymentMarkOnBillSave is true
  // useEffect(() => {
  //   if (generalsettings?.phpaymentMarkOnBillSave) {
  //     // Calculate grand total directly (same logic as calculateGrandTotal)
  //     const total = products.reduce((sum, product) => sum + parseFloat(product.totalAmount || 0), 0);
  //     const grandTotal = roundOff ? Math.round(total) : total;
  //     setPayingAmount(parseFloat(grandTotal).toFixed(2) || 0);
  //   }
  // }, [products, roundOff, generalsettings?.phpaymentMarkOnBillSave]);

  useEffect(() => {
    if (showBillPaymentModal && billPaymentSavedContext) {
      setBillPaymentModalAmount(billPaymentSavedContext.totalAmount);
      setBillPaymentModalMode('Cash');
      setBillPaymentModalSplit({ cash: 0, bank: 0 });
    }
  }, [showBillPaymentModal, billPaymentSavedContext]);

  const resetForm = async () => {
    // Use startTransition to batch all state updates for better performance
    startTransition(() => {
      // Clear all form state in one batch
      setEditInvoiceNumber('');
      setInvoiceNumber('');
      setBilledDate('');
      setPatients([]);
      setSelectedDoctor("");
      setMobileInput('');
      setSelectedPatient(null);
      setMedicines([]);
      setProducts([]);
      setIsButtonDisabled(false);
      setPayingAmount(0);
      setShowPaymentOptions(false);
      setPaymentMethod('cash');
      setRoundOff(false);
      setHalfQtyUsed(false);
      setSelectedIndex(null);
      setSearchOpnum('');
      loadedOpnumRef.current = '';
      setEdit('');
      setShowBatchModal(false);
      setSelectedMedicineForBatch(null);
      setCurrentProductIndex(null);
      setMedicineSearch('');
      setShowMedicineList(false);
      setSelectedMedicineIndex(-1);
      setEditingMedicineRowIndex(null);
      setMedicineSearchForEdit('');
      setShowPatientSidebar(false);
      setShowEmrHistoryModal(false);
      setEmrHistoryList([]);
      setPatientHistory([]);
      setExpandedBillId(null);
      setBillItems({});
      setBillCreatedDates({});
      setLoadingBillItems({});
      setLoadingPatientBillLoad(null);
      setEditNumber('');
      seteditName('');
      setEditAge('');
      setEditGender('');
      setGlobalDiscount('');
      setPrivilegeCardNumber('');
      setHasUnsavedChanges(false);
      setShowShareModal(false);
      setShareProductIndex(null);
      setShareQty('');
      setShowBillPaymentModal(false);
      setBillPaymentSavedContext(null);
      setBillPaymentModalAmount(0);
      setPrevBillDue(0);
      setBillPaymentModalMode('Cash');
      setBillPaymentModalSplit({ cash: 0, bank: 0 });
    });

    // Clear refs and cache synchronously
    medicinesCacheRef.current = {};
    isSavingRef.current = false;
    isSubmittingShareRef.current = false;
    hasUnsavedChangesRef.current = false;
    initialBillStateRef.current = null;
    productRefs.current = [];
    qtyRefs.current = [];
    rateRefs.current = [];
    discountRefs.current = [];
    medicineNameCellRefs.current = [];
    medicineRowRefs.current = [];

    // Set current billed date
    const billigDate = new Date();
    const newdate = formatBilledDate(billigDate);
    setBilledDate(newdate);

    await getSalesLoadData();

    // Focus on OP number input
    setTimeout(() => {
      setLoading(false);
      setIsButtonDisabled(false);
      opnumRef.current?.focus();
    }, 100);
  };


  const printKOT = async () => {
    // Block printing if there are unsaved changes in edit mode (check ref for synchronous access)
    if (editInvoiceNumber && hasUnsavedChangesRef.current) {
      toast.error('Please save the bill before printing. Changes have been made.', {
        autoClose: 2000,
      });
      return;
    }

    if (!isElectron()) {
      toast.error('Not Allowed', { duration: 1000 });
      return;
    }

    if (!sysInfo.pharmacyBillPriinter) {
      toast.error('Cannot Print,Printer Not Selected ', { duration: 1000 });
      return;
    }

    const kotDiv = document.getElementById('kotDiv');

    if (!kotDiv) {
      toast.error('KOT element not found', { duration: 1000 });
      return;
    }

    try {
      // Function to convert image to base64
      const getBase64Image = (imgPath) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          };
          img.onerror = reject;
          img.src = imgPath;
        });
      };

      // Convert the QR code image to base64
      let base64Image = '';
      try {
        base64Image = await getBase64Image(`${process.env.PUBLIC_URL}/general.png`);
      } catch (error) {
        console.warn('Failed to convert image to base64:', error);
      }

      // Clone the KOT div and replace the image source
      const kotClone = kotDiv.cloneNode(true);
      const imgElement = kotClone.querySelector('img');
      if (imgElement && base64Image) {
        imgElement.src = base64Image;
      }

      // Get the HTML content with base64 image
      const kotHTML = kotClone.outerHTML;

      // Enhanced CSS for printing with better image handling
      const printCSS = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
            color: #000;
            background: #fff;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
          
          #kotDiv {
            width: 302px;
            margin: 0 auto;
            padding: 8px;
            background: #fff;
            color: #000;
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
          }
          
          .text-xs { font-size: 11px; }
          .text-sm { font-size: 13px; }
          .text-lg { font-size: 14px; }
          .font-bold { font-weight: 700; }
          .font-extrabold { font-weight: 800; }

          /* Fixed width classes for table alignment */
          .w-\\[70\\%\\] { 
            width: 70% !important; 
            max-width: 70% !important;
          }
          .w-\\[15\\%\\] { 
            width: 15% !important; 
            max-width: 15% !important;
          }
          .w-full { 
            width: 100% !important; 
          }

          .w-16 { 
            width: 16px !important; 
            max-width: 16px !important;
          }
          .h-16 { 
            height: 16px !important; 
            max-height: 16px !important;
          }
          
          /* Enhanced image styling for printing */
          img {
            width: 70px !important;
            height: 70px !important;
            max-width: 70px !important;
            max-height: 70px !important;
            display: block !important;
            margin: 0 auto !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-left { text-align: left; }
          
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .flex-1 { flex: 1; }
          .items-center { align-items: center; }
          
          .mb-1 { margin-bottom: 4px; }
          .mb-2 { margin-bottom: 8px; }
          .mb-3 { margin-bottom: 12px; }
          .my-2 { margin: 8px 0; }
          .py-1 { padding: 4px 0; }
          .pt-2 { padding-top: 8px; }
          .pr-2 { padding-right: 8px; }
          .ml-4 { margin-left: 16px; }
          
          .border-t { border-top: 1px solid #000; }
          .border-b { border-bottom: 1px solid #000; }
          .border-dashed { border-style: dashed; }
          .border-black { border-color: #000; }
          
          .leading-tight { line-height: 1.1; }
          .whitespace-nowrap { white-space: nowrap; }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            #kotDiv {
              width: 302px !important;
              margin: 0 !important;
              padding: 8px !important;
            }
            
            img {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            /* Ensure table structure is maintained in print */
            .w-\\[70\\%\\] { 
              width: 70% !important; 
              max-width: 70% !important;
            }
            .w-\\[15\\%\\] { 
              width: 15% !important; 
              max-width: 15% !important;
            }
          }
        </style>
      `;

      // Set up the listener BEFORE sending the event
      ipcRenderer.once('kot-print-success', async (event, { status, error }) => {
        if (status === 'success') {
          toast.success('KOT printed successfully!', { duration: 1000 });
        } else {
          toast.error('KOT Print failed. Please check your printer settings.', {
            autoClose: 1000,
          });
          setLoading(false);
        }
      });

      // Send the HTML content for direct printing
      ipcRenderer.send('print-kot-html', {
        htmlContent: kotHTML,
        cssContent: printCSS,
        printer: sysInfo.pharmacyBillPriinter,
      });

    } catch (error) {
      toast.error('Failed to prepare KOT for printing.', {
        autoClose: 1000,
      });
    }
  };



  const generatePDF = () => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a5'
      });

      // Map values
      const firmName = (generalsettings?.FirstName).toUpperCase() + ' ' + (generalsettings?.LastName).toUpperCase();
      const address = (generalsettings?.AddressLine1 || '') + ',' + (generalsettings?.AddressLine2 || '') + ',' + (generalsettings?.Place || '');
      const phoneNo = `Phone No : ${generalsettings?.MobileNo1 || ''}, ${generalsettings?.MobileNo2 || ''}`;
      const gstNo = `GST No : ${generalsettings?.GstNo || ''}`;
      const dlNo = `DL No : ${generalsettings?.dlnum1 || ''}, ${generalsettings?.dlnum2 || ''}`;
      const invNo = formatPrintInvoiceNumber(invoiceNumber);
      const invDate = billedDate;
      const pName = editName || '';
      const docName = selectedDoctor?.DoctorName || selectedDoctor?.label || '';
      const userName = userInfo?.UserName || '';

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;

      // Calculate Footer Values
      const totalAmt = parseFloat(calculateGrandTotal());
      const totalDisc = parseFloat(products.reduce((acc, curr) => acc + parseFloat(curr.discountAmount || 0), 0));
      const totalGST = parseFloat(calculateTotalGST());
      const netTotal = parseFloat(totalAmt); // Use grand total, not paying amount
      const saved = totalDisc;
      const returnDays = generalsettings?.returnValidityDays || 7;
      const policy = `Return accepted within ${returnDays} days for unopened medicines along with the purchase receipt`;
      const amountInWords = numToWords(netTotal);

      // Define Table Columns and Rows
      const tableColumn = ["Sr", "Product Name", "Batch", "Qty", "Exp", "Mrp", "Disc", "GST%", "GST", "Total"];
      const tableRows = [];

      products.forEach((product, index) => {
        let exp = product.batch?.expiryDate || '';
        // Format expiry date to MM/YY format
        if (exp) {
          try {
            // Handle different date formats
            if (exp.includes('-')) {
              const parts = exp.split('-');
              if (parts.length === 3) {
                // Format: YYYY-MM-DD -> MM/YY
                const year = parts[0].substring(2); // Last 2 digits
                const month = parts[1].padStart(2, '0');
                exp = `${month}/${year}`;
              } else if (parts.length === 2) {
                // Format: MM-YY or YY-MM
                if (parts[0].length === 4) {
                  // YYYY-MM format
                  exp = `${parts[1]}/${parts[0].substring(2)}`;
                } else {
                  // MM-YY format
                  exp = `${parts[0]}/${parts[1]}`;
                }
              }
            } else if (exp.includes('/')) {
              // Already in MM/YY or similar format, keep as is
              const parts = exp.split('/');
              if (parts.length === 2 && parts[0].length <= 2 && parts[1].length <= 2) {
                // Ensure proper formatting
                exp = `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}`;
              }
            } else {
              // Try to parse as date
              const date = new Date(exp);
              if (!isNaN(date.getTime())) {
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = String(date.getFullYear()).slice(-2);
                exp = `${month}/${year}`;
              }
            }
          } catch (e) {
            // If parsing fails, keep original value
            console.warn('Failed to parse expiry date:', exp);
          }
        }

        // Format discount for display (show amount, not percentage string)
        let discDisplay = '0.00';
        if (product.discount) {
          const discStr = product.discount.toString().trim();
          if (discStr.toLowerCase().endsWith('p') || discStr.toLowerCase().endsWith('%')) {
            // Percentage - show the actual discount amount
            discDisplay = parseFloat(product.discountAmount || 0).toFixed(2);
          } else {
            // Fixed amount
            discDisplay = parseFloat(discStr || 0).toFixed(2);
          }
        }

        const row = [
          index + 1,
          product.name,
          product.batch?.batchNo ? String(product.batch.batchNo).slice(-6) : '',
          product.qty,
          exp,
          parseFloat(product.batch?.mrp || 0).toFixed(2),
          discDisplay,
          (product.gstRate || product.batch?.gstRate || 0) + '%',
          parseFloat(product.gstAmount || 0).toFixed(2),
          parseFloat(product.totalAmount || 0).toFixed(2)
        ];
        tableRows.push(row);
      });

      // Define Layout Constants
      // Increased footer height to prevent overlap
      const headerHeight = 115;
      const footerHeight = 100;

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: headerHeight,
        theme: 'plain',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          textColor: [0, 0, 0],
          font: 'helvetica'
        },
        headStyles: {
          fontStyle: 'bold',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          halign: 'center',
          cellPadding: 3
        },
        bodyStyles: {
          cellPadding: 2
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 25 }, // Sr
          1: { halign: 'left', cellWidth: 120 }, // Product Name
          2: { halign: 'left', cellWidth: 50 }, // Batch
          3: { halign: 'center', cellWidth: 30 }, // Qty
          4: { halign: 'center', cellWidth: 40 }, // Exp
          5: { halign: 'right', cellWidth: 50 }, // Mrp
          6: { halign: 'right', cellWidth: 45 }, // Disc
          7: { halign: 'right', cellWidth: 40 }, // GST%
          8: { halign: 'right', cellWidth: 45 }, // GST
          9: { halign: 'right', cellWidth: 55 } // Total
        },
        // Margins to ensure content doesn't overlap header/footer
        margin: { top: headerHeight, bottom: footerHeight, left: margin, right: margin },

        didDrawPage: (data) => {
          // ================= DRAW HEADER =================
          let y = 15; // Start a bit higher

          // Firm Name
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text(firmName, pageWidth / 2, y, { align: 'center' });
          y += 14;

          // Address
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          const addressLines = doc.splitTextToSize(address, pageWidth - 100);
          addressLines.forEach(line => {
            doc.text(line, pageWidth / 2, y, { align: 'center' });
            y += 10;
          });

          // Phone No
          doc.text(phoneNo, pageWidth / 2, y, { align: 'center' });
          y += 10;

          // GST No
          doc.text(gstNo, pageWidth / 2, y, { align: 'center' });
          y += 10;

          // DL No
          y += 2; // Small extra gap
          doc.text(dlNo, pageWidth / 2, y, { align: 'center' });
          y += 14;

          // Title
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text("PHARMACY CASH BILL", pageWidth / 2, y, { align: 'center' });
          y += 18;

          // Patient & Doctor (Left)
          doc.setFontSize(10);
          const infoStartY = y;
          doc.text(`Patient : ${pName}`, margin, y);
          y += 12; // Reduced line height
          doc.text(`Doctor : ${docName}`, margin, y);

          // Invoice & Date (Right)
          let rightY = infoStartY;
          doc.text(`Invoice No : ${invNo}`, pageWidth - margin, rightY, { align: 'right' });
          rightY += 12;
          doc.text(`Date : ${invDate}`, pageWidth - margin, rightY, { align: 'right' });

          // Line above table header 
          // Position it closer to the info block matching the reduced headerHeight
          const tableHeaderY = headerHeight - 5;
          doc.setLineWidth(0.5);
          doc.line(margin, tableHeaderY, pageWidth - margin, tableHeaderY);


          // ================= DRAW FOOTER =================
          // Calculate footer start position - line should be ABOVE all footer content
          const footerStartY = pageHeight - footerHeight;
          
          // Line above footer - draw it first, before any text
          doc.setLineWidth(0.5);
          doc.line(margin, footerStartY, pageWidth - margin, footerStartY);
          
          // Start footer content after the line with proper spacing
          let footerY = footerStartY + 10;

          // Left Side - Saved Amount, Amount in Words, Printed By
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.text(`You have saved : ${saved.toFixed(2)}`, margin, footerY);
          footerY += 13;

          // Amount in Words (split if too long)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          const amountWordsLines = doc.splitTextToSize(`Amount to Pay : ${amountInWords}`, pageWidth / 2 - margin - 20);
          amountWordsLines.forEach((line, idx) => {
            doc.text(line, margin, footerY + (idx * 11));
          });
          footerY += Math.max(13, amountWordsLines.length * 11);

          // Printed By
          doc.text(`Printed By : ${userName}`, margin, footerY);

          // Right Side - Totals Section (aligned with left side)
          let totalsY = footerStartY + 10; // Start at same Y as left side
          const rightStart = pageWidth - 180;

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);

          doc.text("Grand Total", rightStart, totalsY);
          doc.text(totalAmt.toFixed(2), pageWidth - margin, totalsY, { align: 'right' });
          totalsY += 13;

          doc.text("Total Discount", rightStart, totalsY);
          doc.text(totalDisc.toFixed(2), pageWidth - margin, totalsY, { align: 'right' });
          totalsY += 13;

          doc.text("Total GST", rightStart, totalsY);
          doc.text(totalGST.toFixed(2), pageWidth - margin, totalsY, { align: 'right' });
          totalsY += 13;

          doc.setFont('helvetica', 'bold');
          doc.text("Net Total", rightStart, totalsY);
          doc.text(netTotal.toFixed(2), pageWidth - margin, totalsY, { align: 'right' });

          // Policy (Bottom Center) - Positioned at the very bottom with minimal gap
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          // Position policy text at the bottom, ensuring it's below all footer content
          // Calculate the lowest point of footer content (left or right side)
          const leftSideBottom = footerY + 10; // Printed By + some space
          const rightSideBottom = totalsY + 13; // Net Total + some space
          const footerContentBottom = Math.max(leftSideBottom, rightSideBottom);
          // Position policy 8pt below the footer content, but at least 8pt from page bottom
          const policyY = Math.max(footerContentBottom, pageHeight - 8);
          doc.text(policy, pageWidth / 2, policyY, { align: 'center' });
        },
        didDrawHead: (data) => {
          // Draw line at bottom of header
          const headRow = data.table.head[0];
          const y = headRow.y + headRow.height;
          doc.setLineWidth(0.5);
          doc.line(margin, y, pageWidth - margin, y);
        }
      });

      doc.save(`Invoice_${invNo}.pdf`);

    } catch (error) {
      console.error(error);
      toast.error('Failed to generate PDF', { autoClose: 2000 });
    }
  };

  const handlePrint = async () => {
    if(userInfo?.Designation === 'Admin'){
      // Block printing if there are unsaved changes in edit mode (check ref for synchronous access)
      if (editInvoiceNumber && hasUnsavedChangesRef.current) {
        toast.error('Please save the bill before printing. Changes have been made.', {
          autoClose: 2000,
        });
        return;
      }

      const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';
      
      if (phBillType === 'KOT') {
        printKOT();
      } else {
        if (normalBillPrintRef.current) {
          await normalBillPrintRef.current.handleDotMatrixPrint();
        }
      }

    } else {
      if (editInvoiceNumber) {
        if(generalsettings?.billPrintonlyPaymentsave && prevBillDue > 0){
          toast.error('Please mark the payment before printing.', {
            autoClose: 2000,
          });
          return;
        } else {
          // Block printing if there are unsaved changes in edit mode (check ref for synchronous access)
          if (editInvoiceNumber && hasUnsavedChangesRef.current) {
            toast.error('Please save the bill before printing. Changes have been made.', {
              autoClose: 2000,
            });
            return;
          }
  
          const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';
          
          if (phBillType === 'KOT') {
            printKOT();
          } else {
            if (normalBillPrintRef.current) {
              await normalBillPrintRef.current.handleDotMatrixPrint();
            }
          }
        }
      } else {
        if(generalsettings?.billPrintonlyPaymentsave){
          toast.error('Please mark the payment before printing.', {
            autoClose: 2000,
          });
          return;
        } else {
          // Block printing if there are unsaved changes in edit mode (check ref for synchronous access)
          if (editInvoiceNumber && hasUnsavedChangesRef.current) {
            toast.error('Please save the bill before printing. Changes have been made.', {
              autoClose: 2000,
            });
            return;
          }
  
          const phBillType = sysInfo?.phbilltype || generalsettings?.phBillType || '';
          
          if (phBillType === 'KOT') {
            printKOT();
          } else {
            if (normalBillPrintRef.current) {
              await normalBillPrintRef.current.handleDotMatrixPrint();
            }
          }
        }  
      }
    }

  }


  const roundAmount = (total) => {
    if ((total - Math.floor(total)) < 0.5) {
      return Math.floor(total);
    } else {
      return Math.ceil(total);
    }
  };

  // Function to check if changes have been made to the loaded bill
  const checkForChanges = () => {
    if (!editInvoiceNumber || !initialBillStateRef.current) {
      return;
    }

    const initial = initialBillStateRef.current;
    const current = {
      products: products.map(p => ({
        id: p.id,
        qty: p.qty,
        discount: p.discount,
        batchNo: p.batch?.batchNo,
        totalAmount: p.totalAmount
      })),
      patientName: editName,
      mobileNo: editNumber,
      doctorId: selectedDoctor?.value,
      payingAmount: payingAmount,
      globalDiscount: globalDiscount
    };

    // Compare products
    const productsChanged = JSON.stringify(initial.products) !== JSON.stringify(current.products);

    // Compare other fields
    const otherFieldsChanged =
      initial.patientName !== current.patientName ||
      initial.mobileNo !== current.mobileNo ||
      initial.doctorId !== current.doctorId ||
      initial.payingAmount !== current.payingAmount ||
      initial.globalDiscount !== current.globalDiscount;

    const hasChanges = productsChanged || otherFieldsChanged;
    setHasUnsavedChanges(hasChanges);
    hasUnsavedChangesRef.current = hasChanges; // Update ref for synchronous access
  };

  // Derived payment figures for the mark-payment modal.
  const billOutstanding = toSafeNumber(billPaymentSavedContext?.totalAmount);
  const isSplitBillPayment = billPaymentModalMode === 'Split';
  const billSplitCash = toSafeNumber(billPaymentModalSplit.cash);
  const billSplitBank = toSafeNumber(billPaymentModalSplit.bank);
  const billPayingAmount = isSplitBillPayment
    ? Math.round((billSplitCash + billSplitBank) * 100) / 100
    : toSafeNumber(billPaymentModalAmount);
  const billSplitExceeds = isSplitBillPayment && billPayingAmount > billOutstanding;
  const billPaymentInvalid =
    billPayingAmount <= 0 ||
    billSplitExceeds ||
    (isSplitBillPayment && (billSplitCash <= 0 || billSplitBank <= 0));

  const handleBillPaymentModeChange = (mode) => {
    setBillPaymentModalMode(mode);
    if (mode === 'Split') {
      const seed = toSafeNumber(billPaymentModalAmount) || billOutstanding;
      setBillPaymentModalSplit({ cash: seed, bank: 0 });
    } else {
      setBillPaymentModalSplit({ cash: 0, bank: 0 });
    }
  };

  const updateBillSplitAmount = (field, value) => {
    const entered = Math.min(Math.max(toSafeNumber(value), 0), billOutstanding);
    setBillPaymentModalSplit((prev) => ({ ...prev, [field]: entered }));
  };

  const finalizeAfterBillPaymentModal = () => {
    setShowBillPaymentModal(false);
    setBillPaymentSavedContext(null);
    setBillPaymentModalAmount(0);
    setBillPaymentModalMode('Cash');
    setBillPaymentModalSplit({ cash: 0, bank: 0 });
    const shouldPrint = printAfterSave;
    if (shouldPrint) {
      handlePrint();
    }
    const delay = shouldPrint ? 1500 : 0;
    setTimeout(async () => {
      await getSalesLoadData();
      resetForm();
      setLoading(false);
      setIsButtonDisabled(false);
    }, delay);
  };

  // POST_SALES_INVOICE echoes the persisted keys back in data.data ({ Sequence, InvoiceNo, EditNo }).
  // Receipt posting must use those, never the local state copies (sequence/editNo can be stale).
  // Returns false when there is nothing left to collect, so the caller can go straight to printing.
  const openBillPaymentModalAfterSave = (savedInvoiceData, totalAmount, patientOverride = null) => {
    const saved = Array.isArray(savedInvoiceData) ? savedInvoiceData[0] : savedInvoiceData;
    const invoiceNo = saved?.InvoiceNo ?? editInvoiceNumber ?? invoiceNumber;
    const sequence = saved?.Sequence ?? header?.Sequence ?? (parseInt(localStorage.getItem('finac_year'), 10) || 0);
    const editNo = saved?.EditNo ?? header?.EditNo ?? 0;
    const outstanding = toSafeNumber(totalAmount);

    if (invoiceNo == null || invoiceNo === '' || outstanding <= 0) {
      return false;
    }

    setBillPaymentSavedContext({
      patientID: patientOverride?.PatientID ?? selectedPatient?.PatientID ?? 0,
      patientName: patientOverride?.PatientName ?? editName ?? nameRef.current?.value ?? '',
      invoiceNo,
      sequence,
      editNo,
      totalAmount: outstanding,
      transType: 'SI',
    });
    setBillPaymentModalAmount(outstanding);
    setBillPaymentModalMode('Cash');
    setBillPaymentModalSplit({ cash: 0, bank: 0 });
    setShowBillPaymentModal(true);
    setLoading(false);
    setIsButtonDisabled(false);
    return true;
  };

  const handleBillPaymentPost = async () => {
    if (!billPaymentSavedContext) {
      toast.error('No invoice data for payment', { autoClose: 1000 });
      return;
    }
    if (billPayingAmount <= 0) {
      toast.error('Please enter a valid payment amount', { autoClose: 1000 });
      return;
    }
    if (billPayingAmount > billOutstanding) {
      toast.error('Paying amount cannot exceed the balance due', { autoClose: 1000 });
      return;
    }
    if (isSplitBillPayment && (billSplitCash <= 0 || billSplitBank <= 0)) {
      toast.error('Enter both a cash and a bank amount for a split payment', { autoClose: 1500 });
      return;
    }
    try {
      setLoading(true);
      // Split rides on one transaction - both legs go in the same receipt.
      const receiptData = {
        patientID: billPaymentSavedContext.patientID,
        patientName: billPaymentSavedContext.patientName,
        cashAmount: isSplitBillPayment
          ? billSplitCash.toFixed(2)
          : (billPaymentModalMode === 'Cash' ? billPayingAmount.toFixed(2) : '0.00'),
        bankAmount: isSplitBillPayment
          ? billSplitBank.toFixed(2)
          : (billPaymentModalMode === 'Bank' ? billPayingAmount.toFixed(2) : '0.00'),
        payMode: billPaymentModalMode,
        drAmount: 0,
        crAmount: 0,
        lines: [{
          invoiceNo: parseInt(billPaymentSavedContext.invoiceNo),
          sequence: parseInt(billPaymentSavedContext.sequence) || 0,
          editNo: parseInt(billPaymentSavedContext.editNo) || 0,
          totalAmount: billOutstanding.toFixed(2),
          payingAmout: billPayingAmount.toFixed(2),
          transType: billPaymentSavedContext.transType,
        }]
      };

      const response = await axios.post(`${BASE_URL}${POST_PAYMENT}`, receiptData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Department': 'Pharmacy',
          'UserID': userInfo?.UserID,
        },
      });

      if (response.status === 200) {
        toast.success(`Payment of ₹${billPayingAmount.toLocaleString()} marked successfully!`, {
          autoClose: 2000
        });
        finalizeAfterBillPaymentModal();
      } else {
        toast.error('Something went wrong', { autoClose: 2000 });
        setLoading(false);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error('Error processing payment. Please try again.');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (editInvoiceNumber) {
      const originalBalanceDue = parseFloat(header?.BalanceDue ?? prevBillDue ?? 0);
      if (!Number.isFinite(originalBalanceDue) || originalBalanceDue <= 0) {
        toast.error('Payment already marked for this bill. Editing is not allowed.', { autoClose: 2500 });
        return;
      }
    }

    const result = await Swal.fire({
      title: 'Save Sales?',
      text: 'Are you sure you want to save this sales transaction?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, save it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
        popup: 'rounded-xl shadow-xl'
      }
    });

    // If user cancels, don't proceed
    if (!result.isConfirmed) {
      return;
    }

    // Prevent double submission
    if (isSavingRef.current) {
      return;
    }

    // Set saving flag immediately
    isSavingRef.current = true;
    setIsButtonDisabled(true);
    setLoading(true);

    // If OP number was changed without Enter/blur completing, load details before save
    const currentOp = (searchOpnum || '').trim();
    let patientForSave = selectedPatient;
    let doctorForSave = selectedDoctor;
    let nameForSave = editName || nameRef.current?.value;
    let numberForSave = editNumber;

    if (currentOp && currentOp !== String(loadedOpnumRef.current).trim()) {
      const opResult = await handleOpSearch({ focusNext: false });
      if (!opResult?.ok) {
        toast.error('Could not load OP details. Please check the OP number.', { autoClose: 2000 });
        setLoading(false);
        setIsButtonDisabled(false);
        isSavingRef.current = false;
        opnumRef.current?.focus();
        return;
      }
      if (opResult.patient) {
        patientForSave = opResult.patient;
        nameForSave = opResult.patient.PatientName || nameForSave;
        numberForSave = opResult.patient.MobileNo || numberForSave;
      }
      if (opResult.doctor) {
        doctorForSave = opResult.doctor;
      }
    }

    const currentName = nameForSave;

    if (currentName === '') {
      toast.error('Name is required.', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
      isSavingRef.current = false; // Reset flag on early return
      nameRef.current.focus();
      return;
    }

    const validProducts = products.filter(product => product.id && product.name);

    if (validProducts.length === 0) {
      toast.error('Please select medicines.', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
      isSavingRef.current = false; // Reset flag on early return
      if (productRefs.current[0]) {
        productRefs.current[0].focus();
      }
      return;
    }

    // Validate that all items have quantity greater than zero
    const productsWithZeroQty = validProducts.filter(product => {
      const qty = parseFloat(product.qty || 0);
      return qty <= 0;
    });

    if (productsWithZeroQty.length > 0) {
      const productNames = productsWithZeroQty.map(p => p.name || 'Unknown').join(', ');
      toast.error(`Zero qty found for medicines: ${productNames}`, { autoClose: 3000 });
      setLoading(false);
      setIsButtonDisabled(false);
      isSavingRef.current = false; // Reset flag on early return
      // Focus the first product with zero quantity
      const firstZeroQtyIndex = products.findIndex(p => {
        const qty = parseFloat(p.qty || 0);
        return qty <= 0 && p.id && p.name;
      });
      if (firstZeroQtyIndex !== -1 && qtyRefs.current[firstZeroQtyIndex]) {
        qtyRefs.current[firstZeroQtyIndex].focus();
        qtyRefs.current[firstZeroQtyIndex].select();
      }
      return;
    }

    // Helper function to parse decimal values (3 decimal places)
    const parseDecimal = (value) => parseFloat(parseFloat(value || 0).toFixed(3));

    // Calculate totals
    const totalAmountBeforeDiscount = parseDecimal(validProducts.reduce((sum, product) => sum + parseDecimal(product.amount || 0), 0));
    const totalDiscount = parseDecimal(validProducts.reduce((sum, product) => sum + parseDecimal(product.discountAmount || 0), 0));
    const totalTax = parseDecimal(calculateTotalGST());
    const netAmount = parseDecimal(calculateGrandTotal()); // Net amount is the grand total (after discount, inclusive of GST)
    const totalAmount = parseDecimal(totalAmountBeforeDiscount); // Total before discount

    // Calculate total purchase cost (for profit calculation)
    let totalPurchaseCost = parseDecimal(0);

    // Build items array with profit calculations
    const items = validProducts.map((product, index) => {
      const qty = parseDecimal(product.qty || 0);
      const purchasePrice = parseDecimal(product.batch?.purchasePrice || 0);
      const mrp = parseDecimal(product.batch?.mrp || 0);
      const salesPrice = mrp; // MRP is the sales price
      const discAmount = parseDecimal(product.discountAmount || 0);
      const discPercentage = parseDecimal(product.amount > 0 ? (discAmount / parseDecimal(product.amount || 1)) * 100 : 0);
      const taxAmount = parseDecimal(product.gstAmount || 0);
      const netTotal = parseDecimal(parseDecimal(product.totalAmount || 0) - parseDecimal(product.gstAmount || 0)); // After discount, inclusive of GST
      const total = parseDecimal(product.amount || 0); // Before discount
      const postTaxTotal = parseDecimal(netTotal + parseDecimal(product.gstAmount || 0)); // Same as netTotal since GST is inclusive

      // Calculate profit for this item
      const purchaseCost = parseDecimal(purchasePrice * qty);
      const gpAmount = parseDecimal(netTotal - purchaseCost); // Profit = Sales (after discount) - Purchase cost
      const gpPercentage = parseDecimal(purchaseCost > 0 ? (gpAmount / purchaseCost) * 100 : 0);

      // Add to total purchase cost
      totalPurchaseCost = parseDecimal(totalPurchaseCost + purchaseCost);

      return {
        SI_No: index + 1,
        ItemNo: product.id || 0,
        ItemName: product.name || '',
        TaxCode: String(product.batch?.gstRate || 0),
        BatchCode: product.batch?.batchNo || '',
        Qty: qty,
        Foc: 0, // FOC quantity if applicable
        SalesPrice: parseDecimal(salesPrice - (discAmount / qty)),
        PurchasePrice: purchasePrice,
        Mrp: mrp,
        Total: total,
        Disc_Percentage: discPercentage,
        Disc_Amount: discAmount,
        TaxAmount: taxAmount,
        NetTotal: netTotal,
        PostTaxTotal: postTaxTotal,
        GP_Percentage: gpPercentage,
        GP_Amount: gpAmount,
        Onhand: parseDecimal(product.batch?.availableQty || 0),
        DepartmentID: localStorage.getItem('department_id') || ''
      };
    });

    // Calculate overall profit for the bill
    const gpAmount = parseDecimal(netAmount - totalPurchaseCost); // Overall profit = Total sales (after discount) - Total purchase cost
    const gpPercentage = parseDecimal(totalPurchaseCost > 0 ? (gpAmount / totalPurchaseCost) * 100 : 0);
    const discountPercentage = parseDecimal(totalAmountBeforeDiscount > 0 ? (totalDiscount / totalAmountBeforeDiscount) * 100 : 0);

    // Get current date in local timezone (IST)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const entryDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD (in local timezone)

    const markPaymentOnSave = !!generalsettings?.phpaymentMarkOnBillSave;

    // Calculate payment amounts based on payment method (invoice saved with no allocation when posting receipt on bill save)
    let payingAmt = parseDecimal(payingAmount) || 0;
    let cashAmount = 0;
    let cardAmount = 0;
    let bankAmount = 0;

    if (markPaymentOnSave) {
      payingAmt = 0;
      cashAmount = 0;
      cardAmount = 0;
      bankAmount = 0;
    } else if (payingAmt > 0) {
      if (paymentMethod === 'cash') {
        cashAmount = payingAmt;
      } else if (paymentMethod === 'card') {
        cardAmount = payingAmt;
      } else if (paymentMethod === 'upi') {
        bankAmount = payingAmt;
      }
    }

    let gTotal = parseDecimal(totalAmount) - parseDecimal(totalDiscount);

    let roundOffAmount = roundAmount(gTotal);

    const billTotalForPayment = generalsettings?.phValueRoundOff
      ? parseDecimal(roundAmount(netAmount))
      : parseDecimal(netAmount);

    // Build payload
    const payload = {
      PatientID: patientForSave?.PatientID || 0,
      OpNo: parseDecimal(searchOpnum) || 0,
      RefDoctorID: doctorForSave?.value || 0,
      EntryDate: entryDate,
      TotalAmount: generalsettings?.phValueRoundOff
        ? parseDecimal(roundOffAmount)
        : parseDecimal(totalAmount) - parseDecimal(totalDiscount), // Total before discount
      NetAmount: parseDecimal(netAmount - totalTax), // Net amount after discount (grand total)
      TaxAmount: parseDecimal(totalTax),
      Discount_Percentage: discountPercentage,
      DiscountAmount: parseDecimal(totalDiscount),
      GpAmount: gpAmount,
      GP_Percentage: gpPercentage,
      CashAmount: parseDecimal(cashAmount),
      CardAmount: parseDecimal(cardAmount),
      BankAmount: parseDecimal(bankAmount),
      BalanceDue: markPaymentOnSave
        ? (generalsettings?.phValueRoundOff
          ? parseDecimal(roundAmount(netAmount))
          : parseDecimal(netAmount))
        : (generalsettings?.phValueRoundOff
          ? parseDecimal(roundAmount(netAmount) - payingAmt)
          : parseDecimal(netAmount - payingAmt)),
      PatientName: nameForSave,
      MobileNo: numberForSave,
      Items: items
    };

    try {

      if (editInvoiceNumber) {

        const response = await axios.post(`${BASE_URL}${POST_SALES_INVOICE}`, payload, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'Sequence': header.Sequence,
            'InvoiceNo': header.InvoiceNo,
            'EditNo': header.EditNo,
            'DocStatus': 'U',
          }
        });

        if (response.status === 200) {
          isSavingRef.current = false;

          // Reset change tracking immediately after successful save (before print)
          initialBillStateRef.current = {
            products: products.map(p => ({
              id: p.id,
              qty: parseFloat(p.qty || 0),
              discount: p.discount || '',
              batchNo: p.batch?.batchNo || '',
              totalAmount: parseFloat(p.totalAmount || 0)
            })),
            patientName: editName || '',
            mobileNo: editNumber || '',
            doctorId: selectedDoctor?.value || 0,
            payingAmount: parseFloat(payingAmount || 0),
            globalDiscount: globalDiscount || ''
          };
          setHasUnsavedChanges(false);
          hasUnsavedChangesRef.current = false;

          toast.success('Pharmacy Bill Created Successfully', { autoClose: 1000 });

          if (markPaymentOnSave) {
            const paymentModalOpened = openBillPaymentModalAfterSave(response.data?.data, billTotalForPayment, {
              PatientID: patientForSave?.PatientID || 0,
              PatientName: nameForSave || '',
            });
            if (paymentModalOpened) {
              return;
            }
          }

          if (printAfterSave) {
            handlePrint();
          }

          await getSalesLoadData();
          setTimeout(() => {
            resetForm();
          }, 2000);
        } else {
          setLoading(false);
          setIsButtonDisabled(false);
          isSavingRef.current = false; // Reset flag on error
          throw new Error('Failed to save sales entry');
        }
      } else {

        try {
          const response = await axios.get(`${BASE_URL}${GET_SALES_LOAD_DATA}`, {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`
            },
          });

          if (response.status === 200) {
            const data = response.data.data;

            // Set invoice number from nextNum
            if (data.nextNum) {
              setInvoiceNumber(data.nextNum);
              setNewInvoiceNumber(data.nextNum);
            }

          }
        } catch (error) {
          toast.error('Failed to fetch sales load data.', {
            autoClose: 1000,
          });
        }

        const response = await axios.post(`${BASE_URL}${POST_SALES_INVOICE}`, payload, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'Sequence': 0,
            'InvoiceNo': 0,
            'EditNo': 0,
            'DocStatus': 'O',
          }
        });

        if (response.status === 200) {
          isSavingRef.current = false;
          toast.success('Pharmacy Bill Created Successfully', { autoClose: 1000 });

          if (markPaymentOnSave) {
            const paymentModalOpened = openBillPaymentModalAfterSave(response.data?.data, billTotalForPayment, {
              PatientID: patientForSave?.PatientID || 0,
              PatientName: nameForSave || '',
            });
            if (paymentModalOpened) {
              return;
            }
          }

          if (printAfterSave) {
            handlePrint();
          }
          setTimeout(() => {
            resetForm();
          }, 2000);
          
        } else {
          setLoading(false);
          setIsButtonDisabled(false);
          isSavingRef.current = false; // Reset flag on error
          throw new Error('Failed to save sales entry');
        }
      }
    } catch (error) {
      toast.error('Failed to save sales entry.', { autoClose: 2000 });
      setLoading(false);
      setIsButtonDisabled(false);
      isSavingRef.current = false; // Reset flag on catch
    }

  };

  // Update refs with latest values
  useEffect(() => {
    isButtonDisabledRef.current = isButtonDisabled;
    handleSaveRef.current = handleSave;
  }, [isButtonDisabled, handleSave]);

  useEffect(() => {
    showBillPaymentModalRef.current = showBillPaymentModal;
  }, [showBillPaymentModal]);

  // Keyboard shortcut for Alt+S to save
  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      // Check for Alt+S or Alt+s (case insensitive)
      if (e.altKey && (e.key === 's' || e.key === 'S')) {
        // Don't trigger if user is typing in an input, textarea, or select
        const target = e.target;

        // Only trigger if not in an input field and save is not disabled
        if (!isButtonDisabledRef.current && !isSavingRef.current && !showBillPaymentModalRef.current) {
          e.preventDefault();
          e.stopPropagation();
          // Call the latest handleSave function from ref
          if (handleSaveRef.current) {
            handleSaveRef.current();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';  // Return empty string if dateString is null or undefined

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {  // Check if the date is invalid
      return '';  // Return empty string if invalid date
    }

    // Use local timezone instead of UTC to preserve IST date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;  // Return the formatted date if valid (YYYY-MM-DD in local timezone)
  };

  const transformInvoiceItemsToMedicines = (items = []) => {
    return items
      .map((item) => {
        const itemBatches = item.itemBatch || [];
        if (!itemBatches || itemBatches.length === 0) {
          return null;
        }

        const batches = itemBatches.map((batch) => ({
          batchNo: batch.batchCode || '',
          expiryDate: batch.expiryDate || '',
          mrp: parseFloat(batch.salesPrice || 0),
          rate: parseFloat(batch.salesPrice || 0),
          gstRate: parseFloat(item.taxCode || 0),
          availableQty: parseFloat(batch.onhand || 0),
          purchasePrice: parseFloat(batch.purchasePrice || 0),
          sgst: 0,
          cgst: 0,
          totalWithGst: 0
        }));

        const hasStock = batches.some((batch) => parseFloat(batch.availableQty || 0) > 0);
        if (!hasStock) {
          return null;
        }

        return {
          id: item.item_No,
          name: item.item_Name || '',
          genericName: item.chemical || '',
          manufacturer: item.company || '',
          batches,
          section: item.section || '',
          hsnCode: item.hsnCode || '',
          discount1: item.discount1 || 0,
          discount2: item.discount2 || 0,
          discount3: item.discount3 || 0,
          itemStock: item.itemStock || 0,
          shelf: item.shelf || ''
        };
      })
      .filter((med) => med !== null);
  };

  const fetchMedicineByFilter = async (filterText) => {
    if (!filterText) return [];

    const response = await axios.get(`${BASE_URL}${GET_INVOICE_ITEMS}`, {
      headers: {
        'XApiKey': process.env.REACT_APP_API_KEY,
        'Authorization': `Bearer ${token}`,
      },
      params: {
        FilterText: filterText,
        ItemType: 'Pharmacy',
        FltrType: generalsettings.PhMedicineFilterType || 'all',
        DepartmentID: localStorage.getItem('department_id') || ''
      }
    });

    if (response.status !== 200) return [];
    return transformInvoiceItemsToMedicines(response?.data?.data || []);
  };

  const parseJsonArraySafe = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getPrescriptionCountFromEmr = (emrRow) => {
    return parseJsonArraySafe(emrRow?.Presecriptions).length;
  };

  const formatEmrDateTime = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '-';
    }
  };

  const getDefaultBatchForPrescription = (medicine) => {
    const validBatches = (medicine?.batches || [])
      .filter((batch) => parseFloat(batch.availableQty || 0) > 0)
      .filter((batch) => !isBatchExpired(batch.expiryDate));

    if (validBatches.length === 0) return null;
    return validBatches[0];
  };

  const loadProductsFromLatestEmr = async (latestEmrData) => {
    const prescriptions = parseJsonArraySafe(latestEmrData?.Presecriptions);
    if (!prescriptions.length) {
      toast.info('Latest EMR has no prescription items.', { autoClose: 1200 });
      return;
    }

    const medicineMap = {};
    for (const prescription of prescriptions) {
      const medicineId = String(prescription?.medicineId || '').trim();
      const medicineName = String(prescription?.medicine || '').trim();

      let medicine = null;
      if (medicineId) {
        const byId = await fetchMedicineByFilter(medicineId);
        medicine = byId.find((m) => String(m.id) === medicineId) || byId[0] || null;
      }

      if (!medicine && medicineName) {
        const byName = await fetchMedicineByFilter(medicineName);
        const nameLower = medicineName.toLowerCase();
        medicine = byName.find((m) => String(m.name || '').toLowerCase() === nameLower)
          || byName.find((m) => String(m.name || '').toLowerCase().includes(nameLower))
          || byName[0]
          || null;
      }

      if (medicine) {
        medicineMap[String(medicine.id)] = medicine;
      }
    }

    const fetchedMedicines = Object.values(medicineMap);
    if (fetchedMedicines.length > 0) {
      setMedicines((prev) => {
        const merged = [...prev];
        fetchedMedicines.forEach((med) => {
          if (!merged.find((m) => String(m.id) === String(med.id))) {
            merged.push(med);
          }
          medicinesCacheRef.current[med.id] = med;
        });
        return merged;
      });
    }

    const importedProducts = [];
    const skippedItems = [];

    for (const prescription of prescriptions) {
      const medicineId = String(prescription?.medicineId || '').trim();
      const medicineName = String(prescription?.medicine || '').trim();

      let matchedMedicine = null;
      if (medicineId) {
        matchedMedicine = medicineMap[medicineId] || null;
      }
      if (!matchedMedicine && medicineName) {
        const nameLower = medicineName.toLowerCase();
        matchedMedicine = fetchedMedicines.find((m) => String(m.name || '').toLowerCase() === nameLower)
          || fetchedMedicines.find((m) => String(m.name || '').toLowerCase().includes(nameLower))
          || null;
      }

      if (!matchedMedicine) {
        skippedItems.push(medicineName || medicineId || 'Unknown medicine');
        continue;
      }

      const selectedBatch = getDefaultBatchForPrescription(matchedMedicine);
      if (!selectedBatch) {
        skippedItems.push(medicineName || matchedMedicine.name || medicineId);
        continue;
      }

      const qty = Math.max(0, parseFloat(prescription?.quantity || 0));
      const mrp = parseFloat(selectedBatch.mrp || 0);
      const medicineDefaultDiscount = getDefaultDiscountString(matchedMedicine);
      const discountStr = globalDiscount || medicineDefaultDiscount;
      const calculated = calculateGstInclusive(mrp, qty, discountStr, selectedBatch.gstRate);
      const baseRate = mrp / (1 + (selectedBatch.gstRate / 100));

      importedProducts.push({
        id: matchedMedicine.id,
        medicine: { value: matchedMedicine.id, label: matchedMedicine.name },
        batch: selectedBatch,
        rate: baseRate.toFixed(2),
        amount: calculated.amount.toFixed(2),
        qty,
        discount: discountStr,
        defaultDiscount: medicineDefaultDiscount,
        discountAmount: calculated.discountAmount.toFixed(2),
        mrp: selectedBatch.mrp,
        gstAmount: calculated.gstAmount.toFixed(2),
        gstRate: selectedBatch.gstRate,
        totalAmount: calculated.totalAmount.toFixed(2),
        name: matchedMedicine.name,
        shelf: matchedMedicine.shelf || ''
      });
    }

    if (importedProducts.length > 0) {
      setProducts((prev) => {
        const cleaned = prev.filter((p) => p?.medicine || p?.batch || p?.name);
        return [...cleaned, ...importedProducts];
      });
      toast.success(`Loaded ${importedProducts.length} EMR prescription item(s).`, { autoClose: 1200 });
    }

    if (skippedItems.length > 0) {
      toast.warning(`Skipped ${skippedItems.length} item(s): ${skippedItems.slice(0, 3).join(', ')}`, { autoClose: 2000 });
    }
  };

  const loadProductsFromPatientHistory = async (historyItems, invoiceNo) => {
    if (!historyItems || historyItems.length === 0) {
      toast.info('No items to load.', { autoClose: 1200 });
      return;
    }

    setLoadingPatientBillLoad(invoiceNo);
    try {
      const importedProducts = [];
      const skippedItems = [];
      const fetchedMedicines = [];

      for (const item of historyItems) {
        const itemId = String(item?.ItemNo || '').trim();
        const itemName = String(item?.ItemName || '').trim();

        let matchedMedicine = medicinesCacheRef.current[itemId]
          || medicines.find((m) => String(m.id) === itemId)
          || null;

        if (!matchedMedicine && itemId) {
          const byId = await fetchMedicineByFilter(itemId);
          matchedMedicine = byId.find((m) => String(m.id) === itemId) || byId[0] || null;
        }

        if (!matchedMedicine && itemName) {
          const byName = await fetchMedicineByFilter(itemName);
          const nameLower = itemName.toLowerCase();
          matchedMedicine = byName.find((m) => String(m.name || '').toLowerCase() === nameLower)
            || byName.find((m) => String(m.name || '').toLowerCase().includes(nameLower))
            || byName[0]
            || null;
        }

        if (!matchedMedicine) {
          skippedItems.push(itemName || itemId || 'Unknown item');
          continue;
        }

        fetchedMedicines.push(matchedMedicine);
        medicinesCacheRef.current[matchedMedicine.id] = matchedMedicine;

        const selectedBatch = getDefaultBatchForPrescription(matchedMedicine);
        if (!selectedBatch) {
          skippedItems.push(itemName || matchedMedicine.name || itemId);
          continue;
        }

        const qty = 1;
        const mrp = parseFloat(selectedBatch.mrp || 0);
        const medicineDefaultDiscount = getDefaultDiscountString(matchedMedicine);
        const discountStr = globalDiscount || medicineDefaultDiscount;
        const calculated = calculateGstInclusive(mrp, qty, discountStr, selectedBatch.gstRate);
        const baseRate = mrp / (1 + (selectedBatch.gstRate / 100));

        importedProducts.push({
          id: matchedMedicine.id,
          medicine: { value: matchedMedicine.id, label: matchedMedicine.name },
          batch: selectedBatch,
          rate: baseRate.toFixed(2),
          amount: calculated.amount.toFixed(2),
          qty,
          discount: discountStr,
          defaultDiscount: medicineDefaultDiscount,
          discountAmount: calculated.discountAmount.toFixed(2),
          mrp: selectedBatch.mrp,
          gstAmount: calculated.gstAmount.toFixed(2),
          gstRate: selectedBatch.gstRate,
          totalAmount: calculated.totalAmount.toFixed(2),
          name: matchedMedicine.name,
          shelf: matchedMedicine.shelf || ''
        });
      }

      if (fetchedMedicines.length > 0) {
        setMedicines((prev) => {
          const merged = [...prev];
          fetchedMedicines.forEach((med) => {
            if (!merged.find((m) => String(m.id) === String(med.id))) {
              merged.push(med);
            }
          });
          return merged;
        });
      }

      if (importedProducts.length > 0) {
        setProducts((prev) => {
          const cleaned = prev.filter((p) => p?.medicine || p?.batch || p?.name);
          return [...cleaned, ...importedProducts];
        });
        toast.success(`Loaded ${importedProducts.length} item(s) from bill.`, { autoClose: 1200 });
      } else {
        toast.info('No items could be loaded.', { autoClose: 1200 });
      }

      if (skippedItems.length > 0) {
        toast.warning(`Skipped ${skippedItems.length} item(s): ${skippedItems.slice(0, 3).join(', ')}`, { autoClose: 2000 });
      }
    } catch (error) {
      toast.error('Failed to load bill items.', { autoClose: 1000 });
    } finally {
      setLoadingPatientBillLoad(null);
    }
  };

  const handleBatchCellClick = async (index) => {
    const product = products[index];
    if (!product || !product.medicine) return;

    const medicineId = String(product.medicine?.value || product.id || '').trim();
    const medicineLabel = String(product.medicine?.label || product.name || '').trim();

    let medicineData = medicines.find((m) => String(m.id) === medicineId)
      || medicinesCacheRef.current[medicineId]
      || null;

    if (!medicineData && medicineId) {
      const byId = await fetchMedicineByFilter(medicineId);
      medicineData = byId.find((m) => String(m.id) === medicineId) || byId[0] || null;
    }

    if (!medicineData && medicineLabel) {
      const byName = await fetchMedicineByFilter(medicineLabel);
      const labelLower = medicineLabel.toLowerCase();
      medicineData = byName.find((m) => String(m.name || '').toLowerCase() === labelLower)
        || byName.find((m) => String(m.name || '').toLowerCase().includes(labelLower))
        || byName[0]
        || null;
    }

    if (!medicineData || !Array.isArray(medicineData.batches) || medicineData.batches.length === 0) {
      toast.info('No batch available for this item.', { autoClose: 1200 });
      return;
    }

    medicinesCacheRef.current[medicineData.id] = medicineData;
    setSelectedMedicineForBatch(medicineData);
    setCurrentProductIndex(index);
    setShowBatchModal(true);
  };

  const handleOpSearch = async ({ focusNext = true } = {}) => {
    const opToLoad = (searchOpnum || '').trim();

    if (opToLoad === '') {
      loadedOpnumRef.current = '';
      if (focusNext) {
        mobileInputRef.current?.focus();
      }
      return { ok: true };
    }

    // Already loaded this OP — skip refetch
    if (opToLoad === String(loadedOpnumRef.current).trim()) {
      if (focusNext) {
        const lastIndex = products.length - 1;
        if (productRefs.current[lastIndex]) {
          productRefs.current[lastIndex].focus();
        }
      }
      return { ok: true, alreadyLoaded: true };
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_OP_DETAILS}`, {
        headers: {
          'Sequence': 0,
          'InvoiceNo': opToLoad,
          'EditNo': 0,
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      const opNo = opToLoad;
      const fromDate = '';
      const toDate = '';


      const response1 = await axios.get(`${BASE_URL}${GET_EMR_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${token}`,
        },
        params: { opNo, fromDate, toDate },
      });


      if (response.status === 200) {
        setLoading(false)

        setSearchOpnum(opToLoad)
        loadedOpnumRef.current = opToLoad;

        const patientData = response.data.data[0]
        const doctorobj = doctors_options.find(doctor => doctor.value === patientData.Ref_DoctorID);

        const patient = {
          PatientID: patientData.PatientID,
          PatientName: patientData.PatientName,
          Age: patientData.Age,
          Place: patientData.Place,
          MobileNo: patientData.MobileNo,
          DOB: formatDate(patientData.DOB),
          Month: patientData.Month,
          Days: patientData.Days,
          Gender: patientData.Gender
        };

        setSelectedPatient(patient)
        setMobileInput(patientData.MobileNo || "");
        setSelectedDoctor(doctorobj)

        if (focusNext) {
          const lastIndex = products.length - 1;
          if (productRefs.current[lastIndex]) {
            productRefs.current[lastIndex].focus();
          }
        }

        const emrRows = Array.isArray(response1?.data?.data) ? response1.data.data : [];
        const currentOpNo = parseInt(opNo, 10);
        const emrForCurrentOp = emrRows.filter((row) => parseInt(row?.OPNo, 10) === currentOpNo);
        const validEmrRows = [...emrForCurrentOp]
          .filter((row) => getPrescriptionCountFromEmr(row) > 0)
          .sort((a, b) => (parseInt(b?.EditNo, 10) || 0) - (parseInt(a?.EditNo, 10) || 0));

        setEmrHistoryList(validEmrRows);
        return { ok: true, patient, doctor: doctorobj };
      }
      return { ok: false };
    } catch (error) {
      toast.error('Failed to fetch patient data.', {
        autoClose: 1000,
      });
      if (focusNext) {
        mobileInputRef.current?.focus();
      }
      return { ok: false };
    } finally {
      setLoading(false);
    }

  }

  // Reload OP details when user leaves the field after changing the number
  const handleOpnumBlur = () => {
    const current = (searchOpnum || '').trim();
    if (!current) {
      loadedOpnumRef.current = '';
      return;
    }
    if (current === String(loadedOpnumRef.current).trim()) {
      return;
    }
    handleOpSearch({ focusNext: false });
  };

  const getItemDatasparms = async (prevnum) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          Sequence: localStorage.getItem('finac_year'),
          InvoiceNo: prevnum.toString(),
        }
      });

      if (response.status === 200) {

        // Check if invoice date is today - only allow editing today's bills

     
        const invoiceData = response.data.data;
        setHeader(invoiceData.header);
        setEditInvoiceNumber(invoiceData.header.InvoiceNo.toString());
        setPrevBillDue(invoiceData.header.BalanceDue || 0);

        if (!invoiceData || !invoiceData.header || !invoiceData.details) {
          toast.error('Invalid invoice data received.', { autoClose: 1000 });
          setLoading(false);
          return;
        }

        const { header, details, itemList } = invoiceData;


        const date = new Date(header.CreatedDate);
        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric",
        };

        let formatted = date.toLocaleString("en-GB", options)
          .replace(",", "")              // remove comma
          .replace(/\s/g, "-")           // replace spaces with hyphens temporarily
          .replace(/-(\d{4})-/, "-$1 ")  // fix year spacing
          .replace(/(\d{2})-(\w{3})-(\d{4})/, "$1-$2-$3"); // ensure proper date format

        setBilledDate(header.CreatedDate);

        // Set invoice number
        if (header.InvoiceNo) {
          setInvoiceNumber(header.InvoiceNo.toString());
        }

        // Set OP Number
        if (header.OPNo) {
          const opFromInvoice = header.OPNo.toString();
          setSearchOpnum(opFromInvoice);
          loadedOpnumRef.current = opFromInvoice;
        } else {
          loadedOpnumRef.current = '';
        }

        // Set patient information
        if (header.PatientID && header.PatientName) {
          // First set basic patient data from header
          const patientData = {
            PatientID: header.PatientID,
            PatientName: header.PatientName || '',
            MobileNo: '', // Will be fetched if needed
            Age: '',
            Place: '',
            Gender: '',
            DOB: ''
          };

          setSelectedPatient(patientData);
          seteditName(header.PatientName || '');

          // Try to fetch full patient details by searching with PatientName
          // Then verify PatientID matches to ensure we get the correct patient
          try {
            const patientResponse = await axios.get(`${BASE_URL}${GET_PATIENTS}`, {
              headers: {
                'PageNo': 1,
                'PageSize': 100,
                'FilterText': header.PatientName,
                'XApiKey': process.env.REACT_APP_API_KEY,
                'Authorization': `Bearer ${token}`
              },
            });

            if (patientResponse.status === 200 && patientResponse.data.data.masterData.length > 0) {
              // Find exact match by PatientID to ensure we get the correct patient
              const exactMatch = patientResponse.data.data.masterData.find(
                p => p.PatientID === header.PatientID
              );

              if (exactMatch) {
                // Only update if we found the exact match by PatientID
                setSelectedPatient(exactMatch);
                seteditName(exactMatch.PatientName || '');
                setEditNumber(exactMatch.MobileNo || '');
                setEditAge(exactMatch.Age || '');
                setEditGender(exactMatch.Gender || '');
                setMobileInput(exactMatch.MobileNo || '');
              }
              // If no exact match found, keep the header data (don't update with wrong patient)
            }
          } catch (error) {
            console.error('Error fetching patient details:', error);
            // On error, keep the header data - don't update with wrong patient
          }
        } else {
          seteditName(header.PatientName || '');
          setEditNumber(header.MobileNo || '');
        }

        // Set doctor
        if (header.RefDoctorID) {
          const doctorObj = doctors_options.find(doctor => doctor.value === header.RefDoctorID);
          if (doctorObj) {
            setSelectedDoctor(doctorObj);
          } else {
            // If doctor not found in current list, create a temporary option
            setSelectedDoctor({
              value: header.RefDoctorID,
              label: header.DoctorName || 'Unknown Doctor',
              DoctorName: header.DoctorName || 'Unknown Doctor'
            });
          }
        }

        // Transform details into products format
        const transformedProducts = [];

        // Create a map of itemList for quick lookup
        const itemListMap = {};
        if (itemList && Array.isArray(itemList)) {
          itemList.forEach(item => {
            // Parse ItemBatch JSON string
            let batches = [];
            try {
              if (item.ItemBatch && typeof item.ItemBatch === 'string') {
                batches = JSON.parse(item.ItemBatch);
              } else if (Array.isArray(item.ItemBatch)) {
                batches = item.ItemBatch;
              }
            } catch (error) {
              batches = [];
            }

            itemListMap[item.Item_No] = {
              ...item,
              parsedBatches: batches
            };
          });
        }

        // Process each detail item
        details.forEach((detail, index) => {
          const itemInfo = itemListMap[detail.ItemNo];

          if (!itemInfo) {
            return;
          }

          // Find the batch that matches the BatchCode
          let selectedBatch = null;
          if (itemInfo.parsedBatches && itemInfo.parsedBatches.length > 0) {
            selectedBatch = itemInfo.parsedBatches.find(
              batch => batch.BatchCode === detail.BatchCode
            );

            // If exact batch not found, use the first batch
            if (!selectedBatch && itemInfo.parsedBatches.length > 0) {
              selectedBatch = itemInfo.parsedBatches[0];
            }
          }

          // If still no batch found, create a dummy batch from detail data
          if (!selectedBatch) {
            selectedBatch = {
              BatchCode: detail.BatchCode || '',
              ExpiryDate: '', // Not available in detail
              Onhand: detail.Onhand || 0,
              SalesPrice: detail.SalesPrice || detail.MRP || 0,
              PurchasePrice: detail.PurchasePrice || 0
            };
          }

          // Calculate available stock: current stock + sold quantity (since stock is after sale)
          const soldQty = parseFloat(detail.Qty || 0);
          const currentStock = parseFloat(detail.Onhand || 0);
          const availableQty = currentStock + soldQty;

          // Update the batch with correct available quantity
          const batchWithStock = {
            batchNo: selectedBatch.BatchCode || detail.BatchCode || '',
            expiryDate: selectedBatch.ExpiryDate || '',
            mrp: parseFloat(detail.MRP || detail.SalesPrice || 0),
            rate: parseFloat(detail.SalesPrice || detail.MRP || 0),
            gstRate: parseFloat(detail.TaxCode || itemInfo.TaxCode || 0),
            availableQty: availableQty,
            purchasePrice: parseFloat(detail.PurchasePrice || selectedBatch.PurchasePrice || 0)
          };

          // Calculate discount percentage from detail
          const discountPercentage = parseFloat(detail.Disc_Percentage || 0);
          const discountAmount = parseFloat(detail.Disc_Amount || 0);

          // Convert discount to string format (percentage with 'p' or fixed amount)
          let discountStr = '';
          if (discountPercentage > 0) {
            discountStr = `${discountPercentage.toFixed(2)}p`;
          } else if (discountAmount > 0) {
            discountStr = discountAmount.toFixed(2);
          }

          // Calculate values using existing function
          const qty = parseFloat(detail.Qty || 1);
          const mrp = parseFloat(detail.MRP || detail.SalesPrice || 0);
          const calculated = calculateGstInclusive(mrp, qty, discountStr, batchWithStock.gstRate);
          const baseRate = mrp / (1 + (batchWithStock.gstRate / 100));

          // Calculate default discount from medicine data (discount1, discount2, discount3)
          let medicineDefaultDiscount = '';
          if (itemInfo.Discount1 && parseFloat(itemInfo.Discount1) > 0) {
            medicineDefaultDiscount = `${parseFloat(itemInfo.Discount1).toFixed(2)}p`;
          } else if (itemInfo.Discount2 && parseFloat(itemInfo.Discount2) > 0) {
            medicineDefaultDiscount = `${parseFloat(itemInfo.Discount2).toFixed(2)}p`;
          } else if (itemInfo.Discount3 && parseFloat(itemInfo.Discount3) > 0) {
            medicineDefaultDiscount = `${parseFloat(itemInfo.Discount3).toFixed(2)}p`;
          }

          const product = {
            id: detail.ItemNo,
            medicine: {
              value: detail.ItemNo,
              label: detail.ItemName || itemInfo.Item_Name || ''
            },
            batch: batchWithStock,
            rate: baseRate.toFixed(2),
            amount: calculated.amount.toFixed(2),
            qty: qty,
            discount: discountStr,
            defaultDiscount: medicineDefaultDiscount, // Store the original default discount from medicine
            discountAmount: calculated.discountAmount.toFixed(2),
            mrp: batchWithStock.mrp,
            gstAmount: calculated.gstAmount.toFixed(2),
            gstRate: batchWithStock.gstRate,
            totalAmount: calculated.totalAmount.toFixed(2),
            name: detail.ItemName || itemInfo.Item_Name || '',
            shelf: itemInfo?.Shelf || itemInfo?.shelf || ''
          };

          transformedProducts.push(product);
        });

        // Set the products
        setProducts(transformedProducts);

        // Also populate medicines state with itemList data for batch selection when editing
        if (itemList && Array.isArray(itemList)) {
          const transformedMedicines = itemList.map((item) => {
            // Parse ItemBatch JSON string
            let batches = [];
            try {
              if (item.ItemBatch && typeof item.ItemBatch === 'string') {
                batches = JSON.parse(item.ItemBatch);
              } else if (Array.isArray(item.ItemBatch)) {
                batches = item.ItemBatch;
              }
            } catch (error) {
              batches = [];
            }

            // Transform batches to match the expected format
            const transformedBatches = batches.map((batch) => {
              // Find if this batch was used in any product
              const usedProduct = transformedProducts.find(
                p => p.batch && p.batch.batchNo === batch.BatchCode
              );

              // Calculate available stock: if this batch is used, add back the sold qty
              let availableQty = parseFloat(batch.Onhand || 0);
              if (usedProduct) {
                const soldQty = parseFloat(usedProduct.qty || 0);
                availableQty = availableQty + soldQty;
              }

              return {
                batchNo: batch.BatchCode || '',
                expiryDate: batch.ExpiryDate || '',
                mrp: parseFloat(batch.SalesPrice || item.MRP || 0),
                rate: parseFloat(batch.SalesPrice || item.MRP || 0),
                gstRate: parseFloat(item.TaxCode || 0),
                availableQty: availableQty,
                purchasePrice: parseFloat(batch.PurchasePrice || 0),
                sgst: 0,
                cgst: 0,
                totalWithGst: 0
              };
            });

            return {
              id: item.Item_No,
              name: item.Item_Name || '',
              genericName: item.Chemical || '',
              manufacturer: item.Company || '',
              batches: transformedBatches,
              section: item.Section || '',
              hsnCode: item.HSNCode || '',
              discount1: item.Discount1 || 0,
              discount2: item.Discount2 || 0,
              discount3: item.Discount3 || 0,
              itemStock: item.ItemStock || 0,
              shelf: item.Shelf || item.shelf || ''
            };
          });

          setMedicines(transformedMedicines);
          // Also update cache
          transformedMedicines.forEach(med => {
            medicinesCacheRef.current[med.id] = med;
          });
        }

        // Expand refs arrays
        setTimeout(() => {
          const newLength = transformedProducts.length;
          if (productRefs.current.length < newLength) {
            productRefs.current = Array(newLength).fill(null);
          }
          if (qtyRefs.current.length < newLength) {
            qtyRefs.current = Array(newLength).fill(null);
          }
          if (rateRefs.current.length < newLength) {
            rateRefs.current = Array(newLength).fill(null);
          }
          if (discountRefs.current.length < newLength) {
            discountRefs.current = Array(newLength).fill(null);
          }
        }, 100);

        // Set billed date from EntryDate
        if (header.CreatedDate) {
          const entryDate = new Date(header.CreatedDate);
          if (!isNaN(entryDate.getTime())) {
            const formattedDate = formatBilledDate(entryDate);
            setBilledDate(formattedDate);
          }
        }


        if (response.data.data.header.EntryDate) {
          const invoiceDate = response.data.data.header.EntryDate;

          // Use local timezone instead of UTC for date comparison
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const todayStr = `${year}-${month}-${day}`; // YYYY-MM-DD in local timezone

          if ((invoiceDate.split('T')[0]) !== todayStr) {
            toast.error('Please return bill. You can\'t edit previous date bills.', {
              autoClose: 1000
            });
            setLoading(false);
            setIsButtonDisabled(true);
            return;
          } else {
            setIsButtonDisabled(false);
          }
        }

        const shouldSkipPermission = userInfo?.Designation === 'Admin';

        if (shouldSkipPermission || hasPermission('Pharmacy Bill Edit', 'Create')) {
          console.log('Permission granted');
        }else {
            setIsButtonDisabled(true);
            toast.error('You don\'t have permission to edit this bill.', { autoClose: 1000 });
        }

        // Store initial state for change tracking (after all data is loaded)
        setTimeout(() => {
          initialBillStateRef.current = {
            products: transformedProducts.map(p => ({
              id: p.id,
              qty: parseFloat(p.qty || 0),
              discount: p.discount || '',
              batchNo: p.batch?.batchNo || '',
              totalAmount: parseFloat(p.totalAmount || 0)
            })),
            patientName: editName || '',
            mobileNo: editNumber || '',
            doctorId: selectedDoctor?.value || 0,
            payingAmount: parseFloat(payingAmount || 0),
            globalDiscount: globalDiscount || ''
          };
          setHasUnsavedChanges(false); // Reset change tracking when bill is loaded
          hasUnsavedChangesRef.current = false; // Reset ref
        }, 500);
      }
    } catch (error) {
      toast.error('Failed to load invoice details.', { autoClose: 2000 });
      resetForm();
    } finally {
      setLoading(false);
    }
  }

  const handlePrevious = async () => {
    const prevnum = parseInt(invoiceNumber) - 1
    if (prevnum === 0) {
      console.log(prevnum)
    } else {
      getItemDatasparms(prevnum)
    }
  }

  const handleNext = async () => {
    const nextnum = parseInt(invoiceNumber) + 1
    if (newinvoiceNumber <= nextnum) {
      resetForm()
    } else {
      getItemDatasparms(nextnum)
    }
  }

  const handleGoToInvoice = async (inputValue) => {
    const invno = parseInt(inputValue)
    getItemDatasparms(invno)
  }

  const handleNewDoctorSaved = (newDoctor) => {
    setSelectedDoctor({
      //value: newDoctor.ID,
      //label: newDoctor.DrName
      value: newDoctor.ID,
      label: newDoctor.Designation
        ? `${newDoctor.DrName}, ${newDoctor.Designation}`
        : newDoctor.DrName,
      DoctorName: newDoctor.DrName,
      Designation: newDoctor.Designation,
      Department: newDoctor.Department
    });
  };


  const handleValidate = async () => {
    // Validate patient has PatientID
    if (!selectedPatient?.PatientID) {
      toast.error('Please Select Patient', { duration: 1000 });
      return;
    }

    // Validate at least one item is selected
    const validProducts = products.filter(product => product.id && product.name);
    if (validProducts.length === 0) {
      toast.error('Please select at least one item', { duration: 1000 });
      return;
    }

    if (!privilegeCardNumber) {
      toast.error('Please Enter Card Number', { duration: 1000 });
    } else {

      try {
        setLoading(true);

        const response = await axios.post(`${BASE_URL}${VALIDATE_CARD}`, {}, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'patientID': selectedPatient?.PatientID,
            'cardNumber': privilegeCardNumber,
          },
        });

        if (response.status === 200) {
          const responseData = response.data.data;

          // Check if items array exists
          if (responseData.items && Array.isArray(responseData.items) && responseData.items.length > 0) {
            // Create a map of TestID to Discount for quick lookup
            const itemsDiscountMap = {};
            responseData.items.forEach(item => {
              if (item.TestID && item.Discount) {
                itemsDiscountMap[item.TestID] = item.Discount;
              }
            });

            // Update products with matching discounts
            const updatedProducts = products.map((product) => {
              // Check if product ID matches any TestID in items array
              if (product.id && itemsDiscountMap[product.id]) {
                const discountPercentage = itemsDiscountMap[product.id];
                const discountStr = `${discountPercentage.toFixed(2)}p`; // Format as percentage string

                // Recalculate with new discount
                if (product.batch) {
                  const qty = parseFloat(product.qty || 0);
                  const mrp = parseFloat(product.batch.mrp || 0);
                  const calculated = calculateGstInclusive(mrp, qty, discountStr, product.batch.gstRate);

                  return {
                    ...product,
                    discount: discountStr,
                    amount: calculated.amount.toFixed(2),
                    discountAmount: calculated.discountAmount.toFixed(2),
                    gstAmount: calculated.gstAmount.toFixed(2),
                    totalAmount: calculated.totalAmount.toFixed(2)
                  };
                }
              }
              return product;
            });

            setProducts(updatedProducts);

            // Count how many products got discounts
            const discountedCount = updatedProducts.filter(p =>
              p.id && itemsDiscountMap[p.id]
            ).length;

            if (discountedCount > 0) {
              toast.success(`Discount applied to ${discountedCount} item(s)`, {
                autoClose: 2000,
              });
            } else {
              toast.info('No matching items found for discount', {
                autoClose: 2000,
              });
            }
          } else {
            // No discounts found - restore default discounts for all products
            const updatedProducts = products.map((product) => {
              if (product.batch) {
                // Restore from stored defaultDiscount field, or use empty string if not available
                const defaultDiscountStr = product.defaultDiscount || '';
                const qty = parseFloat(product.qty || 0);
                const mrp = parseFloat(product.batch.mrp || 0);
                const calculated = calculateGstInclusive(mrp, qty, defaultDiscountStr, product.batch.gstRate);

                return {
                  ...product,
                  discount: defaultDiscountStr,
                  amount: calculated.amount.toFixed(2),
                  discountAmount: calculated.discountAmount.toFixed(2),
                  gstAmount: calculated.gstAmount.toFixed(2),
                  totalAmount: calculated.totalAmount.toFixed(2)
                };
              }
              return product;
            });

            setProducts(updatedProducts);
            toast.info('No discounts found. Restored default discounts', {
              autoClose: 2000,
            });
          }

        } else {
          toast.error('Something Went Wrong', { duration: 1000 });
        }
      } catch (error) {
        console.error(error);
        toast.error('Something Went Wrong', { duration: 1000 });
      } finally {
        setLoading(false);
      }
    }
  }

  // Handle share button click - open modal for quantity input
  const handleShareClick = (index) => {
    setShareProductIndex(index);
    setShareQty('');
    setShowShareModal(true);
  };

  // Handle share API call
  const handleShareSubmit = async () => {
    // Prevent double submission
    if (isSubmittingShareRef.current) {
      return;
    }

    if (!shareQty || parseFloat(shareQty) <= 0) {
      toast.error('Please enter a valid quantity', { autoClose: 1000 });
      return;
    }

    if (shareProductIndex === null || !products[shareProductIndex]) {
      toast.error('Invalid product selected', { autoClose: 1000 });
      return;
    }

    const product = products[shareProductIndex];
    if (!product.id || !product.name) {
      toast.error('Product information is missing', { autoClose: 1000 });
      return;
    }

    // Set flag immediately to prevent double submission
    isSubmittingShareRef.current = true;
    setLoading(true);
    try {
      const payload = [
        {
          itemNo: product.id,
          itemName: product.name,
          qty: shareQty,
          addedAt: new Date().toISOString(),
          isorderd: false
        }
      ];

      const response = await axios.post(`${BASE_URL}${POST_SHORT_BOOKS_ITEMS}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.status === 200) {
        toast.success('Item added to short books successfully', { autoClose: 2000 });
        setShowShareModal(false);
        setShareQty('');
        setShareProductIndex(null);
      } else {
        toast.error('Failed to add item to short books', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error sharing item:', error);
      toast.error('Failed to add item to short books', { autoClose: 2000 });
    } finally {
      setLoading(false);
      // Reset flag after API call completes
      isSubmittingShareRef.current = false;
    }
  };


  return (
    <div className='flex h-full bg-background overflow-hidden'>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <BatchSelectionModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          // Clear editing state if we were editing
          if (editingMedicineRowIndex !== null) {
            setEditingMedicineRowIndex(null);
            setMedicineSearchForEdit('');
            setShowMedicineList(false);
          }
          // Only return focus if batch was NOT selected (user cancelled)
          // If batch was selected, focus is handled by onSelectBatch
          // We check if we're still selecting (ref will be reset after selection)
          if (!isSelectingMedicineRef.current) {
            // Return focus to the medicine search input if it was from new entry row
            if (currentProductIndex === products.length && medicineSearchInputRef.current) {
              setTimeout(() => {
                medicineSearchInputRef.current?.focus();
              }, 100);
            } else if (currentProductIndex !== null && productRefs.current[currentProductIndex]) {
              setTimeout(() => {
                productRefs.current[currentProductIndex].focus();
              }, 100);
            }
          }
        }}
        medicine={selectedMedicineForBatch}
        batches={selectedMedicineForBatch?.batches || []}
        generalsettings={generalsettings}
        onReopen={() => {
          // Blur medicine search input explicitly
          if (medicineSearchInputRef.current) {
            medicineSearchInputRef.current.blur();
            setShowMedicineList(false);
          }
          // Blur any other focused inputs
          if (document.activeElement && document.activeElement.tagName === 'INPUT') {
            document.activeElement.blur();
          }
          // Reopen the modal with same state
          // Focus will be handled by useEffect when isOpen becomes true
          setShowBatchModal(true);
        }}
        onSelectBatch={(selectedBatch) => {
          // Mark that we're selecting to prevent blur handler interference
          isSelectingMedicineRef.current = true;

          // Calculate target index BEFORE adding the product (since products.length will change)
          const isNewProduct = currentProductIndex === products.length;
          const blankRowIndex = products.findIndex(p => !p.medicine && !p.batch && !p.name);

          // Calculate the target index that will be used after the product is added
          let targetIndex;
          if (isNewProduct) {
            if (blankRowIndex !== -1) {
              targetIndex = blankRowIndex;
            } else {
              // Product will be added to the end, so target index is current products.length
              targetIndex = products.length;
            }
          } else {
            targetIndex = currentProductIndex;
          }

          handleBatchSelection(currentProductIndex, selectedMedicineForBatch, selectedBatch);
          setShowBatchModal(false);
          // Clear editing state if we were editing
          if (editingMedicineRowIndex !== null) {
            setEditingMedicineRowIndex(null);
            setMedicineSearchForEdit('');
          }

          // Focus quantity field directly after batch selection - prevent any other focus
          requestAnimationFrame(() => {
            // Ensure search input is still blurred
            if (medicineSearchInputRef.current) {
              medicineSearchInputRef.current.blur();
            }
            // Blur any currently focused element
            if (document.activeElement && document.activeElement.tagName !== 'INPUT') {
              document.activeElement.blur();
            }
            // Use double requestAnimationFrame to ensure state is updated and refs are ready
            requestAnimationFrame(() => {
              // For new products without blank row, ensure refs array is expanded
              if (isNewProduct && blankRowIndex === -1) {
                // Expand refs array if needed
                if (qtyRefs.current.length <= targetIndex) {
                  qtyRefs.current = [...qtyRefs.current, null];
                }
              }

              // Use a small delay to ensure DOM is fully updated
              setTimeout(() => {
                if (qtyRefs.current[targetIndex]) {
                  qtyRefs.current[targetIndex].focus();
                  qtyRefs.current[targetIndex].select();
                }
                // Reset selection flag after focus is set
                isSelectingMedicineRef.current = false;
              }, 100);
            });
          });
        }}
      />

      <ShareQuantityModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareQty('');
          setShareProductIndex(null);
          // Reset submission flag when modal closes
          isSubmittingShareRef.current = false;
        }}
        product={shareProductIndex !== null ? products[shareProductIndex] : null}
        qty={shareQty}
        setQty={setShareQty}
        onSubmit={handleShareSubmit}
      />

      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>

        <div className="bg-white p-2 mb-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Pharmacy Billing
              </h1>
              <p className="text-xs text-black ">{currentDateTime || new Date().toLocaleString()}</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-md">
                <span className="text-sm font-semibold text-white">Prev Total:</span>
                <span className="text-lg font-bold text-white">₹{parseFloat(prevBillAmount || 0).toFixed(2)}</span>
              </div>
              {/* Navigation Buttons */}
              <div className="flex items-center gap-1">
                {editInvoiceNumber && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all"
                    title="Print Invoice"
                    onClick={async () => {
                      const result = await Swal.fire({
                        title: 'Select Print Option',
                        icon: 'question',
                        showCancelButton: true,
                        showDenyButton: true,
                        confirmButtonColor: '#4f46e5',
                        denyButtonColor: '#e11d48',
                        confirmButtonText: 'Print',
                        denyButtonText: 'View PDF',
                        cancelButtonText: 'Cancel'
                      });

                      if (result.isConfirmed) {
                        handlePrint();
                      } else if (result.isDenied) {
                        generatePDF();
                      }
                    }}
                  >
                    <FiPrinter className="w-4 h-4" />
                  </button>
                )}
                {selectedPatient && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all flex items-center gap-2"
                    title="View Patient History"
                    onClick={() => {
                      setShowPatientSidebar(true);
                    }}
                  >
                    <FiClock className="w-4 h-4" />
                    <span className="text-xs">View History</span>
                  </button>
                )}
                {searchOpnum && (
                  <button
                    type="button"
                    className={`px-4 py-2 font-medium text-sm rounded-md shadow-sm transition-all flex items-center gap-2 ${
                      emrHistoryList.length > 0
                        ? 'px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all'
                        : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    }`}
                    title={emrHistoryList.length > 0 ? 'View OP EMR history' : 'No EMR data'}
                    disabled={emrHistoryList.length === 0}
                    onClick={() => {
                      if (emrHistoryList.length > 0) {
                        setShowEmrHistoryModal(true);
                      }
                    }}
                  >
                    <FiClock className="w-4 h-4" />
                    <span className="text-xs">EMR History</span>
                  </button>
                )}

                <button
                  type="button"
                  className="p-1.5 bg-white text-gray-600 rounded-lg hover:bg-gray-50 hover:text-indigo-600 shadow-sm transition-colors border border-black"
                  title="Previous Invoice"
                  onClick={handlePrevious}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <input
                  type="text"
                  className="w-16 px-2 py-1.5 text-center text-sm border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-bold text-black"
                  placeholder="Invoice #"
                  value={invoiceNumber}
                  onChange={(e) => {
                    setInvoiceNumber(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleGoToInvoice(e.target.value);  // Pass the input value directly
                    }
                  }}
                />
                <button
                  type="button"
                  className="p-1.5 bg-white text-gray-600 rounded-lg hover:bg-gray-50 hover:text-indigo-600 shadow-sm transition-colors border border-black"
                  title="Next Invoice"
                  onClick={handleNext}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

            </div>
          </div>
        </div>


        <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3">
          <div className='w-full flex p-3 bg-gray-100 rounded-lg border border-gray-200 flex-col gap-2.5'>

            {/* Patient Info Section - Compact Header */}
            <div className="flex items-center justify-between gap-2 pb-2 border-b border-gray-300">
              <div className="flex items-center gap-2">
                <FiUser className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-black text-sm">Patient Information</h3>
              </div>
              {selectedPatient?.PatientID > 0 && patientPendingDue.billCount > 0 && (
                <div className="flex items-center gap-1 text-sm font-semibold text-red-600">
                  <TbCurrencyRupee size={16} />
                  <span>Total Due Today: {patientPendingDue.totalDue.toFixed(2)}</span>
                  <span className="text-xs font-medium text-gray-500">
                    ({patientPendingDue.billCount} pending)
                  </span>
                </div>
              )}
            </div>

            {/* Compact Grid Layout for Patient Fields */}
            <div className='w-full'>
              <div className='grid grid-cols-12 gap-2.5'>

                {/* Op Number - Compact */}
                <div className='col-span-1'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Op Number
                  </label>
                  <input
                    type="text"
                    placeholder="op number"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={opnumRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleOpSearch();
                      }
                    }}
                    onBlur={handleOpnumBlur}
                    value={searchOpnum}
                    onChange={e => setSearchOpnum(e.target.value)}
                  />
                </div>

                <div className='col-span-4'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Mobile Number / Name <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder="Enter mobile or name..."
                    inputId="mobile-input"
                    value={options.find(option => option.value === selectedPatient?.PatientID)}
                    options={options}
                    formatOptionLabel={customOptionLabel}
                    onInputChange={(value, { action }) => {
                      if (action === 'input-change') {
                        handleMobileChange(value);
                      }
                    }}
                    onChange={(selectedOption) => {
                      handleSuggestionClick(selectedOption);
                    }}
                    ref={mobileInputRef}
                    onMenuClose={() => {
                      // When menu closes, focus name input
                      setTimeout(() => {
                        nameRef.current?.focus();
                      }, 100);
                    }}
                    isClearable
                    noOptionsMessage={() => "No matches found"}
                    styles={customSelectStyles}
                    className="w-full"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Only move to next field if no option is currently highlighted/focused
                        const isMenuOpen = mobileInputRef.current?.state?.menuIsOpen;
                        const focusedOption = mobileInputRef.current?.state?.focusedOption;

                        if (!isMenuOpen && !focusedOption) {
                          e.preventDefault();
                          nameRef.current?.focus();
                        }
                      }
                    }}
                  />
                </div>



                {/* Name */}
                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter patient name"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={nameRef}
                    onKeyDown={(e) => handleKeyDown(e, numberRef)}
                    value={editName}
                    onChange={(e) => seteditName(e.target.value.toUpperCase())}
                  />
                </div>


                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter patient number"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={numberRef}
                    onKeyDown={(e) => handleKeyDown(e, doctorSelectRef)}
                    value={editNumber}
                    onChange={(e) => setEditNumber(e.target.value)}
                  />
                </div>



                {/* Doctor */}
                <div className='col-span-3'>
                  <label className="flex justify-between items-center text-xs font-medium text-black mb-1">
                    <span>Referring Doctor <span className="text-red-500">*</span></span>
                    <span className="text-blue-500 cursor-pointer hover:text-blue-700" onClick={() => setDoctorModal(true)}>Add New +</span>
                  </label>
                  <Select
                    options={doctors_options}
                    className="w-full font-medium"
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    formatOptionLabel={customDoctorLabel}
                    required
                    value={selectedDoctor}
                    ref={doctorSelectRef}

                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Only move to next field if no option is currently highlighted/focused
                        const isMenuOpen = doctorSelectRef.current?.state?.menuIsOpen;
                        const focusedOption = doctorSelectRef.current?.state?.focusedOption;

                        if (!isMenuOpen && !focusedOption) {
                          e.preventDefault();
                          // Focus medicine search input in the yellow row
                          setTimeout(() => {
                            if (medicineSearchInputRef.current) {
                              medicineSearchInputRef.current.focus();
                              setShowMedicineList(true);
                              setSelectedMedicineIndex(-1);
                              // Calculate position for dropdown
                              if (medicineSearchContainerRef.current) {
                                setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                              }
                            }
                          }, 100);
                        }
                      }
                    }}
                    onChange={(selectedOption) => {
                      setSelectedDoctor(selectedOption);
                      // Focus medicine search input after selecting doctor
                      setTimeout(() => {
                        if (medicineSearchInputRef.current) {
                          medicineSearchInputRef.current.focus();
                          setShowMedicineList(true);
                          setSelectedMedicineIndex(-1);
                          // Calculate position for dropdown
                          if (medicineSearchContainerRef.current) {
                            setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                          }
                        }
                      }, 150);
                    }}
                    inputId="doctor-input"
                    styles={customSelectStyles}
                  />
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="mt-2 flex-1 overflow-hidden flex flex-col ml-3 mr-3">
          <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-black">
            <h3 className="font-semibold text-black text-sm">Medicine Selection</h3>
            <button
              type="button"
              onClick={handleHalfQuantity}
              disabled={halfQtyUsed}
              className={`px-3 py-1.5 font-medium text-sm rounded-md shadow-sm transition-all ${halfQtyUsed
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                }`}
            >
              ⚡ Half Qty
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-400 flex-1 w-full">
            <div className="overflow-y-auto overflow-x-auto border rounded-xl relative bg-white h-full">
              <table className="border-collapse text-xs w-full" style={{ minWidth: '1200px' }}>
                <thead className="bg-gray-200 text-left sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '50px' }}>No.</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '330px' }}>Medicine Name</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Qty</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Batch No</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Expiry</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>MRP</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Amount</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Discount</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>GST%</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>GST</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Total</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {/* Regular rows with products */}
                  {products.map((product, index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-300 ${selectedIndex === index && edit
                        ? "bg-indigo-50"
                        : "hover:bg-gray-50"
                        } transition-colors`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <td className="px-3 py-2.5 text-sm text-black border border-gray-200 whitespace-nowrap">
                        {index + 1}
                      </td>

                      <td
                        className="px-3 py-1.5 text-black font-medium border border-gray-200 whitespace-nowrap cursor-pointer hover:bg-indigo-50 transition-colors"
                        onClick={(e) => handleMedicineNameClick(index, e)}
                        ref={(el) => (medicineNameCellRefs.current[index] = el)}
                        title="Click to change medicine"
                        style={{ fontSize: '14px' }}
                        tabIndex={-1}
                      >
                        {product.name || ''}
                      </td>

                      <td className="px-3 py-1.5 text-black border border-gray-200" style={{ fontSize: '13px' }}>
                        <input
                          type="text"
                          className="w-full px-1 py-0.5  text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          style={{ fontSize: '15px' }}
                          value={product.qty}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                          onKeyDown={(e) => handleQtyKeyDown(e, index)}
                          ref={(el) => (qtyRefs.current[index] = el)}
                          onFocus={(e) => e.target.select()}
                          disabled={!product.batch}
                        />
                      </td>

                      <td
                        className="px-3 py-1.5 text-black border border-gray-200 whitespace-nowrap cursor-pointer hover:bg-indigo-50 transition-colors"
                        style={{ fontSize: '13px' }}
                        title="Click to change batch"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBatchCellClick(index);
                        }}
                      >
                        {product.batch?.batchNo || ''}
                      </td>

                      <td className="px-3 py-1.5 text-black border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }} title="Batch expiry">
                        {product.batch?.expiryDate || ''}
                      </td>

                      <td className="px-3 py-1.5 text-black font-medium border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }}>
                        {product.batch ? `${product.batch.mrp}` : ''}
                      </td>



                      <td className="px-3 py-1.5 text-black font-medium text-right border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }}>
                        {product.amount ? `${parseFloat(product.amount).toFixed(2)}` : ''}
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-0.5 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          style={{ fontSize: '15px' }}
                          value={product.discount || ''}
                          onChange={(e) => handleDiscountChange(index, e.target.value)}
                          onKeyDown={(e) => handleDiscountKeyDown(e, index, product)}
                          ref={(el) => (discountRefs.current[index] = el)}
                          placeholder="10 or 10p"
                          title="Amount: 0 to item total. Percentage: 0p to 100p (no negatives)"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.batch}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-black text-right border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }}>
                        {product.batch ? `${product.gstRate || product.batch.gstRate || 0}%` : ''}
                      </td>

                      <td className="px-3 py-1.5 text-black text-right border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }}>
                        {product.gstAmount ? `${parseFloat(product.gstAmount).toFixed(2)}` : ''}
                      </td>

                      <td className="px-3 py-1.5 text-black font-bold text-right border border-gray-200 whitespace-nowrap" style={{ fontSize: '13px' }}>
                        {product.totalAmount ? `${parseFloat(product.totalAmount).toFixed(2)}` : ''}
                      </td>

                      <td className="px-3 py-1.5 text-black border border-gray-200" style={{ fontSize: '13px' }}>
                        <div className="flex items-center gap-1">
                          {!editInvoiceNumber && (
                            <button
                              type="button"
                              className="p-0.5 bg-blue-50 text-blue-500 rounded hover:bg-blue-100 transition-colors"
                              style={{ fontSize: '13px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShareClick(index);
                              }}
                              title="Share to short books"
                            >
                              <FiShare2 size={12} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="p-0.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition-colors"
                            style={{ fontSize: '13px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(index);
                            }}
                            title="Remove medicine"
                          >
                            <MdDelete size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* New entry row - yellow background */}
                  <tr className="bg-gray-200 relative">
                    <td className="px-3 py-1.5 text-xs text-black border border-gray-200 whitespace-nowrap">
                      {products.length + 1}
                    </td>

                    <td className="px-3 py-1.5 relative border border-gray-200">
                      <div className="relative w-full" ref={medicineSearchContainerRef}>
                        <input
                          className="w-full px-1 py-1 text-black focus:ring-1 focus:ring-yellow-400"
                          style={{ fontSize: '18px' }}
                          value={medicineSearch}
                          onChange={(e) => {
                            const searchValue = e.target.value;
                            setMedicineSearch(searchValue);
                            setShowMedicineList(true);

                            // Clear previous debounce timeout
                            if (medicineSearchDebounceRef.current) {
                              clearTimeout(medicineSearchDebounceRef.current);
                            }

                            // Call API when user types 3+ characters (like getPatientData) with debounce
                            if (searchValue && searchValue.length >= 1) {
                              medicineSearchDebounceRef.current = setTimeout(() => {
                                getInvoiceItems(searchValue);
                              }, 300); // 300ms debounce
                            } else if (searchValue.length === 0) {
                              // Clear medicines when search is cleared
                              setMedicines([]);
                            }

                            // Don't auto-select - user must manually navigate
                            setSelectedMedicineIndex(-1);
                            // Calculate position for dropdown
                            if (medicineSearchContainerRef.current) {
                              setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                            }
                          }}
                          onFocus={() => {
                            setShowMedicineList(true);
                            setSelectedMedicineIndex(-1); // Don't auto-focus initially
                            // Calculate position for dropdown
                            if (medicineSearchContainerRef.current) {
                              setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                            }
                          }}
                          onKeyDown={handleMedicineSearchKeyDown}
                          onBlur={() => {
                            // Don't hide dropdown if we're in the process of selecting
                            if (isSelectingMedicineRef.current) {
                              return;
                            }
                            // Delay to allow click on dropdown items
                            if (editingMedicineRowIndex === null) {
                              setTimeout(() => setShowMedicineList(false), 200);
                            }
                          }}
                          ref={medicineSearchInputRef}
                          placeholder="Search medicine..."
                        />
                      </div>
                    </td>

                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white ml-3 mr-3 mt-3 mb-2 rounded-lg shadow-sm border border-gray-300">
          <div className="p-3">
            {/* First Row - Totals and Options */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              {/* Invoice Number */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-black">Invoice:</span>
                <span className="text-sm font-bold text-black">{formatPrintInvoiceNumber(invoiceNumber)}</span>
              </div>

              {/* Total Amount (Without Discount) */}
              <div className="flex items-center gap-2 border-l border-r border-gray-300 px-4">
                <span className="text-xs font-semibold text-black">Total Amount:</span>
                <span className="text-sm font-bold text-gray-900">₹{calculateTotalAmountBeforeDiscount()}</span>
              </div>

              {/* Total Discount */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-black">Discount:</span>
                <span className="text-sm font-semibold text-gray-900">₹{calculateTotalDiscount()}</span>
              </div>

              {/* Net Total (With Tax, After Discount) */}
              <div className="flex items-center gap-2 border-l border-r border-gray-300 px-4">
                <span className="text-xs font-semibold text-black">Net Total:</span>
                <span className="text-base font-bold text-indigo-600">₹{generalsettings?.phValueRoundOff
                  ? roundAmount(calculateGrandTotal())
                  : calculateGrandTotal()}</span>
              </div>

              {/* GST Included */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-black">GST:</span>
                <span className="text-sm font-semibold text-gray-900">₹{calculateTotalGST()}</span>
              </div>

              {/* Balance Due */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-red-600">Balance Due:</span>
                <span className="text-sm font-bold text-red-600">
                  {generalsettings?.phpaymentMarkOnBillSave
                    ? (generalsettings?.phValueRoundOff
                      ? parseFloat(roundAmount(calculateGrandTotal())).toFixed(2)
                      : parseFloat(calculateGrandTotal()).toFixed(2))
                    : generalsettings?.phValueRoundOff
                      ? parseFloat((roundAmount(calculateGrandTotal()) - (parseFloat(payingAmount) || 0))).toFixed(2)
                      : parseFloat((calculateGrandTotal() - (parseFloat(payingAmount) || 0))).toFixed(2)}
                </span>
              </div>

              {/* Print Checkbox */}
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={printAfterSave}
                    onChange={(e) => setPrintAfterSave(e.target.checked)}
                    className="text-text-black focus:ring-gray-500 h-4 w-4"
                  />
                  <span className="ml-1.5 text-xs text-black">Print on save</span>
                </label>
              </div>

              {/* Global Discount Field */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-black whitespace-nowrap">Discount:</label>
                <input
                  type="text"
                  placeholder="10 or 10p"
                  className="w-24 px-2 py-1.5 text-sm border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-black"
                  value={globalDiscount}
                  onChange={(e) => handleGlobalDiscountChange(e.target.value)}
                  title="Amount: 0 to bill total. Percentage: 0p to 100p (no negatives). Applies to all items."
                />
              </div>

              {/* Payment Options Accordion */}
              <div className="relative payment-options-container">
                <button
                  type="button"
                  onClick={() => setShowPaymentOptions(!showPaymentOptions)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-black text-xs font-medium rounded-md hover:bg-gray-200 transition-colors border border-gray-300"
                >
                  <span>Payment: {paymentMethod.toUpperCase()}</span>
                  {roundOff && <span className="text-indigo-600">• Round</span>}
                  {showPaymentOptions ? (
                    <FiChevronUp size={14} />
                  ) : (
                    <FiChevronDown size={14} />
                  )}
                </button>

                {/* Payment Options Menu */}
                {showPaymentOptions && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                    <div className="p-2 space-y-1">
                      {/* Payment Method Selection */}
                      <div className="px-2 py-1 text-xs font-semibold text-gray-600 border-b border-gray-200">
                        Payment Method
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentMethod('cash');
                          setShowPaymentOptions(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-100 transition-colors ${paymentMethod === 'cash' ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-black'
                          }`}
                      >
                        💵 Cash
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentMethod('upi');
                          setShowPaymentOptions(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-100 transition-colors ${paymentMethod === 'upi' ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-black'
                          }`}
                      >
                        📱 Bank
                      </button>

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-1"></div>

                      {/* Round Off Toggle */}
                      <button
                        type="button"
                        onClick={() => {
                          setRoundOff(!roundOff);
                          setShowPaymentOptions(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-100 transition-colors ${roundOff ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-black'
                          }`}
                      >
                        🔄 Round Off {roundOff ? '(ON)' : '(OFF)'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Second Row - Paying Amount, Center Totals, Privilege Card and Action Buttons */}
            <div className="flex justify-between items-center gap-2">
              {/* Left Section - Paying Amount Input - Conditionally shown */}
              {/* <div className="flex items-center gap-2">
                {generalsettings?.phpaymentMarkOnBillSave && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-black whitespace-nowrap">Paying Amount:</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-32 px-2 py-1.5 text-sm border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-black text-right"
                      value={payingAmount || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setPayingAmount(value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitbtnRef.current?.focus();
                        }
                      }}
                      title="Enter the amount being paid"
                    />
                  </div>
                )}
              </div> */}

              {/* Center Section - Net Total */}
              <div className="flex flex-col items-center justify-center flex-1 gap-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg shadow-md">
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-2 w-full justify-center'>
                      <span className="text-sm font-semibold text-white">Net Total:</span>
                      <span className="text-lg font-bold text-white">₹{generalsettings?.phValueRoundOff
                      ? roundAmount(calculateGrandTotal())
                      : calculateGrandTotal()}</span>

                    </div>
                    <div className='flex items-center gap-2 w-full justify-center'>
                    {editInvoiceNumber && billedDate && (
                      <div className="flex items-center gap-2 text-xs text-white">
                        <FiClock className="w-3 h-3" />
                        <span>Bill Date: {billedDate}</span>
                      </div>
                    )}

                  </div>
                </div>
                </div>
              </div>

              {/* Right Section - Privilege Card and Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Privilege Card Section */}
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-black whitespace-nowrap">Privilege Card:</label>
                  <input
                    type="text"
                    placeholder="Enter card number"
                    className="w-36 px-2 py-1.5 text-sm border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-black"
                    value={privilegeCardNumber}
                    onChange={(e) => setPrivilegeCardNumber(e.target.value)}
                    ref={privilegeCardRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // Handle validate - functionality to be added
                        handleValidate();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-indigo-100 text-indigo-600 hover:bg-indigo-200 font-medium rounded-lg text-sm transition-colors"
                    onClick={() => {
                      // Handle validate - functionality to be added
                      handleValidate();
                    }}
                  >
                    Validate
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={`px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    onClick={handleSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!isButtonDisabled && !isSavingRef.current) {
                          handleSave();
                        }
                      }
                    }}
                    disabled={isButtonDisabled || isSavingRef.current}
                    ref={submitbtnRef}
                  >
                    Submit
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                    onClick={resetForm}
                  >
                    New +
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {(showMedicineList || editingMedicineRowIndex !== null) && createPortal(
        <>
          {/* Backdrop for click outside */}
          {editingMedicineRowIndex !== null && (
            <div
              className="fixed inset-0 z-[99998] bg-transparent"
              onClick={() => {
                // Close without making any changes
                setShowMedicineList(false);
                setMedicineSearch('');
                setMedicineSearchForEdit('');
                setEditingMedicineRowIndex(null);
                setSelectedMedicineIndex(-1);
                setMedicines([]);
              }}
            />
          )}
          <div
            ref={medicineDropdownRef}
            className="fixed bg-white shadow-2xl border z-[99999] max-h-96 overflow-auto"
            style={{
              top: dropdownPosition.isAbove ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.isAbove ? `${dropdownPosition.bottom}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              width: `${Math.max(dropdownPosition.width || 1000, 1000)}px`,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {/* Search input for editing mode */}
            {editingMedicineRowIndex !== null && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
                  placeholder="Search medicine..."
                  value={medicineSearchForEdit}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setMedicineSearchForEdit(searchValue);

                    // Clear previous debounce timeout
                    if (medicineSearchDebounceRef.current) {
                      clearTimeout(medicineSearchDebounceRef.current);
                    }

                    // Call API when user types 3+ characters (like getPatientData) with debounce
                    if (searchValue && searchValue.length >= 1) {
                      medicineSearchDebounceRef.current = setTimeout(() => {
                        getInvoiceItems(searchValue);
                      }, 300); // 300ms debounce
                    } else if (searchValue.length === 0) {
                      // Clear medicines when search is cleared
                      setMedicines([]);
                    }

                    // Don't auto-select - user must manually navigate
                    setSelectedMedicineIndex(-1);
                  }}
                  onKeyDown={handleMedicineSearchKeyDown}
                  autoFocus
                />
              </div>
            )}
            {filteredMedicines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '20px' }}>SI</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '170px' }}>Name</th>
                      <th className="px-2 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '30px' }}>QTY</th>
                      <th className="px-2 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '25px' }}>Expiry</th>
                      <th className="px-2 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '30px' }}>MRP</th>
                      <th className="px-2 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '60px' }}>Company</th>
                      <th className="px-2 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '25px' }}>Shelf</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredMedicines.map((med, medIndex) => {
                      const totalQty = med.batches.reduce((sum, batch) => sum + (batch.availableQty || 0), 0);
                      const firstBatch = med.batches[0];
                      const isSelected = medIndex === selectedMedicineIndex;
                      return (
                        <tr
                          key={medIndex}
                          ref={(el) => {
                            if (el) {
                              medicineRowRefs.current[medIndex] = el;
                            }
                          }}
                          className={`border-b border-gray-200 cursor-pointer ${isSelected
                            ? 'bg-indigo-200 hover:bg-indigo-300'
                            : 'hover:bg-blue-100'
                            } transition-colors`}
                          style={{
                            backgroundColor: isSelected ? '#c7d2fe' : undefined
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Immediately blur the search input to prevent focus
                            if (medicineSearchInputRef.current) {
                              medicineSearchInputRef.current.blur();
                            }
                            setSelectedMedicineIndex(medIndex);
                            const targetIndex = editingMedicineRowIndex !== null ? editingMedicineRowIndex : products.length;
                            handleSelectMedicineFromSearch(med, targetIndex);
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onMouseEnter={() => setSelectedMedicineIndex(medIndex)}
                        >
                          <td className="px-2 py-1 border border-gray-200 text-black whitespace-nowrap">{medIndex + 1}</td>
                          <td className="px-2 py-1 border border-gray-200 text-black">
                            <div className="font-semibold text-black" style={{ fontSize: '15px' }}>{med.name}</div>
                            {med.genericName && (
                              <div className="text-black mt-1" style={{ fontSize: '13px' }}>{med.genericName}</div>
                            )}
                          </td>
                          <td className="px-1 py-1 border border-gray-200 text-black whitespace-nowrap bg-orange-100 font-bold text-left" style={{ minWidth: '30px' }}>{totalQty}</td>
                          <td className="px-1 py-1 border border-gray-200 text-black whitespace-nowrap font-semibold text-left" style={{ minWidth: '25px' }}>{formatExpiryDateMMYY(firstBatch?.expiryDate)}</td>
                          <td className="px-1 py-1 border border-gray-200 text-black whitespace-nowrap font-semibold text-left" style={{ minWidth: '30px' }}>
                            {firstBatch?.mrp ? `${firstBatch.mrp}` : ''}
                          </td>
                          <td className="px-1 py-1 border border-gray-200 text-black whitespace-nowrap bg-gradient-to-r from-blue-100 to-blue-200 font-semibold text-left" style={{ minWidth: '60px' }}>{med.manufacturer || ''}</td>
                          <td className="px-1 py-1 border border-gray-200 text-black whitespace-nowrap font-semibold text-left" style={{ minWidth: '25px' }}>{med.shelf || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* {filteredMedicines.length === 0 && (
              <div className="p-4 text-center text-black">
                <p>No medicines found</p>
              </div>
            )} */}
          </div>
        </>,
        document.body
      )}

      {doctorModal &&
        <AddDoctorModal setDoctorModal={setDoctorModal} refreshData={getSalesLoadData} onDoctorSaved={handleNewDoctorSaved} />
      }

      {/* EMR History Sidebar */}
      {showEmrHistoryModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-[100] transition-opacity"
            onClick={() => setShowEmrHistoryModal(false)}
          />

          <div className={`fixed top-0 right-0 h-full w-[650px] bg-white shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out ${showEmrHistoryModal ? 'translate-x-0' : 'translate-x-full'
            }`}>
            <div className="flex flex-col h-full">
              <div className="bg-indigo-600 text-white p-4 flex items-center justify-between border-b border-indigo-700">
                <div>
                  <h2 className="text-lg font-bold">EMR History</h2>
                  <p className="text-sm text-indigo-100 mt-1">
                    OP: {searchOpnum || '-'} | Select any edit to load medicines
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEmrHistoryModal(false)}
                  className="p-2 hover:bg-indigo-700 rounded-lg transition-colors"
                  title="Close"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {emrHistoryList.length > 0 ? (
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-black border-b border-gray-200">Edit No</th>
                        <th className="px-4 py-3 text-left font-semibold text-black border-b border-gray-200">Date & Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-black border-b border-gray-200">Doctor ID</th>
                        <th className="px-4 py-3 text-left font-semibold text-black border-b border-gray-200">Prescription Items</th>
                        <th className="px-4 py-3 text-left font-semibold text-black border-b border-gray-200">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emrHistoryList.map((emrRow) => {
                        const itemCount = getPrescriptionCountFromEmr(emrRow);
                        return (
                          <tr key={`${emrRow.EMRAID}-${emrRow.EditNo}`} className="hover:bg-emerald-50 transition-colors border-b border-gray-100">
                            <td className="px-4 py-3 border-b border-gray-100 text-black font-medium">
                              {emrRow.EditNo || '-'}
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100 text-black">
                              {formatEmrDateTime(emrRow.CreteadAt || emrRow.CreatedAt)}
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100 text-black">
                              {emrRow.DoctorID || '-'}
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-700 font-medium">
                                {itemCount}
                              </span>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <button
                                type="button"
                                className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-medium rounded-md hover:from-indigo-700 hover:to-purple-700 transition-all"
                                onClick={async () => {
                                  await loadProductsFromLatestEmr(emrRow);
                                  setShowEmrHistoryModal(false);
                                }}
                              >
                                Load This EMR
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-black">
                    No EMR prescription data found for this OP.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Patient History Sidebar */}
      {showPatientSidebar && selectedPatient && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-[100] transition-opacity"
            onClick={() => {
              setShowPatientSidebar(false);
              setExpandedBillId(null);
            }}
          />

          {/* Sidebar */}
          <div className={`fixed top-0 right-0 h-full w-[650px] bg-white shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out ${showPatientSidebar ? 'translate-x-0' : 'translate-x-full'
            }`}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="bg-indigo-600 text-white p-4 flex items-center justify-between border-b border-indigo-700">
                <div>
                  <h2 className="text-lg font-bold">Patient History</h2>
                  <p className="text-sm text-indigo-100 mt-1">{selectedPatient.PatientName}</p>

                </div>
                <button
                  onClick={() => {
                    setShowPatientSidebar(false);
                    setExpandedBillId(null);
                  }}
                  className="p-2 hover:bg-indigo-700 rounded-lg transition-colors"
                  title="Close"
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 bg-gray-50">
                <button
                  className={'flex-1 px-4 py-3 text-sm font-medium transition-colors text-text-black bg-gray-200 hover:text-black hover:bg-gray-100'}
                >
                  Previous Bills
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <div>
                  {patientHistory.length > 0 ? (
                    <div className="space-y-3">
                      {patientHistory.map((bill, index) => {
                        const isExpanded = expandedBillId === bill.InvoiceNo;
                        const items = billItems[bill.InvoiceNo] || [];
                        const isLoading = loadingBillItems[bill.InvoiceNo];

                        return (
                          <div key={index} className="bg-white rounded-lg border border-gray-400 hover:border-gray-500 transition-colors overflow-hidden">
                            {/* Bill Header - Clickable */}
                            <div


                              className="p-3 cursor-pointer flex justify-between items-start"
                              onClick={() => handleBillToggle(bill.InvoiceNo, bill.Sequence)}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-semibold text-black">Bill No: {bill.InvoiceNo}</p>
                                  {bill.DoctorName && (
                                    <span className="text-xs text-gray-600">• {bill.DoctorName}</span>
                                  )}
                                </div>
                                <p className="text-xs text-black">Bill Date: {bill.EntryDate.split('T')[0]}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className="text-sm font-bold text-indigo-600">Grand Total : ₹{((bill.TotalAmount)).toFixed(2)}</span>
                                {isExpanded ? (
                                  <FiChevronUp className="text-gray-600" size={18} />
                                ) : (
                                  <FiChevronDown className="text-gray-600" size={18} />
                                )}
                              </div>
                            </div>

                            {/* Bill Items - Expandable */}
                            {isExpanded && (
                              <div className="border-t border-gray-300 bg-gray-50">
                                {isLoading ? (
                                  <div className="p-4 text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
                                    <p className="text-xs text-gray-600 mt-2">Loading items...</p>
                                  </div>
                                ) : items.length > 0 ? (
                                  <div className="p-3">
                                    {/* Load / Print - shown when expanded and items are loaded */}
                                    <div className="flex justify-end mb-2 gap-2">
                                      <button
                                        type="button"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          await loadProductsFromPatientHistory(items, bill.InvoiceNo);
                                          setShowPatientSidebar(false);
                                          setExpandedBillId(null);
                                        }}
                                        disabled={loadingPatientBillLoad === bill.InvoiceNo}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-medium rounded-md hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                        title="Load items into billing (qty 1, first batch)"
                                      >
                                        {loadingPatientBillLoad === bill.InvoiceNo ? (
                                          <>
                                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                                            <span>Loading...</span>
                                          </>
                                        ) : (
                                          <span>Load</span>
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePrintBillFromHistory(bill, items);
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                                        title="Print Bill"
                                      >
                                        <FiPrinter size={14} />
                                        <span>Print</span>
                                      </button>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs border-collapse">
                                        <thead className="bg-gray-200">
                                          <tr>
                                            <th className="px-2 py-1.5 text-left border border-gray-300 font-medium text-black">SI</th>
                                            <th className="px-2 py-1.5 text-left border border-gray-300 font-medium text-black">Item</th>
                                            <th className="px-2 py-1.5 text-left border border-gray-300 font-medium text-black">Batch</th>
                                            <th className="px-2 py-1.5 text-left border border-gray-300 font-medium text-black">Expiry</th>
                                            <th className="px-2 py-1.5 text-right border border-gray-300 font-medium text-black">Qty</th>
                                            <th className="px-2 py-1.5 text-right border border-gray-300 font-medium text-black">MRP</th>
                                            <th className="px-2 py-1.5 text-right border border-gray-300 font-medium text-black">Total</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white">
                                          {items.map((item, itemIndex) => (
                                            <tr key={itemIndex} className="border-b border-gray-200">
                                              <td className="px-2 py-1.5 border border-gray-200 text-black">{item.SI_No}</td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black font-medium">{item.ItemName}</td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black">{item.BatchCode}</td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black">
                                                {item.ExpiryDate ? (() => {
                                                  try {
                                                    // Handle both "2029-10-01" and ISO date strings
                                                    const dateStr = item.ExpiryDate.split('T')[0]; // Remove time if present
                                                    const [year, month] = dateStr.split('-');
                                                    return `${month}-${year}`;
                                                  } catch {
                                                    return item.ExpiryDate;
                                                  }
                                                })() : '-'}
                                              </td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black text-right">{item.Qty}</td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black text-right">₹{parseFloat(item.MRP).toFixed(2)}</td>
                                              <td className="px-2 py-1.5 border border-gray-200 text-black text-right font-semibold">₹{parseFloat(item.NetTotal).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 text-center">
                                    <p className="text-xs text-gray-600">No items found</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-black">No recent bills</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showBillPaymentModal && billPaymentSavedContext && (
        <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !billPaymentInvalid) {
                e.preventDefault();
                handleBillPaymentPost();
              }
            }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold flex items-center">
                    <TbCurrencyRupee className="mr-2" size={20} />
                    Mark Payment
                  </h2>
                  <p className="text-xs text-indigo-100 mt-1 truncate">
                    Invoice {formatPrintInvoiceNumber(billPaymentSavedContext.invoiceNo)}
                    {billPaymentSavedContext.patientName ? ` · ${billPaymentSavedContext.patientName}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => finalizeAfterBillPaymentModal()}
                  className="text-white/80 hover:text-white transition-colors p-1.5 hover:bg-white/15 rounded-lg shrink-0"
                  title="Skip payment"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Amount summary strip */}
            <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200 bg-gray-50">
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Bill Total</p>
                <p className="text-base font-bold text-gray-900 mt-0.5">₹{billOutstanding.toFixed(2)}</p>
              </div>
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Paying</p>
                <p className={`text-base font-bold mt-0.5 ${billSplitExceeds ? 'text-red-600' : 'text-indigo-600'}`}>
                  ₹{billPayingAmount.toFixed(2)}
                </p>
              </div>
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Balance</p>
                <p className={`text-base font-bold mt-0.5 ${Math.max(0, billOutstanding - billPayingAmount) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{Math.max(0, billOutstanding - billPayingAmount).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              {/* Payment method - segmented control */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Payment Method</label>
                <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-xl">
                  {['Cash', 'Bank', 'Split'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleBillPaymentModeChange(mode)}
                      className={`py-2 rounded-lg text-sm font-semibold transition-all ${
                        billPaymentModalMode === mode
                          ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-200'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {isSplitBillPayment ? (
                /* Split amounts - both legs ride on a single receipt transaction */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Cash</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                        <input
                          type="number"
                          min="0"
                          max={billOutstanding}
                          step="0.01"
                          value={billSplitCash}
                          onChange={(e) => updateBillSplitAmount('cash', e.target.value)}
                          className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base font-semibold bg-white text-right"
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Bank</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                        <input
                          type="number"
                          min="0"
                          max={billOutstanding}
                          step="0.01"
                          value={billSplitBank}
                          onChange={(e) => updateBillSplitAmount('bank', e.target.value)}
                          className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base font-semibold bg-white text-right"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {billSplitExceeds && (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 text-center font-medium">
                      Cash + Bank cannot exceed the bill total
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setBillPaymentModalSplit({ cash: Math.max(0, billOutstanding - billSplitBank), bank: billSplitBank })}
                    className="w-full px-3 py-2 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors font-semibold"
                  >
                    Balance rest to Cash
                  </button>
                </div>
              ) : (
                /* Single-mode paying amount */
                <div className="space-y-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Paying Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl">₹</span>
                    <input
                      type="number"
                      min="0"
                      max={billOutstanding}
                      step="0.01"
                      value={billPaymentModalAmount}
                      onChange={(e) => {
                        const value = Math.min(Math.max(toSafeNumber(e.target.value), 0), billOutstanding);
                        setBillPaymentModalAmount(value);
                      }}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-2xl font-bold bg-white text-right"
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setBillPaymentModalAmount(billOutstanding)}
                      className="flex-1 px-3 py-2 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors font-semibold"
                    >
                      Full
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillPaymentModalAmount(Math.round((billOutstanding / 2) * 100) / 100)}
                      className="flex-1 px-3 py-2 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-semibold"
                    >
                      Half
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillPaymentModalAmount(0)}
                      className="flex-1 px-3 py-2 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => finalizeAfterBillPaymentModal()}
                className="px-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleBillPaymentPost}
                disabled={billPaymentInvalid}
                className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                <FiCheck size={16} />
                Process ₹{billPayingAmount.toFixed(2)}
              </button>
            </div>
          </div>
        </div>
      )}
      {generalsettings?.phBillTemplate === 'A5' ? (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <NormalBillPrint
            ref={normalBillPrintRef}
            invoiceNumber={formatPrintInvoiceNumber(invoiceNumber)}
            selectedPatient={{
              PatientID: selectedPatient?.PatientID || 0,
              PatientName: editName || '',
              Age: editAge || '',
              Gender: editGender || '',
              MobileNo: editNumber || '',
            }}
            selectedDoctor={selectedDoctor}
            payingAmount={payingAmount}
            date={billedDate}
            products={products}
            calculateTotal={calculateTotal}
            calculateGrandTotal={calculateGrandTotal}
            calculateTotalDiscount={calculateTotalDiscount}
            calculateTotalGST={calculateTotalGST}
            grand_total={parseFloat(calculateGrandTotal())}
            balance_due={parseFloat((calculateGrandTotal() - (parseFloat(payingAmount) || 0))).toFixed(2)}
            generalSetting={generalsettings}
            sysInfo={sysInfo}
            username={userInfo?.UserName}
            setTotalPages={setTotalPages}
            currentPage={currentPage}
          />
        </div>
      ) : (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <KotBillPrint
            invoiceNumber={formatPrintInvoiceNumber(invoiceNumber)}
            selectedPatient={{
              PatientID: selectedPatient?.PatientID || 0,
              PatientName: editName || '',
              Age: editAge || '',
              Gender: editGender || '',
              MobileNo: editNumber || '',
            }}
            selectedDoctor={selectedDoctor}
            payingAmount={payingAmount}
            date={billedDate}
            products={products}
            calculateTotal={calculateTotal}
            calculateGrandTotal={calculateGrandTotal}
            calculateTotalDiscount={calculateTotalDiscount}
            calculateTotalGST={calculateTotalGST}
            grand_total={parseFloat(calculateGrandTotal())}
            balance_due={parseFloat((calculateGrandTotal() - (parseFloat(payingAmount) || 0))).toFixed(2)}
            generalSetting={generalsettings}
            sysInfo={sysInfo}
            username={userInfo?.UserName}
            setTotalPages={setTotalPages}
            currentPage={currentPage}
          />
        </div>
      )}
    </div>
  );
};

export default PharmacyBilling;